// src/services/works/quotationService.js

import store from '@/store';
import apiService from '../apiService';
import workflowService from '../workflowService';
import PracticalSchemaValidator from '../validation/schemaValidator';
import { logServiceError, logAIRequestError, ERROR_TYPES } from './errorLoggingService';
import { buildUnitMeta, unitHint, normalizeUnitValue, requiredUnexposedNames, placeExtraComponent, materialTermsPrompt, unexposedSourceRule, guessedNames } from './attributeUnitRules';
import { splitProductName, applySkuSize, optionSourceLines, bannedWordsRule, dropUnsupportedAttributeValues, emptyMandatoryAttributes, mandatoryUnexposedError } from './seoSpecGuard';
import { alignExposedToSource, resolveDuplicateExposedValues, buildMinimalSplitGroups, placeUnlistedOptions, assertSplitCoverage } from './splitOptionGroups';
import { keepUserEdits, restoreUserEdits } from './userEditGuard';
import { packageEstimateGuide, packageImageUrl, isImageRejectedError, PACKAGE_FORM_FIELD, normalizeBoxDimension, normalizeBoxWeight, packageFrom1688, floorBoxDimension, fillDocLogisticsPackage, shippingInfoForAi } from './packageEstimate';
import { firstBrandName, quotationManufacturer } from './quotationManufacturer';
import { isColorAttr, needsAttributeImage, colorSourceGuide, COLOR_SOURCE_FIELD } from './attributeColor';
// 묶음 판정 = 옵션 잣대 하나(상품 플래그 hasBundling 0 · PR261021-DB0DBE)
import bundleOptionService from './bundleOptionService';

// 소싱에서 남긴 옵션(sourcingSkuIds)을 나눈 조각의 옵션(id = 1688 skuId)만큼으로 좁힌다.
// 통째 복사로 두면 그 조각을 «초기화» 할 때 남의 조각 옵션까지 다시 만든다(B2692B 곁 H3 · 4 → 12).
// 하나도 안 맞으면(옵션에 skuId 없음) 지우지 않고 그대로 둔다 — 비우면 초기화가 «남길 옵션 없음» 으로 막힌다.
// 그대로 둠 길은 서버 로그로도 남긴다(셀러 콘솔만이면 몇 번 나는지 못 셈 · 회사는 서버가 로그인에서 붙임 · skuidslog-1)
function sourcingSkuIdsFor(ids, options, registrationId) {
  const own = new Set((options || []).map(o => o && o.id).filter(v => v !== undefined && v !== null).map(String));
  const kept = ids.map(String).filter(id => own.has(id));
  if (!kept.length) {
    const line = `나눔: sourcingSkuIds ${ids.length}개 중 조각 옵션과 맞는 것 0 — 그대로 둠`;
    console.error(`[quotation] ${registrationId} ${line}`);
    logServiceError('quotationService', 'sourcingSkuIdsFor', line, {
      errorType: ERROR_TYPES.PROCESSING_ERROR,
      productId: registrationId,
      additional: { sourcingSkuIds: ids.length, options: (options || []).length }
    });
  }
  return kept.length ? kept : ids;
}

/**
 * 등록 세부정보(견적서) 작업을 위한 서비스
 */
const quotationService = {
  /**
   * 견적서 작업 후 상품 및 coupangDoc 업데이트 (부분 업데이트 방식)
   * @param {Object} product - 상품 객체
   * @param {Object} quotationData - 견적서 작업 결과 데이터
   * @param {boolean} applyToAllOptions - 모든 옵션에 적용할지 여부
   * @returns {Promise<boolean>} 업데이트 성공 여부
   */
  async updateProductWithCoupangDocPartial(product, quotationData, applyToAllOptions = true) {
    try {
      if (!product || !product._id) {
        throw new Error('유효하지 않은 상품 정보입니다');
      }

      console.log('📊 updateProductWithCoupangDocPartial 시작:', {
        productId: product._id,
        hasCoupangDoc: !!product.coupangDoc,
        jsonDocumentLength: product.coupangDoc?.jsonDocument?.length || 0,
        applyToAllOptions
      });

      // coupangDoc 구조 로깅 (중요 필드만)
      if (product.coupangDoc) {
        console.log('📄 coupangDoc 구조 확인:', {
          version: product.coupangDoc.version,
          scope: product.coupangDoc.scope,
          hasJsonDocument: !!product.coupangDoc.jsonDocument,
          jsonDocumentLength: product.coupangDoc.jsonDocument?.length || 0
        });

        // jsonDocument 첫 번째 항목의 구조 샘플 로깅 (있는 경우)
        if (product.coupangDoc.jsonDocument && product.coupangDoc.jsonDocument.length > 0) {
          const firstDoc = product.coupangDoc.jsonDocument[0];
          console.log('📑 jsonDocument[0] 구조 샘플:', {
            hasStartPage: !!firstDoc.startPage,
            hasProductPage: !!firstDoc.productPage,
            hasLogisticsPage: !!firstDoc.logisticsPage,
            hasLegalPage: !!firstDoc.legalPage
          });
        }
      } else {
        console.warn('⚠️ product.coupangDoc이 없습니다!');
      }

      // 점 표기법을 사용하여 업데이트할 필드 지정
      const updateFields = {
        'quotation': quotationData,
        'quotationStatus': 'completed',
        'updatedAt': Date.now()
      };

      // coupangDoc 특정 필드들을 점 표기법으로 업데이트
      if (product.coupangDoc && product.coupangDoc.jsonDocument) {
        console.log('📊 coupangDoc 점 표기법 업데이트 시작');

        // 모든 jsonDocument 항목에 견적서 정보 적용
        product.coupangDoc.jsonDocument.forEach((_, docIndex) => {
          this.addCoupangDocFieldsToUpdateFields(updateFields, docIndex, quotationData);
        });

        console.log('✅ coupangDoc 점 표기법 필드 추가 완료');
      }

      // 옵션 정보 업데이트
      if (product.options && product.options.length > 0) {
        product.options.forEach((option, optionIndex) => {
          if (option.optionNumber) {
            // 점 표기법으로 특정 옵션의 특정 필드만 업데이트
            updateFields[`options.${optionIndex}.quotationStatus`] = 'completed';
          }
        });
      }

      // 서버에 부분 업데이트 요청
      await apiService.updateRocketProductPartial(product._id, updateFields);

      // store 상태 부분 업데이트
      store.commit('updateProductPartial', {
        productId: product._id,
        updateFields: updateFields
      });

      return true;
    } catch (error) {
      console.error('견적서 정보로 상품 업데이트 중 오류:', error);
      throw error;
    }
  },

  /**
   * 견적서 작업 후 상품 및 coupangDoc 업데이트 (이전 방식 - 호환성 유지)
   * @param {Object} product - 상품 객체
   * @param {Object} quotationData - 견적서 작업 결과 데이터
   * @param {boolean} applyToAllOptions - 모든 옵션에 적용할지 여부
   * @returns {Promise<Object>} 업데이트된 상품 객체
   * @deprecated 새로운 updateProductWithCoupangDocPartial 메서드를 사용하세요
   */
  async updateProductWithCoupangDoc(product, quotationData, applyToAllOptions = true) {
    console.warn('updateProductWithCoupangDoc는 더 이상 사용되지 않습니다. updateProductWithCoupangDocPartial을 사용하세요.');
    return this.updateProductWithCoupangDocPartial(product, quotationData, applyToAllOptions);
  },

  /**
   * coupangDoc 필드들을 점 표기법으로 updateFields에 추가
   * @param {Object} updateFields - 업데이트할 필드들 객체
   * @param {number} docIndex - jsonDocument 배열 인덱스
   * @param {Object} quotationData - 견적서 데이터
   */
  addCoupangDocFieldsToUpdateFields(updateFields, docIndex, quotationData) {
    try {
      // productPage 필드들
      if (quotationData.brand) {
        updateFields[`coupangDoc.jsonDocument.${docIndex}.productPage.brand`] = quotationData.brand;
      }
      if (quotationData.businessType) {
        updateFields[`coupangDoc.jsonDocument.${docIndex}.productPage.businessType`] = quotationData.businessType;
      }
      if (quotationData.importType) {
        updateFields[`coupangDoc.jsonDocument.${docIndex}.productPage.importType`] = quotationData.importType;
      }
      if (quotationData.manufacturer) {
        updateFields[`coupangDoc.jsonDocument.${docIndex}.productPage.manufacturer`] = quotationData.manufacturer;
      }
      if (quotationData.taxationType) {
        updateFields[`coupangDoc.jsonDocument.${docIndex}.productPage.taxationSchema`] = quotationData.taxationType;
      }

      // commonAttributes 필드들
      if (quotationData.purchasePrice) {
        updateFields[`coupangDoc.jsonDocument.${docIndex}.productPage.commonAttributes.purchasePrice`] = quotationData.purchasePrice;
      }
      if (quotationData.salePrice) {
        updateFields[`coupangDoc.jsonDocument.${docIndex}.productPage.commonAttributes.coupangSalePrice`] = quotationData.salePrice;
      }
      if (quotationData.osrp) {
        updateFields[`coupangDoc.jsonDocument.${docIndex}.productPage.commonAttributes.osrp`] = quotationData.osrp;
      } else if (quotationData.msrp) {
        updateFields[`coupangDoc.jsonDocument.${docIndex}.productPage.commonAttributes.msrp`] = quotationData.msrp;
      }
      if (quotationData.barcode && quotationData.barcode !== '바코드 없음(쿠팡 바코드 생성 요청)') {
        updateFields[`coupangDoc.jsonDocument.${docIndex}.productPage.commonAttributes.productBarcode`] = quotationData.barcode;
      }
      if (quotationData.exposedAttributes && quotationData.exposedAttributes.length > 0) {
        updateFields[`coupangDoc.jsonDocument.${docIndex}.productPage.commonAttributes.exposedAttributes`] = quotationData.exposedAttributes;
      }
      if (quotationData.unexposedAttributes && quotationData.unexposedAttributes.length > 0) {
        updateFields[`coupangDoc.jsonDocument.${docIndex}.productPage.unexposedAttributes`] = quotationData.unexposedAttributes;
      }

      // logisticsPage 필드들
      if (quotationData.skuUnitBoxDimension) {
        updateFields[`coupangDoc.jsonDocument.${docIndex}.logisticsPage.skuUnitBoxDimension`] = quotationData.skuUnitBoxDimension;
      }
      if (quotationData.skuUnitBoxWeight) {
        updateFields[`coupangDoc.jsonDocument.${docIndex}.logisticsPage.skuUnitBoxWeight`] = quotationData.skuUnitBoxWeight;
      }
      // totalSKUsInBox 처리 - 0이거나 없으면 1로 설정
      const totalSkusInBox = quotationData.totalSkusInBox || 1;
      updateFields[`coupangDoc.jsonDocument.${docIndex}.logisticsPage.totalSKUsInBox`] = totalSkusInBox;
      if (quotationData.totalSkusInBox === 0 || !quotationData.totalSkusInBox) {
        console.log(`🔧 totalSKUsInBox 자동 수정: ${quotationData.totalSkusInBox} → ${totalSkusInBox}`);
      }
      if (quotationData.daysToExpiration !== undefined) {
        updateFields[`coupangDoc.jsonDocument.${docIndex}.logisticsPage.daysToExpiration`] = quotationData.daysToExpiration;
      }
      if (quotationData.specialHandlingReason) {
        updateFields[`coupangDoc.jsonDocument.${docIndex}.logisticsPage.specialHandlingReason`] = quotationData.specialHandlingReason;
      }
      if (quotationData.fashionSeason) {
        updateFields[`coupangDoc.jsonDocument.${docIndex}.logisticsPage.fashionSeason`] = '사계절';
      }

      // legalPage 필드들
      if (quotationData.kcMarkType) {
        updateFields[`coupangDoc.jsonDocument.${docIndex}.legalPage.kcMarkType`] = quotationData.kcMarkType;
      }
      // certificates는 개별 필드 업데이트로 처리하지 않음 (applyAttributesToOption에서 처리)
      // 전체 배열을 덮어쓰면 기존 "해당사항없음" 값이 null로 변경될 수 있음
      if (quotationData.certificates && quotationData.certificates.length > 0) {
        console.log(`⚠️ certificates 점 표기법 업데이트 건너뜀 (applyAttributesToOption에서 처리됨)`);
        updateFields[`coupangDoc.jsonDocument.${docIndex}.legalPage.certificates`] = quotationData.certificates.map(cert => ({
          certificateName: cert.name,
          certificateValue: '해당사항없음'
        }));
      }
      if (quotationData.productNotices && quotationData.productNotices.length > 0) {
        updateFields[`coupangDoc.jsonDocument.${docIndex}.legalPage.notices`] = quotationData.productNotices.map(notice => ({
          noticeCategoryName: notice.category,
          noticeCategoryDetailName: notice.detail,
          content: notice.content
        }));
      }

      console.log(`✅ docIndex ${docIndex}의 coupangDoc 필드들을 점 표기법으로 추가 완료`);
    } catch (error) {
      console.error(`docIndex ${docIndex}의 coupangDoc 필드 추가 중 오류:`, error);
    }
  },

  /**
   * 쉼표로 구분된 브랜드명에서 첫 번째 브랜드만 추출
   * @param {string} brandName - 브랜드명 문자열
   * @returns {string} - 첫 번째 브랜드명
   */
  getFirstBrandName(brandName) {
    if (brandName && brandName.includes(',')) {
      const firstBrand = brandName.split(',')[0].trim();
      console.log('쉼표로 구분된 브랜드명에서 첫 번째 값만 사용:', firstBrand);
      return firstBrand;
    }
    return brandName;
  },

  /**
   * 노출·비노출 속성 중 NUMBER 속성값을 쿠팡 허용 단위로 맞춘다(attributeUnitRules).
   * 확정 규칙으로 못 맞추는 값이 있으면 사유를 담아 throw — 추정해 채우지 않는다.
   * @param {Object} product - 상품 객체 (rocketRegInfo.schemaString 사용)
   * @param {Object} attributeData - 속성 데이터
   * @returns {Object} 단위를 맞춘 속성 데이터(새 객체)
   */
  enforceAttributeUnits(product, attributeData) {
    const unitMeta = buildUnitMeta(product.rocketRegInfo?.schemaString);
    if (!Object.keys(unitMeta).length) return attributeData;
    const defaultSettings = (store.state.settings[0] || {}).defaultSettings || {};
    const checkUnexposed = defaultSettings.enableUnexposedAttributes !== false; // 비노출 적용을 건너뛰면 검사도 건너뛴다(필수 비노출은 적용하므로 검사한다)
    const requiredUnexposed = checkUnexposed ? null : requiredUnexposedNames(product.rocketRegInfo?.schemaString);
    const problems = [];
    const notes = []; // 비운 값의 사유 — 결과(unitNotes)에 남긴다
    const extras = []; // 수량에서 뗀 «+ 딸린 물건» — 구성품으로 옮긴다
    const fix = (list) => (list || []).map(attr => {
      const m = unitMeta[attr.attributeName];
      if (!m) return attr;
      const raw = attr.attributeValue || attr.value;
      const r = normalizeUnitValue(raw, m);
      if (!r.ok) {
        problems.push(`${attr.attributeName}: ${r.reason}`);
        return attr;
      }
      if (!r.changed) return attr;
      if (r.extra) extras.push(r.extra);
      if (r.reason) notes.push(`${attr.attributeName}: ${r.reason}`);
      console.warn(`[단위 정규화] ${attr.attributeName}: "${raw}" → ${r.value === null ? '빈 값' : `"${r.value}"`}${r.reason ? ` (${r.reason})` : ''}`);
      const next = { ...attr, attributeValue: r.value };
      if ('value' in attr) next.value = r.value;
      return next;
    });
    let result = {
      ...attributeData,
      exposedAttributes: fix(attributeData.exposedAttributes),
      unexposedAttributes: checkUnexposed
        ? fix(attributeData.unexposedAttributes)
        : (attributeData.unexposedAttributes || []).map(attr => (requiredUnexposed.has(attr.attributeName) ? fix([attr])[0] : attr))
    };
    if (extras.length) {
      const placed = placeExtraComponent(result, extras, product.rocketRegInfo?.schemaString, { canWriteUnexposed: checkUnexposed });
      result = placed.data;
      if (placed.warning) {
        console.warn(`[단위 정규화] ${placed.warning}`);
        notes.push(placed.warning);
      } else {
        console.warn(`[단위 정규화] 수량에서 뗀 "${extras.join(' + ')}" → 구성품`);
      }
    }
    if (notes.length) result.unitNotes = notes;
    if (problems.length) {
      throw new Error(`속성 단위 오류(쿠팡이 받지 않는 단위): ${problems.join(' / ')}`);
    }
    return result;
  },

  /**
   * 특정 옵션에 속성 적용 (기존 필드에 값만 배치)
   * AIRocketReg에서 이미 coupangDoc 필드를 만들어놨으므로 해당 항목에 맞는 값만 찾아서 배치
   * @param {Object} product - 상품 객체
   * @param {number} optionIndex - 옵션 인덱스
   * @param {Object} attributeData - 속성 데이터 (exposedAttributes, unexposedAttributes 등)
   * @returns {Promise<boolean>} 성공 여부
   */
  async applyAttributesToOption(product, optionIndex, attributeData) {
    try {
      console.log(`옵션 인덱스 ${optionIndex}에 속성 적용 시작 (기존 필드에 값만 배치)`);
      console.log(`속성 데이터:`, JSON.stringify(attributeData, null, 2).substring(0, 500) + '...');

      // coupangDoc 존재 여부 확인
      if (!this.checkCoupangDocExists(product)) {
        console.error('❌ coupangDoc이 없습니다. AIRocketReg에서 먼저 상품을 추가해야 합니다.');
        throw new Error('coupangDoc이 없습니다. AIRocketReg에서 먼저 상품을 추가해야 합니다.');
      }

      // 해당 옵션의 jsonDocument 항목이 있는지 확인
      if (optionIndex >= product.coupangDoc.jsonDocument.length) {
        console.error(`❌ 옵션 인덱스 ${optionIndex}에 해당하는 jsonDocument 항목이 없습니다.`);
        throw new Error(`옵션 인덱스 ${optionIndex}에 해당하는 jsonDocument 항목이 없습니다.`);
      }

      // 해당 옵션의 jsonDocument 항목 가져오기
      const docItem = product.coupangDoc.jsonDocument[optionIndex];
      console.log(`옵션 인덱스 ${optionIndex}의 기존 jsonDocument 항목에 값 배치 시작`);

      // 저장 전 단위 검사 — 쿠팡 검증 2단계가 거절하는 단위는 확정 규칙으로만 맞추고, 못 맞추면 사유와 함께 멈춘다
      attributeData = this.enforceAttributeUnits(product, attributeData);
      // 상품 특징의 금지 표현·원문 소재와 다른 소재(F84B02 «앙고라» · 4DBBFE 재질=금속인데 «가죽»)는 속성 값에서 뺀다 — 공통·차별화·폴백 모두 여기를 지난다
      const factSrc = { productFeature: product.productFeature || '', attributes: product.attributes, options: product.options };
      const exFact = dropUnsupportedAttributeValues(attributeData.exposedAttributes, factSrc);
      const unFact = dropUnsupportedAttributeValues(attributeData.unexposedAttributes, factSrc);
      [...exFact.removed, ...unFact.removed].forEach(r => console.warn(`⚠️ 옵션 ${optionIndex} ${r.attributeName}: 근거 없는·금지 표현 «${r.removed.join('·')}» 뺌 ("${r.from}" → ${r.to === null ? '빈 값' : `"${r.to}"`})`));
      attributeData = { ...attributeData, exposedAttributes: exFact.attributes, unexposedAttributes: unFact.attributes };

      // 노출 속성 적용 (기존 필드에 값만 배치)
      if (attributeData.exposedAttributes && attributeData.exposedAttributes.length > 0) {
        if (docItem.productPage?.commonAttributes?.exposedAttributes) {
          console.log(`🔄 기존 노출 속성 ${docItem.productPage.commonAttributes.exposedAttributes.length}개에 값 배치`);

          // 기존 필드에 해당하는 값만 찾아서 배치
          docItem.productPage.commonAttributes.exposedAttributes.forEach(existingAttr => {
            const newAttr = attributeData.exposedAttributes.find(attr =>
              attr.attributeName === existingAttr.attributeName
            );

            if (newAttr) {
              // 새 값 추출 (다양한 필드명 지원: attributeValue 또는 value)
              const newValue = newAttr.attributeValue || newAttr.value;

              // 중국어 문자 감지 및 처리
              if (this.containsChineseCharacters(newValue)) {
                console.warn(`중국어 문자 감지: ${newAttr.attributeName} = ${newValue}`);
                const safeValue = "상세설명 참조";

                // 기존 필드 형식에 맞춰 값 설정 (attributeValue 또는 value)
                if ('attributeValue' in existingAttr) {
                  existingAttr.attributeValue = safeValue;
                } else if ('value' in existingAttr) {
                  existingAttr.value = safeValue;
                }
              } else {
                // 기존 필드 형식에 맞춰 값 설정 (attributeValue 또는 value)
                if ('attributeValue' in existingAttr) {
                  existingAttr.attributeValue = newValue;
                } else if ('value' in existingAttr) {
                  existingAttr.value = newValue;
                }
              }
              console.log(`✅ ${existingAttr.attributeName}: ${newValue}`);
            } else {
              console.log(`⚠️ ${existingAttr.attributeName}: 새 값 없음 (기존값 유지)`);
            }
          });
          // 사람이 견적서 화면에서 고친 값은 AI 가 다시 쓰지 않는다(보고 3320E1 · quotationUserEdits)
          const option = (product.options || [])[optionIndex];
          restoreUserEdits(docItem.productPage.commonAttributes.exposedAttributes, option && option.optionNumber, product.quotationUserEdits)
            .forEach(b => console.warn(`⚠️ 옵션 ${option.optionNumber} ${b.attributeName}: 사람이 고친 값 "${b.to}" 유지(AI 값 "${b.from}" 버림)`));
        } else {
          console.warn('⚠️ 기존 노출 속성 필드가 없습니다.');
        }
      }

      // 비노출 속성 적용 (기존 필드에 값만 배치) - 설정 확인
      const settings = store.state.settings[0] || {};
      const defaultSettings = settings.defaultSettings || {};
      const enableUnexposedAttributes = defaultSettings.enableUnexposedAttributes !== false; // 기본값 true
      // 꺼져 있어도 «필수» 비노출 속성은 적용한다 — 설정은 «선택» 비노출 속성을 채울지만 정한다(쿠팡이 필수로 요구)
      const requiredUnexposed = enableUnexposedAttributes ? null : requiredUnexposedNames(product.rocketRegInfo?.schemaString);

      if (attributeData.unexposedAttributes && attributeData.unexposedAttributes.length > 0) {
        if (!enableUnexposedAttributes && requiredUnexposed.size === 0) {
          console.log('ℹ️ 비노출속성 자동 생성이 비활성화되어 있어 비노출속성 값 적용을 건너뜁니다.');
        } else if (docItem.productPage?.unexposedAttributes) {
          console.log(`🔄 기존 비노출 속성 ${docItem.productPage.unexposedAttributes.length}개에 값 배치${requiredUnexposed ? ` (자동 생성 꺼짐 — 필수 ${requiredUnexposed.size}개만)` : ''}`);

          // 기존 필드에 해당하는 값만 찾아서 배치
          docItem.productPage.unexposedAttributes.forEach(existingAttr => {
            if (requiredUnexposed && !requiredUnexposed.has(existingAttr.attributeName)) return;
            const newAttr = attributeData.unexposedAttributes.find(attr =>
              attr.attributeName === existingAttr.attributeName
            );

            if (newAttr) {
              // 새 값 추출 (다양한 필드명 지원: attributeValue 또는 value)
              const newValue = newAttr.attributeValue || newAttr.value;

              // 중국어 문자 감지 및 처리
              if (this.containsChineseCharacters(newValue)) {
                console.warn(`중국어 문자 감지: ${newAttr.attributeName} = ${newValue}`);

                // 기존 필드 형식에 맞춰 값 설정 (attributeValue 또는 value)
                if ('attributeValue' in existingAttr) {
                  existingAttr.attributeValue = null;
                } else if ('value' in existingAttr) {
                  existingAttr.value = null;
                }
              } else {
                // 기존 필드 형식에 맞춰 값 설정 (attributeValue 또는 value)
                if ('attributeValue' in existingAttr) {
                  existingAttr.attributeValue = newValue;
                } else if ('value' in existingAttr) {
                  existingAttr.value = newValue;
                }
              }
              console.log(`✅ ${existingAttr.attributeName}: ${newValue}`);
            } else {
              console.log(`⚠️ ${existingAttr.attributeName}: 새 값 없음 (기존값 유지)`);
            }
          });
        } else {
          console.warn('⚠️ 기존 비노출 속성 필드가 없습니다.');
        }
      }
      // 필수 비노출이 비면(한자 → null · 새 값 없음 · 차별화 실패) 빈 채로 두지 않고 이름을 적어 멈춘다(CB70ED — 늦은 «최종 검증 실패 … 누락» 대신)
      const mustUnexposed = [...requiredUnexposedNames(product.rocketRegInfo?.schemaString)].map(name => ({ name, mandatory: true }));
      const emptyUnexposed = docItem.productPage?.unexposedAttributes ? emptyMandatoryAttributes(docItem.productPage.unexposedAttributes, mustUnexposed) : [];
      if (emptyUnexposed.length) throw mandatoryUnexposedError(emptyUnexposed, { schemaAttrs: product.rocketRegInfo?.schemaInfo?.unexposedAttributes, src: factSrc });

      // 물류 정보 적용
      // 포장값은 이미 저장된 값(사람이 견적서 화면에서 넣은 값)을 덮지 않는다. 비었거나 옛 기본값(100*100*100 / 100·1000)일 때만
      // 새 값을 넣는다. 새 값·빈 값 모두 포장 하한(사장님 #260 — 세 변 모두 100mm 이하면 100*100*100mm · 100g 미만이면 100g)을 적용한다
      // (packageEstimate.fillLogisticsPackage). 빈 채로 두면 쿠팡에 null 이 가서 거절됐다(702502). 칸·logisticsPage 가 없어도 채운다(PR261018-4D1FE4).
      fillDocLogisticsPackage(docItem, attributeData).forEach(line => console.log(line));

      // 고시 정보 적용 (기존 필드에 값만 배치)
      if (attributeData.notices && attributeData.notices.length > 0) {
        if (docItem.legalPage?.notices) {
          console.log(`🔄 기존 고시 정보 ${docItem.legalPage.notices.length}개에 값 배치`);

          // 기존 필드에 해당하는 값만 찾아서 배치
          docItem.legalPage.notices.forEach(existingNotice => {
            // 기존 필드의 키 확인 (다양한 형식 지원)
            const existingKey = existingNotice.noticeItemName ||
                               existingNotice.noticeCategoryName ||
                               existingNotice.attributeName;

            const newNotice = attributeData.notices.find(notice => {
              const newKey = notice.noticeItemName || notice.noticeCategoryName || notice.attributeName;
              return newKey === existingKey;
            });

            if (newNotice) {
              // 새 값 추출 (다양한 형식 지원)
              const newValue = newNotice.noticeItemValue || newNotice.content || newNotice.attributeValue;

              // 중국어 문자 감지 및 처리
              if (this.containsChineseCharacters(newValue)) {
                console.warn(`중국어 문자 감지: ${existingKey} = ${newValue}`);
                const safeValue = "상세설명 참조";

                // 기존 필드 형식에 맞춰 값 설정
                if ('noticeItemValue' in existingNotice) {
                  existingNotice.noticeItemValue = safeValue;
                } else if ('content' in existingNotice) {
                  existingNotice.content = safeValue;
                } else if ('attributeValue' in existingNotice) {
                  existingNotice.attributeValue = safeValue;
                }
              } else {
                // 기존 필드 형식에 맞춰 값 설정
                if ('noticeItemValue' in existingNotice) {
                  existingNotice.noticeItemValue = newValue;
                } else if ('content' in existingNotice) {
                  existingNotice.content = newValue;
                } else if ('attributeValue' in existingNotice) {
                  existingNotice.attributeValue = newValue;
                }
              }
              console.log(`✅ ${existingKey}: ${newValue}`);
            } else {
              console.log(`⚠️ ${existingKey}: 새 값 없음 (기존값 유지)`);
            }
          });
        } else {
          console.warn('⚠️ 기존 고시 정보 필드가 없습니다.');
        }
      }

      // 🔥 인증 정보 적용 (기존 필드에 값만 배치) - 누락된 부분 추가
      if (attributeData.certificates && attributeData.certificates.length > 0) {
        if (docItem.legalPage?.certificates) {
          console.log(`🔄 기존 인증 정보 ${docItem.legalPage.certificates.length}개에 값 배치`);

          // 기존 필드에 해당하는 값만 찾아서 배치
          docItem.legalPage.certificates.forEach(existingCert => {
            // 기존 필드의 키 확인 (다양한 형식 지원)
            const existingKey = existingCert.certificateName || existingCert.name;

            const newCert = attributeData.certificates.find(cert => {
              const newKey = cert.certificateName || cert.name;
              return newKey === existingKey;
            });

            if (newCert) {
              // 새 값 추출 (다양한 형식 지원)
              const newValue = newCert.certificateValue || newCert.value;

              // null이나 빈 값인 경우 기존값 유지 (중요!)
              if (newValue === null || newValue === undefined || newValue === '') {
                console.log(`⚠️ ${existingKey}: AI가 생성한 값이 비어있음, 기존값 유지`);
                return; // 기존값 유지
              }

              // 중국어 문자 감지 및 처리
              if (this.containsChineseCharacters(newValue)) {
                console.warn(`중국어 문자 감지: ${existingKey} = ${newValue}`);
                const safeValue = "해당사항없음"; // certificates는 "해당사항없음" 사용

                // 기존 필드 형식에 맞춰 값 설정
                if ('certificateValue' in existingCert) {
                  existingCert.certificateValue = safeValue;
                } else if ('value' in existingCert) {
                  existingCert.value = safeValue;
                }
              } else {
                // 기존 필드 형식에 맞춰 값 설정
                if ('certificateValue' in existingCert) {
                  existingCert.certificateValue = newValue;
                } else if ('value' in existingCert) {
                  existingCert.value = newValue;
                }
              }
              console.log(`✅ ${existingKey}: ${newValue}`);
            } else {
              console.log(`⚠️ ${existingKey}: 새 값 없음 (기존값 유지)`);
            }
          });
        } else {
          console.warn('⚠️ 기존 인증 정보 필드가 없습니다.');
        }
      }

      // 옵션 정보 설정 (startPage.productName)
      if (product.options && product.options[optionIndex]) {
        // const option = product.options[optionIndex];
        let productName = '';

        // 상품명 결정 로직 로깅
        console.log('🔍 상품명 결정 로직 시작:', {
          hasSeo: !!product.seo,
          seoProductName: product.seo?.productName,
          hasProductName: !!product.productName,
          productName: product.productName,
          hasCoupangDocProductName: !!product.coupangDoc?.productName,
          coupangDocProductName: product.coupangDoc?.productName,
          optionBundleQuantity: bundleOptionService.quotationBundleQuantity(product, product.options[optionIndex])
        });

        // 우선순위: coupangDoc.productName > SEO 상품명 > 기본 상품명
        if (product.coupangDoc && product.coupangDoc.productName) {
          productName = product.coupangDoc.productName;
          console.log('✅ coupangDoc.productName 사용:', productName);
        } else if (product.seo && product.seo.productName) {
          productName = product.seo.productName;
          console.log('✅ SEO 상품명 사용:', productName);
        } else if (product.productName) {
          productName = product.productName;
          console.log('✅ 기본 상품명 사용:', productName);
        } else {
          productName = '상품명';
          console.log('⚠️ 기본값 사용:', productName);
        }

        // 이 옵션이 묶음이면 그 옵션 묶음 수만 상품명에 추가(묶음 아닌 옵션엔 안 붙음 · 이미 적혀 있으면 그대로)
        const bundledName = bundleOptionService.quotationOptionProductName(product, product.options[optionIndex], productName);
        if (bundledName !== productName) {
          productName = bundledName;
          console.log('✅ 번들 정보 추가:', productName);
        }

        // 옵션명 추가
        // if (option.korName) {
        //   productName = `${productName}, ${option.korName}`;
        // }

        // 상품명 길이 제한 (50자)
        // if (productName.length > 50) {
        //   productName = productName.substring(0, 50);
        // }

        // startPage.productName 설정
        if (!docItem.startPage) {
          docItem.startPage = {};
        }
        const oldStartPageProductName = docItem.startPage.productName;
        docItem.startPage.productName = productName;
        console.log(`🔄 startPage.productName업데이트: "${oldStartPageProductName}" > "${productName}"`);

        // productPage.modelNumber 설정 (상품명과 동일하게 설정)
        if (!docItem.productPage) {
          docItem.productPage = {};
        }
        const oldModelNumber = docItem.productPage.modelNumber;
        docItem.productPage.modelNumber = productName;
        console.log(`🔄 modelNumber업데이트: "${oldModelNumber}" > "${productName}"`);
      }

      // 적용 후 데이터 검증
      this.validateCoupangDocItem(docItem);

      console.log(`옵션 인덱스 ${optionIndex}에 속성 적용 완료`);
      return true;
    } catch (error) {
      console.error(`옵션 인덱스 ${optionIndex}에 속성 적용 중 오류:`, error);
      throw error;
    }
  },

  /**
   * 문자열에 중국어 문자가 포함되어 있는지 확인
   * @param {string} str - 검사할 문자열
   * @returns {boolean} 중국어 문자 포함 여부
   */
  containsChineseCharacters(str) {
    if (!str || typeof str !== 'string') return false;
    // 중국어 문자 범위: \u4e00-\u9fff
    return /[\u4e00-\u9fff]/.test(str);
  },

  /**
   * coupangDoc 항목 데이터 검증
   * @param {Object} docItem - 검증할 coupangDoc 항목
   */
  validateCoupangDocItem(docItem) {
    try {
      // 필수 필드 확인
      const requiredFields = [
        'startPage',
        'productPage',
        'logisticsPage',
        'legalPage'
      ];

      const missingFields = requiredFields.filter(field => !docItem[field]);
      if (missingFields.length > 0) {
        console.warn(`coupangDoc 항목에 필수 필드가 누락됨: ${missingFields.join(', ')}`);
      }

      // startPage 필드 확인
      if (docItem.startPage) {
        if (!docItem.startPage.productName) {
          console.warn('startPage.productName이 누락됨');
        }
      }

      // productPage 필드 확인
      if (docItem.productPage) {
        if (!docItem.productPage.modelNumber) {
          console.warn('productPage.modelNumber가 누락됨');
        }

        // commonAttributes 필드 확인
        if (docItem.productPage.commonAttributes) {
          if (!docItem.productPage.commonAttributes.exposedAttributes) {
            console.warn('productPage.commonAttributes.exposedAttributes가 누락됨');
          }
        } else {
          console.warn('productPage.commonAttributes가 누락됨');
        }

        // unexposedAttributes 필드 확인
        if (!docItem.productPage.unexposedAttributes) {
          console.warn('productPage.unexposedAttributes가 누락됨');
        }
      }

      // logisticsPage 필드 확인
      if (docItem.logisticsPage) {
        if (!docItem.logisticsPage.skuUnitBoxDimension) {
          console.warn('logisticsPage.skuUnitBoxDimension이 누락됨');
        }
        if (!docItem.logisticsPage.skuUnitBoxWeight) {
          console.warn('logisticsPage.skuUnitBoxWeight가 누락됨');
        }
      }

      // legalPage 필드 확인
      if (docItem.legalPage) {
        if (!docItem.legalPage.notices) {
          console.warn('legalPage.notices가 누락됨');
        } else if (Array.isArray(docItem.legalPage.notices)) {
          // notices 배열의 각 항목에 noticeItemValue가 있는지 확인
          docItem.legalPage.notices.forEach((notice, index) => {
            if (!notice.noticeItemName) {
              console.warn(`legalPage.notices[${index}].noticeItemName이 누락됨`);
            }
            if (!notice.noticeItemValue || notice.noticeItemValue.trim() === '') {
              console.warn(`legalPage.notices[${index}].noticeItemValue가 누락됨 (항목: ${notice.noticeItemName})`);
            }
          });
        }
      }
    } catch (error) {
      console.error('coupangDoc 항목 검증 중 오류:', error);
    }
  },

  /**
   * 빈 coupangDoc 객체 생성
   * @returns {Object} 빈 coupangDoc 객체
   */
  createEmptyCoupangDoc() {
    return {
      docId: `temp_${Date.now()}`,
      jsonDocument: [
        {
          startPage: {},
          productPage: {
            commonAttributes: {}
          },
          logisticsPage: {},
          legalPage: {}
        }
      ]
    };
  },

  /**
   * 상품에 유효한 coupangDoc이 존재하는지 확인
   * @param {Object} product - 상품 객체
   * @returns {boolean} coupangDoc 존재 여부
   */
  checkCoupangDocExists(product) {
    return (
      product &&
      product.coupangDoc &&
      product.coupangDoc.jsonDocument &&
      Array.isArray(product.coupangDoc.jsonDocument) &&
      product.coupangDoc.jsonDocument.length > 0
    );
  },

  /**
   * coupangDoc에서 견적서 데이터 추출
   * @param {Object} product - 상품 객체
   * @param {number} optionIndex - 옵션 인덱스 (선택적, 기본값: 0)
   * @returns {Object} 견적서 데이터
   */
  extractQuotationFromCoupangDoc(product, optionIndex = 0) {
    console.log(`extractQuotationFromCoupangDoc 시작 (옵션 인덱스: ${optionIndex})`);

    if (!this.checkCoupangDocExists(product)) {
      console.warn('유효한 coupangDoc이 없습니다. 기본 견적서 데이터를 생성합니다.');
      return this.createBaseQuotationData(product);
    }

    // coupangDoc 구조 로깅
    console.log('coupangDoc 구조:', {
      docId: product.coupangDoc.docId,
      jsonDocumentLength: product.coupangDoc.jsonDocument?.length || 0
    });

    try {
      // 기본 견적서 데이터 생성
      const baseQuotation = this.createBaseQuotationData(product);
      console.log('기본 견적서 데이터 생성 완료');

      // 첫 번째 jsonDocument 항목에서 기본 정보 추출
      const firstDocItem = product.coupangDoc.jsonDocument[0];

      // 상품 기본 정보 추출
      let productName = '';
      let modelNumber = '';
      let brand = '';
      let manufacturer = '';

      if (firstDocItem) {
        // startPage에서 상품명 추출
        if (firstDocItem.startPage && firstDocItem.startPage.productName) {
          productName = firstDocItem.startPage.productName;
          console.log(`추출된 상품명: ${productName}`);
        }

        // productPage에서 모델번호, 브랜드, 제조사 추출
        if (firstDocItem.productPage) {
          if (firstDocItem.productPage.modelNumber) {
            modelNumber = firstDocItem.productPage.modelNumber;
            console.log(`추출된 모델번호: ${modelNumber}`);
          }

          if (firstDocItem.productPage.brand) {
            brand = firstDocItem.productPage.brand;
            console.log(`추출된 브랜드: ${brand}`);
          }

          if (firstDocItem.productPage.manufacturer) {
            manufacturer = firstDocItem.productPage.manufacturer;
            console.log(`추출된 제조사: ${manufacturer}`);
          }
        }
      }

      // 가격 정보 추출
      let purchasePrice = 0;
      let salePrice = 0;
      let msrp = 0;
      let osrp = 0;
      let barcode = '바코드 없음(쿠팡 바코드 생성 요청)';

      if (firstDocItem && firstDocItem.productPage && firstDocItem.productPage.commonAttributes) {
        const commonAttributes = firstDocItem.productPage.commonAttributes;

        // 바코드 추출
        if (commonAttributes.productBarcode) {
          barcode = commonAttributes.productBarcode;
          console.log(`추출된 바코드: ${barcode}`);
        }

        // 가격 정보 추출
        if (commonAttributes.purchasePrice) {
          purchasePrice = commonAttributes.purchasePrice;
          console.log(`추출된 공급가: ${purchasePrice}`);
        }

        if (commonAttributes.coupangSalePrice) {
          salePrice = commonAttributes.coupangSalePrice;
          console.log(`추출된 판매가: ${salePrice}`);
        }

        // msrp 또는 osrp 필드 확인
        if (Object.prototype.hasOwnProperty.call(commonAttributes, 'osrp')) {
          osrp = commonAttributes.osrp || 0;
          console.log(`추출된 osrp: ${osrp}`);
        } else if (commonAttributes.msrp) {
          msrp = commonAttributes.msrp;
          console.log(`추출된 msrp: ${msrp}`);
        }
      }

      // coupangDoc에서 데이터 추출
      const quotation = {
        ...baseQuotation,
        productName: productName || baseQuotation.productName,
        modelNumber: modelNumber || baseQuotation.modelNumber,
        brand: brand || baseQuotation.brand,
        manufacturer: manufacturer || baseQuotation.manufacturer,
        purchasePrice: purchasePrice || baseQuotation.purchasePrice,
        salePrice: salePrice || baseQuotation.salePrice,
        msrp: msrp || baseQuotation.msrp,
        osrp: osrp,
        barcode: barcode,
        options: []
      };

      // 각 옵션별 데이터 추출
      if (product.options && Array.isArray(product.options)) {
        console.log(`옵션 데이터 추출 시작 (총 ${product.options.length}개 옵션)`);

        product.options.forEach((option, index) => {
          if (index < product.coupangDoc.jsonDocument.length) {
            const docItem = product.coupangDoc.jsonDocument[index];
            console.log(`옵션 ${option.optionNumber} (${option.korName}) 데이터 추출 중...`);

            // 옵션별 견적서 데이터 생성
            const optionQuotation = {
              optionNumber: option.optionNumber,
              korName: option.korName,
              attributes: []
            };

            // 노출 속성 추출
            if (docItem.productPage && docItem.productPage.commonAttributes &&
                docItem.productPage.commonAttributes.exposedAttributes &&
                Array.isArray(docItem.productPage.commonAttributes.exposedAttributes)) {

              // 중국어 문자 감지 및 처리
              docItem.productPage.commonAttributes.exposedAttributes.forEach(attr => {
                // 값 추출 (attributeValue 또는 value 필드 지원)
                const rawValue = attr.attributeValue || attr.value;
                const attributeValue = this.containsChineseCharacters(rawValue) ?
                  "상세설명 참조" : rawValue;

                optionQuotation.attributes.push({
                  type: 'exposed',
                  name: attr.attributeName,
                  value: attributeValue
                });
              });

              console.log(`노출 속성 ${docItem.productPage.commonAttributes.exposedAttributes.length}개 추출 완료`);
            }

            // 비노출 속성 추출
            if (docItem.productPage && docItem.productPage.unexposedAttributes &&
                Array.isArray(docItem.productPage.unexposedAttributes)) {

              // 중국어 문자 감지 및 처리
              docItem.productPage.unexposedAttributes.forEach(attr => {
                // 값 추출 (attributeValue 또는 value 필드 지원)
                const rawValue = attr.attributeValue || attr.value;
                const attributeValue = this.containsChineseCharacters(rawValue) ?
                  null : rawValue;

                optionQuotation.attributes.push({
                  type: 'unexposed',
                  name: attr.attributeName,
                  value: attributeValue
                });
              });

              console.log(`비노출 속성 ${docItem.productPage.unexposedAttributes.length}개 추출 완료`);
            }

            // 물류 정보 추출
            if (docItem.logisticsPage) {
              optionQuotation.logistics = {
                // 저장된 값 그대로 — 예전엔 비어 있어도 '100*100*100'·'100' 으로 보여 값이 들어간 줄 알았다(702502).
                // 빈 값은 견적서가 하한값으로 채워 저장한다(applyAttributesToOption → fillLogisticsPackage).
                dimension: docItem.logisticsPage.skuUnitBoxDimension || '',
                weight: docItem.logisticsPage.skuUnitBoxWeight || ''
              };
              console.log(`물류 정보 추출 완료: ${optionQuotation.logistics.dimension}, ${optionQuotation.logistics.weight}`);
            }

            // 고시 정보 추출
            if (docItem.legalPage && docItem.legalPage.notices &&
                Array.isArray(docItem.legalPage.notices)) {

              // 중국어 문자 감지 및 처리
              optionQuotation.notices = docItem.legalPage.notices.map(notice => {
                const noticeValue = this.containsChineseCharacters(notice.noticeItemValue) ?
                  "상세설명 참조" : notice.noticeItemValue;

                return {
                  name: notice.noticeItemName,
                  value: noticeValue
                };
              });

              console.log(`고시 정보 ${docItem.legalPage.notices.length}개 추출 완료`);
            }

            quotation.options.push(optionQuotation);
            console.log(`옵션 ${option.optionNumber} 데이터 추출 완료`);
          } else {
            console.warn(`옵션 ${option.optionNumber}에 해당하는 jsonDocument 항목이 없습니다.`);
          }
        });
      }

      // 인증 정보 추출
      if (firstDocItem && firstDocItem.legalPage && firstDocItem.legalPage.certificates &&
          Array.isArray(firstDocItem.legalPage.certificates)) {

        quotation.certificates = firstDocItem.legalPage.certificates.map(cert => ({
          name: cert.certificateName,
          value: '해당사항없음'
        }));

        console.log(`인증 정보 ${quotation.certificates.length}개 추출 완료`);
      }

      // 검색 태그 추출
      if (firstDocItem && firstDocItem.productPage && firstDocItem.productPage.searchTags) {
        quotation.searchTags = firstDocItem.productPage.searchTags;
        console.log(`검색 태그 추출 완료: ${quotation.searchTags}`);
      }

      console.log('견적서 데이터 추출 완료');
      console.log('🔍 [AI 생성 데이터] 견적서 노출속성:', JSON.stringify(quotation.options?.[0]?.exposedAttributes, null, 2));
      return quotation;

    } catch (error) {
      console.error('견적서 데이터 추출 중 오류:', error);
      return this.createBaseQuotationData(product);
    }
  },
  /**
 * 등록 세부정보 처리 메인 함수
 * @param {Object} product - 상품 객체
 * @param {Object} options - 처리 옵션 (선택적)
 * @param {boolean} options.applyToAllOptions - 모든 옵션에 적용할지 여부 (기본값: true)
 * @param {boolean} options._isRetry - 내부 재시도 플래그 (외부에서 사용하지 않음)
 * @returns {Promise<Object>} 처리 결과
 */
  async process(product, options = {}) {
    // 옵션 기본값 설정
    const { applyToAllOptions = true, _isRetry = false } = options;

    // 재시도용 입력값 저장 (깊은 복사)
    const originalProductSnapshot = _isRetry ? null : JSON.parse(JSON.stringify(product));
    const originalOptions = { ...options };

    try {
      // 상품 유효성 확인
      if (!product || !product.registrationId) {
        throw new Error('유효하지 않은 상품 정보입니다.');
      }

      // rocketRegInfo 확인
      if (!product.rocketRegInfo) {
        throw new Error('상품에 등록 정보(rocketRegInfo)가 없습니다.');
      }

      // 상품 정보 로깅 (SEO 상태 확인)
      console.log('📊 quotationService.process 시작 - 상품 정보:', {
        registrationId: product.registrationId,
        seoStatus: product.seoStatus,
        hasSeo: !!product.seo,
        seoProductName: product.seo?.productName,
        hasProductName: !!product.productName,
        productName: product.productName,
        hasCoupangDoc: !!product.coupangDoc,
        coupangDocProductName: product.coupangDoc?.productName
      });

      // [BUNDLE_DEBUG] 번들링 정보 추적
      console.log('[BUNDLE_DEBUG] 견적서 - product.pricing:', product.pricing);
      console.log('[BUNDLE_DEBUG] 견적서 - product.options:', product.options?.map(opt => ({
        optionNumber: opt.optionNumber,
        bundleQuantity: opt.bundleQuantity,
        korName: opt.korName
      })));

      // 상품과 모든 옵션의 coupangDoc에서 모델 번호 길이 확인 및 제한
      this.enforceModelNumberLimitsInAllOptions(product);

      // 로딩 상태 시작
      store.dispatch('setProcessing', {
        isProcessing: true,
        message: '등록 세부정보 생성 중...'
      });

      console.log('AI를 사용하여 견적서 정보 생성을 시작합니다.');

      // 상품 속성을 한국어로 변환 - 요청과 응답을 하나의 함수에서 처리
      console.log('상품 속성 한국어 변환 시작...');
      const translatedProduct = await this.requestTranslateAttributes(product);

      // 기본 견적서 데이터 생성
      const baseQuotationData = this.createBaseQuotationData(translatedProduct);

      // 현재 상태 업데이트 (처리 중) - 부분 업데이트 사용
      await store.dispatch('updateProductPartial', {
        productId: product._id,
        updateFields: {
          'quotation': baseQuotationData,
          'quotationStatus': 'processing',
          'processError': null, // 지난 실패 이유가 이번 결과처럼 읽히지 않게 — 이번에 실패하면 아래에서 다시 적는다
          'updatedAt': Date.now()
        }
      });

      // 속성 요청 데이터 준비 (한국어로 변환된 속성 사용)
      const attributeRequest = this.prepareAttributeRequest(translatedProduct);

      // 처리할 옵션 목록 결정
      let optionsToProcess = [];

      if (applyToAllOptions) {
        // 모든 옵션 처리
        optionsToProcess = product.options || [];
        console.log(`견적서 생성 - 모든 옵션(${optionsToProcess.length}개) 처리`);
      } else {
        // 선택된 옵션만 처리
        const selectedOption = store.state.selectedOption ||
          (product.options && product.options.length > 0 ? product.options[0] : null);

        if (selectedOption) {
          optionsToProcess = [selectedOption];
          console.log(`견적서 생성 - 선택된 옵션만 처리: ${selectedOption.optionNumber} (${selectedOption.korName})`);
        } else {
          // 선택된 옵션이 없으면 첫 번째 옵션 사용
          optionsToProcess = product.options && product.options.length > 0 ? [product.options[0]] : [];
          console.log(`견적서 생성 - 선택된 옵션 없음, 첫 번째 옵션 사용: ${optionsToProcess.length > 0 ? optionsToProcess[0].optionNumber : '없음'}`);
        }
      }

      // 처리할 옵션이 없으면 오류 반환
      if (optionsToProcess.length === 0) {
        throw new Error('처리할 옵션이 없습니다.');
      }

      // 스마트 배치 처리로 옵션별 속성 처리 (3회 재시도, 폴백 없음)
      let successCount = 0;
      let failCount = 0;
      const successfulOptions = [];
      const failedOptions = [];

      const MAX_BATCH_RETRIES = 3;
      let batchAttempt = 0;
      let lastBatchError = null;

      while (batchAttempt < MAX_BATCH_RETRIES) {
        batchAttempt++;

        try {
          console.log(`🚀 스마트 배치 처리 시작 (시도 ${batchAttempt}/${MAX_BATCH_RETRIES})`);

          // 진행 상태 업데이트
          store.dispatch('setProcessing', {
            isProcessing: true,
            message: `등록 세부정보 생성 중... (스마트 배치 처리, 시도 ${batchAttempt}/${MAX_BATCH_RETRIES})`
          });

          // 재시도 시 지연 시간 추가 (지수 백오프)
          if (batchAttempt > 1) {
            const retryDelay = 3000 * Math.pow(2, batchAttempt - 1); // 3초, 6초, 12초
            console.log(`⏳ 재시도 전 ${retryDelay / 1000}초 대기...`);
            await this.sleep(retryDelay);
          }

          const batchResult = await this.processBatchAttributes(product, optionsToProcess, attributeRequest);

          if (batchResult.success) {
            successCount = batchResult.successCount;
            failCount = batchResult.totalProcessed - batchResult.successCount;
            successfulOptions.length = 0;
            successfulOptions.push(...batchResult.successfulOptions);
            failedOptions.length = 0;
            failedOptions.push(...batchResult.failedOptions);

            console.log(`✅ 스마트 배치 처리 완료: ${successCount}/${optionsToProcess.length}개 옵션 성공 (시도 ${batchAttempt})`);
            lastBatchError = null;
            break; // 성공 시 루프 종료
          } else {
            console.warn(`⚠️ 스마트 배치 처리 실패 (시도 ${batchAttempt}/${MAX_BATCH_RETRIES})`);
            // 이유를 그대로 — 비어 오면 꼴 어긋남으로 크게(덮지 않는다)
            lastBatchError = new Error(batchResult.error
              ? (batchResult.message ? `${batchResult.message} — ${batchResult.error}` : batchResult.error)
              : `배치 처리 실패(이유가 비어 옴 — processBatchAttributes 결과 꼴 확인: ${batchResult.message || '메시지 없음'})`);

            if (batchAttempt >= MAX_BATCH_RETRIES) {
              console.error(`❌ 스마트 배치 처리 ${MAX_BATCH_RETRIES}회 시도 후 실패`);
            }
          }
        } catch (error) {
          console.error(`❌ 스마트 배치 처리 중 오류 (시도 ${batchAttempt}/${MAX_BATCH_RETRIES}):`, error);
          // 필수 비노출을 못 만듦 — 같은 스키마·원문이면 다시 돌려도 같다(CB70ED) · 이름·까닭 그대로 바로 멈춘다
          if (error.mandatoryUnexposed) throw error;
          lastBatchError = error;

          if (batchAttempt >= MAX_BATCH_RETRIES) {
            console.error(`❌ 스마트 배치 처리 ${MAX_BATCH_RETRIES}회 시도 후 최종 실패`);
          }
        }
      }

      // 3회 시도 후에도 실패한 경우 에러 throw
      if (lastBatchError) {
        console.error('❌ 스마트 배치 처리 최종 실패:', lastBatchError.message);

        // 에러 로깅 추가
        logServiceError('quotationService', 'process.smartBatchFinal', lastBatchError, {
          errorType: ERROR_TYPES.AI_REQUEST_FAILED,
          productId: product?._id || product?.registrationId,
          additional: {
            attempts: MAX_BATCH_RETRIES,
            optionCount: optionsToProcess?.length
          }
        });

        throw new Error(`스마트 배치 처리 ${MAX_BATCH_RETRIES}회 시도 후 실패: ${lastBatchError.message}`);
      }

      // 모든 옵션 처리 완료 후 상태 업데이트
      const completionMessage = `등록 세부정보 생성 완료 (총 ${optionsToProcess.length}개 중 성공: ${successCount}, 실패: ${failCount})`;

      store.dispatch('setProcessing', {
        isProcessing: false,
        message: completionMessage
      });

      // 최종 상품 상태 업데이트 (최종 검증에서 변경될 수 있으므로 let 사용)
      let finalStatus = failCount === 0 ? 'completed' : (successCount > 0 ? 'partial' : 'error');

      // 옵션별 상태는 점표기법으로 개별 업데이트하므로 여기서는 제거

      // coupangDoc에서 견적서 데이터 추출
      let quotationData = null;
      try {
        // coupangDoc이 존재하면 견적서 데이터 추출
        if (this.checkCoupangDocExists(product)) {
          console.log('coupangDoc에서 견적서 데이터 추출 시작');
          quotationData = this.extractQuotationFromCoupangDoc(product);
          console.log('추출된 견적서 데이터:', quotationData);
        } else {
          console.warn('coupangDoc이 존재하지 않아 견적서 데이터를 추출할 수 없습니다.');
          // 기본 견적서 데이터 생성
          quotationData = this.createBaseQuotationData(product);
          console.log('기본 견적서 데이터 생성:', quotationData);
        }
      } catch (error) {
        console.error('견적서 데이터 추출 중 오류:', error);
        // 오류 발생 시 기본 견적서 데이터 생성
        quotationData = this.createBaseQuotationData(product);
      }

      // ===== 모든 옵션 성공 시에만 노출속성 검증 진행 =====
      console.log('✅ 모든 옵션 속성 생성 완료 → 노출속성 검증 진행');

      // ===== 스키마 순서 재정렬 로직 제거됨 =====
      // AIRocketReg에서 상품 추가시 이미 모든 속성을 미리 맞춰놨으므로 재정렬 불필요
      console.log('ℹ️ 스키마 재정렬 프로세스 건너뜀 (AIRocketReg에서 이미 처리됨)');

      // ===== 추가 속성 감지 로직 시작 =====
      console.log('🔍 노출속성 중복 및 추가 속성 감지 시작');

      // 0. 번역이 원문과 어긋난 노출속성을 먼저 맞춘다(원문 색이 다른데 같은 색상 이름 · 원문에 없는 사이즈 차이)
      if (this.checkCoupangDocExists(product)) {
        await this.alignExposedAttributesToSource(product);
      }

      // 1. coupangDoc.jsonDocument 내의 옵션들 중에 노출속성이 완전히 동일한 옵션이 있는지 확인
      let duplicateGroups = [];
      if (this.checkCoupangDocExists(product) && product.coupangDoc.jsonDocument) {
        console.log('📊 노출속성 중복 검사 시작');

        const exposedAttributesMap = new Map();

        product.coupangDoc.jsonDocument.forEach((doc, index) => {
          if (doc && doc.productPage && doc.productPage.commonAttributes && doc.productPage.commonAttributes.exposedAttributes) {
            const exposedAttrs = doc.productPage.commonAttributes.exposedAttributes;
            const attrKey = JSON.stringify(exposedAttrs);

            if (exposedAttributesMap.has(attrKey)) {
              // 중복 발견
              const existingIndex = exposedAttributesMap.get(attrKey);
              let duplicateGroup = duplicateGroups.find(group => group.includes(existingIndex));
              if (!duplicateGroup) {
                duplicateGroup = [existingIndex];
                duplicateGroups.push(duplicateGroup);
              }
              duplicateGroup.push(index);
            } else {
              exposedAttributesMap.set(attrKey, index);
            }

            console.log(`📋 옵션 ${index + 1} 노출속성:`, exposedAttrs);
          }
        });

        if (duplicateGroups.length > 0) {
          console.warn('⚠️ 노출속성이 완전히 동일한 옵션들 발견:', duplicateGroups);
          duplicateGroups.forEach((group, groupIndex) => {
            console.warn(`중복 그룹 ${groupIndex + 1}:`, group.map(idx => `옵션 ${idx + 1}`).join(', '));
          });
        } else {
          console.log('✅ 노출속성 중복 없음');
        }
      }

      // 2. 모든 옵션의 korName 로깅
      if (product.options && product.options.length > 0) {
        console.log('📝 모든 옵션의 korName:');
        product.options.forEach((option, index) => {
          console.log(`옵션 ${index + 1} (${option.optionNumber}): ${option.korName}`);
        });
      }

      // 3. 노출속성 중복이 있는 경우에만 추가속성 감지 및 상품 분리 진행
      let productWasSplit = false; // 상품 분리 여부 플래그
      let splitSaveError = null; // 분리 저장 실패(옵션이 빠질 수 있음) → 오류로 끝낸다
      // 노출속성이 겹치면 ① 값을 서로 다르게 고치고 ② 못 고친 옵션만 둘째 상품으로 뺀다.
      // AI 차이점으로 옵션 전체를 다시 묶지 않는다 — 25색이 22상품으로 쪼개지고 1옵션이 사라졌다(PR261016-0975BF).
      if (duplicateGroups.length > 0) {
        const dupResult = await this.resolveExposedDuplicates(product);
        if (dupResult.status === 'resolved') {
          duplicateGroups = [];
        } else if (dupResult.status === 'split') {
          return dupResult.result;
        } else if (dupResult.status === 'failed') {
          throw new Error(dupResult.error);
        }
        // 'skip'(문서·옵션 수가 안 맞음) → 아래 기존 경로
      }
      if (duplicateGroups.length > 0) {
        console.log('🤖 노출속성 중복이 감지되어 추가 속성 감지를 진행합니다.');

        // 모든 옵션의 노출속성과 옵션명을 확장으로 보내서 추가속성 감지
        try {
          if (this.checkCoupangDocExists(product) && product.coupangDoc.jsonDocument && product.options) {
            console.log('🤖 AI를 통한 추가 속성 감지 시작');

          // 모든 옵션의 노출속성과 옵션명 수집
          const optionAnalysisData = [];

          product.options.forEach((option, index) => {
            const doc = product.coupangDoc.jsonDocument[index];
            if (doc && doc.productPage && doc.productPage.commonAttributes && doc.productPage.commonAttributes.exposedAttributes) {
              optionAnalysisData.push({
                optionNumber: option.optionNumber,
                korName: option.korName,
                exposedAttributes: doc.productPage.commonAttributes.exposedAttributes
              });
            }
          });

          if (optionAnalysisData.length > 0) {
            // 실제로 중복된 노출속성만 추출 (스키마 전체가 아닌)
            const duplicatedExposedAttributes = this.extractDuplicatedExposedAttributes(duplicateGroups, product);
            console.log('📋 실제 중복된 노출속성:', duplicatedExposedAttributes);

            console.log('📋 실제 중복된 노출속성:', duplicatedExposedAttributes);
            console.log('📋 옵션 분석 데이터:', optionAnalysisData);

            // 간단한 텍스트 형식으로 추가속성 감지 (응답 길이 단축)
            console.log(`🎯 추가속성 감지 방법: 간단한 텍스트 형식`);
            console.log(`📊 분석 대상: 옵션수=${optionAnalysisData.length}, 중복그룹수=${duplicateGroups.length}`);

            // 옵션 정보를 간단하게 포맷팅
            const simpleOptionList = optionAnalysisData.map(option =>
              `${option.optionNumber}: ${option.korName}`
            ).join('\n');

            console.log('📋 AI에게 전송할 옵션 목록 (간단 형식):', simpleOptionList.substring(0, 200) + '...');

            // 간단한 텍스트 형식으로 추가속성 감지
            console.log('📊 간단한 텍스트 형식으로 추가속성 감지 시작');
            const additionalAttributesResult = await workflowService.requestExtensionWithPromise(
              {
                workType: 'DETECT_ADDITIONAL_ATTRIBUTES',
                productId: product.registrationId,
                requestArr: [{
                  prompt: `다음 옵션들에서 차별화 요소를 찾아주세요.

중복된 노출속성 (이미 구분됨): ${JSON.stringify(duplicatedExposedAttributes)}

옵션 목록:
${simpleOptionList}

작업: 옵션명에서 노출속성 외의 차별화 요소를 찾아주세요.

응답 형식 (단순한 JSON):
{
  "옵션번호": ["차별화요소1", "차별화요소2"],
  "옵션번호": ["차별화요소"]
}

예시:
{
  "250622008001": ["손가락노출형", "프리미엄"],
  "250622008002": ["손가락없음", "스탠다드"],
  "250622008003": ["손가락노출형", "프리미엄"]
}

규칙:
- 차별화 요소는 간단하게 (한글 2-6글자)
- 중국어는 한글로 번역
- 일관된 용어 사용
- 차별화 요소가 1개면 배열에 1개만, 여러개면 배열에 여러개`
                }],
                duplicatedExposedAttributes: duplicatedExposedAttributes,
                optionAnalysisData: optionAnalysisData
              },
              {
                timeout: 60000, // 60초 타임아웃
                maxRetries: 2   // 최대 2회 재시도
              }
            );

            // 4. 추가 속성 감지 결과 로깅
            console.log('🎯 추가 속성 감지 결과:', additionalAttributesResult);

            // 서버 모드: 직접 객체 반환 / Extension 모드: {batchResults: [...]} 형태
            let responseText = null;
            if (additionalAttributesResult) {
              if (additionalAttributesResult.batchResults && additionalAttributesResult.batchResults.length > 0) {
                // Extension 모드 응답
                const batchResult = additionalAttributesResult.batchResults[0];
                responseText = batchResult.result || batchResult.processedText;
              } else if (typeof additionalAttributesResult === 'object' && !additionalAttributesResult.batchResults) {
                // 서버 모드: 결과가 직접 반환됨
                responseText = additionalAttributesResult;
              }
            }

            if (responseText) {
              let parsedResult = null;

              try {
                  console.log('📋 AI 응답 (원본):', responseText?.toString().substring(0, 300) + '...');

                  // 단순한 JSON 형식 파싱
                  let jsonData = null;
                  if (typeof responseText === 'object') {
                    jsonData = responseText;
                  } else {
                    // JSON 블록 제거 후 파싱
                    let jsonText = responseText;
                    jsonText = jsonText.replace(/```json\n|\n```/g, '');
                    jsonText = jsonText.replace(/```\n|\n```/g, '');
                    jsonText = jsonText.trim();
                    jsonData = JSON.parse(jsonText);
                  }

                  // 단순한 JSON을 기존 형식으로 변환
                  const additionalAttributes = [];
                  if (jsonData && typeof jsonData === 'object') {
                    // 새로운 단순 형식: {"옵션번호": ["차별화요소1", "차별화요소2"]}
                    if (!jsonData.additionalAttributes) {
                      for (const [optionNumber, attributeValues] of Object.entries(jsonData)) {
                        if (Array.isArray(attributeValues)) {
                          additionalAttributes.push({
                            optionNumber: optionNumber,
                            attributes: attributeValues.map(value => ({ value: value }))
                          });
                        }
                      }
                      parsedResult = { additionalAttributes };
                    } else {
                      // 기존 형식 유지
                      parsedResult = jsonData;
                    }
                  }

                  const finalCount = additionalAttributes.length || parsedResult?.additionalAttributes?.length || 0;
                  console.log(`✅ JSON 파싱 완료: ${finalCount}개 추가 속성 감지`);
                  console.log('📋 파싱된 결과 구조:', JSON.stringify(parsedResult, null, 2).substring(0, 500) + '...');

                  if (parsedResult && parsedResult.additionalAttributes) {
                    console.log('✅ 계층적 추가 속성 감지 성공:');
                    parsedResult.additionalAttributes.forEach(optionAttr => {
                      console.log(`옵션 ${optionAttr.optionNumber} 추가 속성:`);
                      if (Array.isArray(optionAttr.attributes)) {
                        optionAttr.attributes.forEach(attr => {
                          if (typeof attr === 'object' && attr.level) {
                            // 새로운 계층적 형식
                            console.log(`  - ${attr.level}차: ${attr.value} (${attr.category || '기타'})${attr.original !== attr.value ? ` [원본: ${attr.original}]` : ''}`);
                          } else if (typeof attr === 'object' && attr.value) {
                            // 단순한 형식: {value: "값"}
                            console.log(`  - ${attr.value}`);
                          } else {
                            // 기존 형식 호환 (문자열)
                            console.log(`  - ${attr}`);
                          }
                        });
                      }
                    });

                    // 5. 추가 속성이 감지된 경우 상품 분리 실행
                    console.log('🔄 추가 속성 기준 상품 분리 시작');

                    // 🔥 분리 전에 모든 옵션이 동일한 추가 속성을 가지는지 확인
                    let needsRetry = false;
                    if (parsedResult.additionalAttributes.length > 1) {
                      const firstAttrKey = JSON.stringify(parsedResult.additionalAttributes[0].attributes);
                      const allSame = parsedResult.additionalAttributes.every(attrData =>
                        JSON.stringify(attrData.attributes) === firstAttrKey
                      );

                      if (allSame) {
                        console.log('⚠️ 모든 옵션이 동일한 추가 속성을 가지고 있습니다. AI가 속성 값을 제대로 감지하지 못한 것으로 판단됩니다.');
                        console.log(`📋 동일한 추가 속성: ${JSON.stringify(parsedResult.additionalAttributes[0].attributes)}`);
                        needsRetry = true;
                      }
                    }

                    // 🔥 재시도 로직
                    let finalAdditionalAttributes = parsedResult.additionalAttributes;
                    if (needsRetry) {
                      console.log('🔄 프롬프트를 개선하여 추가 속성 감지 재시도...');

                      const retryResult = await workflowService.requestExtensionWithPromise(
                        {
                          workType: 'DETECT_ADDITIONAL_ATTRIBUTES',
                          productId: product.registrationId,
                          requestArr: [{
                            prompt: `다음 옵션들에서 차별화 요소를 찾아주세요.

⚠️ 중요: 속성 "이름"이 아닌 각 옵션의 실제 "값"을 감지해야 합니다!

중복된 노출속성 (이미 구분됨): ${JSON.stringify(duplicatedExposedAttributes)}

옵션 목록:
${simpleOptionList}

작업: 각 옵션명에서 실제로 다른 부분을 찾아주세요.

예시 (잘못된 답변):
{
  "251103001001": ["재질", "색상"],  ❌ 속성 이름만 감지 (잘못됨!)
  "251103001002": ["재질", "색상"]   ❌ 모든 옵션이 동일 (잘못됨!)
}

예시 (올바른 답변):
{
  "251103001001": ["부직포", "회색", "3cm", "3m"],  ✅ 실제 값 감지 (올바름!)
  "251103001002": ["부직포", "회색", "5cm", "5m"],  ✅ 각 옵션마다 다름 (올바름!)
  "251103001009": ["PE", "회색", "3cm", "3m"]       ✅ 재질이 다름 (올바름!)
}

응답 형식 (단순한 JSON):
{
  "옵션번호": ["실제값1", "실제값2", "실제값3"],
  "옵션번호": ["실제값1", "실제값2"]
}

규칙:
- 각 옵션의 실제 차이점을 찾아주세요 (속성 이름이 아닌 값!)
- 차별화 요소는 간단하게 (한글 2-6글자)
- 중국어는 한글로 번역
- 일관된 용어 사용`
                          }],
                          duplicatedExposedAttributes: duplicatedExposedAttributes,
                          optionAnalysisData: optionAnalysisData
                        },
                        {
                          timeout: 60000,
                          maxRetries: 2
                        }
                      );

                      // 재시도 결과 파싱
                      if (retryResult && retryResult.batchResults && retryResult.batchResults.length > 0) {
                        const retryBatchResult = retryResult.batchResults[0];
                        if (retryBatchResult.result || retryBatchResult.processedText) {
                          try {
                            let retryResponseText = retryBatchResult.result || retryBatchResult.processedText;
                            console.log('📋 재시도 AI 응답 (원본):', retryResponseText?.toString().substring(0, 300) + '...');

                            let retryJsonData = null;
                            if (typeof retryResponseText === 'object') {
                              retryJsonData = retryResponseText;
                            } else {
                              let jsonText = retryResponseText;
                              jsonText = jsonText.replace(/```json\n|\n```/g, '');
                              jsonText = jsonText.replace(/```\n|\n```/g, '');
                              jsonText = jsonText.trim();
                              retryJsonData = JSON.parse(jsonText);
                            }

                            const retryAdditionalAttributes = [];
                            if (retryJsonData && typeof retryJsonData === 'object') {
                              if (!retryJsonData.additionalAttributes) {
                                for (const [optionNumber, attributeValues] of Object.entries(retryJsonData)) {
                                  if (Array.isArray(attributeValues)) {
                                    retryAdditionalAttributes.push({
                                      optionNumber: optionNumber,
                                      attributes: attributeValues.map(value => ({ value: value }))
                                    });
                                  }
                                }
                              } else {
                                retryAdditionalAttributes.push(...retryJsonData.additionalAttributes);
                              }
                            }

                            // 재시도 결과 검증
                            if (retryAdditionalAttributes.length > 1) {
                              const retryFirstAttrKey = JSON.stringify(retryAdditionalAttributes[0].attributes);
                              const retryAllSame = retryAdditionalAttributes.every(attrData =>
                                JSON.stringify(attrData.attributes) === retryFirstAttrKey
                              );

                              if (!retryAllSame) {
                                console.log('✅ 재시도 성공! 서로 다른 추가 속성을 감지했습니다.');
                                finalAdditionalAttributes = retryAdditionalAttributes;
                              } else {
                                console.log('⚠️ 재시도에도 동일한 속성을 감지했습니다. 옵션명으로 강제 분리합니다.');
                              }
                            }
                          } catch (retryParseError) {
                            console.error('❌ 재시도 결과 파싱 실패:', retryParseError);
                          }
                        }
                      }
                    }

                    const splitResult = await this.splitProductByAdditionalAttributes(product, finalAdditionalAttributes);

                    if (splitResult.success) {
                      console.log('✅ 상품 분리 완료:', splitResult);
                      return await this.finishSplit(product, splitResult);
                    } else {
                      console.error('❌ 상품 분리 실패:', splitResult.error);

                      // 에러 로깅 추가
                      logServiceError('quotationService', 'process.splitProducts', new Error(splitResult.error || '상품 분리 실패'), {
                        errorType: ERROR_TYPES.PROCESSING_ERROR,
                        productId: product?._id || product?.registrationId,
                        additional: {
                          duplicateGroups: duplicateGroups?.length,
                          optionCount: product?.options?.length
                        }
                      });

                      // 옵션을 나누다 저장에 실패했으면(옵션 누락·새 상품 저장 실패) 아래에서 오류로 끝낸다
                      if (Array.isArray(splitResult.failedOptionNumbers) && splitResult.failedOptionNumbers.length > 0) {
                        splitSaveError = splitResult.error;
                      }

                      // 상품 분리 실패 시 원본 상품 상태를 completed로 설정
                      console.log('🔄 상품 분리 실패로 인한 원본 상품 상태 복구');
                      try {
                        await store.dispatch('updateProductPartial', {
                          productId: product._id,
                          updateFields: {
                            'quotationStatus': 'completed',
                            'updatedAt': Date.now()
                          }
                        });
                        product.quotationStatus = 'completed'; // 메모리 상태도 업데이트
                        console.log('✅ 원본 상품 상태 복구 완료');
                      } catch (recoveryError) {
                        console.error('❌ 원본 상품 상태 복구 실패:', recoveryError);
                      }
                    }
                  } else {
                    console.log('ℹ️ 추가 속성이 감지되지 않았습니다.');
                  }
                } catch (parseError) {
                  console.error('추가 속성 결과 파싱 오류:', parseError);
                  console.log('원본 결과:', responseText);
                }
            } else {
              console.log('ℹ️ 추가 속성 감지 결과가 없습니다.');
            }
          } else {
            console.log('ℹ️ 분석할 옵션 데이터가 없습니다.');
          }
        } else {
          console.log('ℹ️ coupangDoc 또는 옵션 데이터가 없어 추가 속성 감지를 건너뜁니다.');
        }
      } catch (additionalAttrError) {
        console.error('추가 속성 감지 중 오류:', additionalAttrError);
      }
    } else {
      console.log('✅ 노출속성 중복이 없어 추가 속성 감지 및 상품 분리를 건너뜁니다.');

      // 노출속성 중복이 없으므로 바로 completed 상태로 업데이트
      console.log('📊 노출속성 중복이 없으므로 상품을 completed 상태로 업데이트');
      try {
        await store.dispatch('updateProductPartial', {
          productId: product._id,
          updateFields: {
            'quotationStatus': 'completed',
            'updatedAt': Date.now()
          }
        });
        product.quotationStatus = 'completed'; // 메모리 상태도 업데이트
        console.log('✅ 상품 completed 상태 업데이트 완료');
      } catch (updateError) {
        console.error('❌ 상품 completed 상태 업데이트 실패:', updateError);
      }
    }

      if (splitSaveError) throw new Error(splitSaveError);

      console.log('🔍 노출속성 중복 및 추가 속성 감지 완료');
      // ===== 추가 속성 감지 로직 끝 =====

      // 상품이 분리되지 않은 경우는 이미 위에서 completed 처리되었으므로 추가 처리 불필요
      if (!productWasSplit) {
        console.log('📊 상품 분리가 없었으므로 이미 completed 상태로 처리됨');
      } else {
        console.log('📊 상품이 분리되었으므로 개별 상품들이 각각 처리됨');
      }

      // ===== 최종 검증 로직 (기존 validateProductSchema 사용) =====
      console.log('🔍 [최종 검증] 견적서 스키마 검증 시작');
      const finalValidation = await this.validateProductSchema(product);

      if (!finalValidation.isValid) {
        console.warn(`⚠️ [최종 검증] ${finalValidation.errors.length}개 문제 발견`);
        finalValidation.errors.forEach((err, idx) => console.warn(`  ${idx + 1}. ${err}`));

        // 재시도 가능 여부 분석
        const errorAnalysis = this.analyzeValidationErrors(finalValidation.errors);

        // 재시도가 필요하고 아직 재시도하지 않은 경우 1회 재시도
        if (errorAnalysis.isRetryable && !_isRetry) {
          console.log('🔄 [최종 검증] 문제 발견으로 인해 process 전체를 1회 재시도합니다.');

          // 진행 상태 업데이트
          store.dispatch('setProcessing', {
            isProcessing: true,
            message: '견적서 검증 실패, 재시도 중...'
          });

          // 잠시 대기 후 재시도 (3초)
          await this.sleep(3000);

          // 원본 스냅샷으로 재시도 (재시도 플래그 설정)
          console.log('🔄 [재시도] 저장된 원본 상품 데이터로 process 재시도');
          const retryResult = await this.process(originalProductSnapshot, {
            ...originalOptions,
            _isRetry: true
          });

          // 재시도 결과에 재시도 정보 추가
          retryResult._wasRetried = true;
          retryResult._originalValidationErrors = finalValidation.errors;

          return retryResult;
        } else {
          // 재시도 불가 또는 이미 재시도한 경우 → error 상태로 설정
          console.error('❌ [최종 검증] 재시도 후에도 검증 실패 → error 상태로 설정');
          finalStatus = 'error';

          // 에러 로깅 추가
          logServiceError('quotationService', 'process.finalValidation', new Error('최종 검증 실패'), {
            errorType: ERROR_TYPES.VALIDATION_ERROR,
            productId: product?._id || product?.registrationId,
            additional: {
              validationErrors: finalValidation.errors,
              optionCount: product?.options?.length
            }
          });

          // 상품 상태를 error로 업데이트
          try {
            await store.dispatch('updateProductPartial', {
              productId: product._id,
              updateFields: {
                'quotationStatus': 'error',
                'processError': {
                  message: '최종 검증 실패',
                  errors: finalValidation.errors,
                  timestamp: Date.now()
                },
                'updatedAt': Date.now()
              }
            });
            product.quotationStatus = 'error';
          } catch (updateError) {
            console.error('❌ error 상태 업데이트 실패:', updateError);
          }
        }
      } else {
        console.log('✅ [최종 검증] 모든 필드가 올바르게 채워졌습니다.');

        // 검증 성공 → completed 상태로 업데이트
        try {
          await store.dispatch('updateProductPartial', {
            productId: product._id,
            updateFields: {
              'quotationStatus': 'completed',
              'updatedAt': Date.now()
            }
          });
          product.quotationStatus = 'completed';

          // 옵션들도 completed로 업데이트
          if (product.options && product.options.length > 0) {
            const optionUpdateFields = {};
            product.options.forEach((_, idx) => {
              optionUpdateFields[`options.${idx}.quotationStatus`] = 'completed';
            });
            await store.dispatch('updateProductPartial', {
              productId: product._id,
              updateFields: optionUpdateFields
            });
          }

          console.log('✅ 상품 및 옵션 상태를 completed로 업데이트 완료');
        } catch (updateError) {
          console.error('❌ completed 상태 업데이트 실패:', updateError);
        }
      }
      // ===== 최종 검증 로직 끝 =====

      // 옵션별 상태 업데이트는 위에서 최종 검증 결과에 따라 수행됨

      return {
        status: finalStatus,
        message: completionMessage,
        details: {
          total: optionsToProcess.length,
          success: successCount,
          failed: failCount,
          successfulOptions,
          failedOptions
        },
        quotation: quotationData, // 견적서 데이터 포함
        finalValidation: finalValidation, // 최종 검증 결과 포함
        _wasRetried: _isRetry, // 재시도 여부
        error: failCount > 0 || !finalValidation.isValid ? {
          message: !finalValidation.isValid
            ? `최종 검증 실패: ${finalValidation.errors.length}개 오류`
            : `${failCount}개 옵션 처리 중 오류가 발생했습니다.`,
          failedOptions,
          validationErrors: finalValidation.errors
        } : null
      };
    } catch (error) {
      console.error('등록 세부정보 처리 중 오류:', error);
      logAIRequestError('quotationService', 'processQuotation', error, {
        productId: product?.registrationId,
        additional: { optionCount: product?.options?.length }
      });

      // 로딩 상태 종료
      store.dispatch('setProcessing', {
        isProcessing: false,
        message: '등록 세부정보 처리 중 오류가 발생했습니다: ' + error.message
      });

      // 실패 이유를 문서에 남긴다 — 안 남기면 견적서가 processing/error 로만 보이고 MCP 도구 응답에 이유가 없다
      if (product && product._id) {
        try {
          await store.dispatch('updateProductPartial', {
            productId: product._id,
            updateFields: {
              'quotationStatus': 'error',
              'processError': { message: '견적서 처리 중 오류: ' + (error.message || '알 수 없는 오류'), timestamp: Date.now() },
              'updatedAt': Date.now()
            }
          });
          product.quotationStatus = 'error';
        } catch (updateError) {
          console.error('❌ 견적서 오류 이유 저장 실패:', updateError);
        }
      }

      return {
        status: 'error',
        error: error.message || '알 수 없는 오류'
      };
    }
  },

  /**
   * 스키마 검증 실패 시 재시도 처리
   * @param {Object} product - 상품 객체
   * @param {Array} validationErrors - 검증 오류 목록
   * @returns {Promise<Object>} 재시도 결과
   */
  async retryQuotationProcess(product, validationErrors) {
    try {
      console.log('🔄 [재시도] 검증 오류 분석 시작');

      // 오류 유형 분석
      const errorAnalysis = this.analyzeValidationErrors(validationErrors);
      console.log('🔍 [오류 분석]:', errorAnalysis);

      // 재시도가 필요한 오류인지 확인
      if (!errorAnalysis.isRetryable) {
        console.log('⚠️ [재시도 불가] 재시도로 해결할 수 없는 오류입니다.');
        return { success: false, error: '재시도 불가능한 오류' };
      }

      // 문제가 있는 옵션들만 재처리
      const problematicOptions = this.identifyProblematicOptions(product, validationErrors);
      console.log(`🎯 [재시도 대상] ${problematicOptions.length}개 옵션 재처리`);

      if (problematicOptions.length === 0) {
        console.log('ℹ️ [재시도 불필요] 재처리할 옵션이 없습니다.');
        return { success: false, error: '재처리할 옵션 없음' };
      }

      // 재시도 실행 (문제 옵션들만)
      const retryResult = await this.processSpecificOptions(product, problematicOptions, errorAnalysis);

      if (retryResult.success) {
        console.log(`✅ [재시도 완료] ${retryResult.processedCount}개 옵션 재처리 성공`);
        return { success: true, processedCount: retryResult.processedCount };
      } else {
        console.log(`❌ [재시도 실패] ${retryResult.error}`);
        return { success: false, error: retryResult.error };
      }

    } catch (error) {
      console.error('❌ [재시도 오류]:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * 검증 오류 분석
   * @param {Array} validationErrors - 검증 오류 목록
   * @returns {Object} 오류 분석 결과
   */
  analyzeValidationErrors(validationErrors) {
    const analysis = {
      isRetryable: false,
      missingRequiredAttributes: [],
      emptyValueAttributes: [],
      orderIssues: [],
      otherIssues: []
    };

    validationErrors.forEach(error => {
      if (error.includes('필수') && error.includes('누락')) {
        analysis.missingRequiredAttributes.push(error);
        analysis.isRetryable = true; // 필수 속성 누락은 재시도 가능
      } else if (error.includes('순서')) {
        analysis.orderIssues.push(error);
        analysis.isRetryable = true; // 순서 문제는 재시도 가능
      } else if (error.includes('빈 값') || error.includes('비어있')) {
        analysis.emptyValueAttributes.push(error);
        analysis.isRetryable = true; // 빈 값 문제는 재시도 가능
      } else {
        analysis.otherIssues.push(error);
      }
    });

    return analysis;
  },

  /**
   * 문제가 있는 옵션들 식별
   * @param {Object} product - 상품 객체
   * @param {Array} validationErrors - 검증 오류 목록
   * @returns {Array} 문제가 있는 옵션 목록
   */
  identifyProblematicOptions(product, validationErrors) {
    const problematicOptionNumbers = new Set();

    validationErrors.forEach(error => {
      // "옵션 1:", "옵션 2:" 등에서 옵션 번호 추출
      const optionMatch = error.match(/옵션 (\d+):/);
      if (optionMatch) {
        const optionIndex = parseInt(optionMatch[1]) - 1; // 0-based index
        if (product.options && product.options[optionIndex]) {
          problematicOptionNumbers.add(product.options[optionIndex].optionNumber);
        }
      }
    });

    return product.options.filter(option =>
      problematicOptionNumbers.has(option.optionNumber)
    );
  },

  /**
   * 특정 옵션들만 재처리
   * @param {Object} product - 상품 객체
   * @param {Array} optionsToRetry - 재처리할 옵션 목록
   * @param {Object} errorAnalysis - 오류 분석 결과
   * @returns {Promise<Object>} 처리 결과
   */
  async processSpecificOptions(product, optionsToRetry, errorAnalysis) {
    try {
      console.log(`🔄 [특정 옵션 재처리] ${optionsToRetry.length}개 옵션 처리 시작`);

      let processedCount = 0;
      const errors = [];

      // 재시도 시에는 더 구체적인 프롬프트 사용
      const enhancedAttributeRequest = this.createEnhancedAttributeRequest(product, errorAnalysis);

      for (const option of optionsToRetry) {
        try {
          const optionIndex = product.options.findIndex(opt => opt.optionNumber === option.optionNumber);
          if (optionIndex === -1) continue;

          console.log(`🔄 [재처리] 옵션 ${option.optionNumber} (${option.korName}) 처리 중...`);

          // 개별 옵션 재처리 (강화된 요청 사용)
          await this.processOptionAttributes(product, optionIndex, enhancedAttributeRequest, 0, 1); // 재시도는 1회만
          processedCount++;

          console.log(`✅ [재처리 완료] 옵션 ${option.optionNumber} 처리 성공`);

        } catch (optionError) {
          console.error(`❌ [재처리 실패] 옵션 ${option.optionNumber}:`, optionError);
          errors.push(`옵션 ${option.optionNumber}: ${optionError.message}`);
        }
      }

      if (processedCount > 0) {
        console.log(`✅ [재처리 결과] ${processedCount}/${optionsToRetry.length}개 옵션 재처리 완료`);
        return { success: true, processedCount, errors };
      } else {
        return { success: false, error: '재처리된 옵션이 없습니다.', errors };
      }

    } catch (error) {
      console.error('❌ [특정 옵션 재처리 오류]:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * 오류 분석 기반 강화된 속성 요청 생성
   * @param {Object} product - 상품 객체
   * @param {Object} errorAnalysis - 오류 분석 결과
   * @returns {Object} 강화된 속성 요청
   */
  createEnhancedAttributeRequest(product, errorAnalysis) {
    const baseRequest = this.createAttributeRequest(product);

    // 오류 분석 결과를 바탕으로 프롬프트 강화
    const enhancements = [];

    if (errorAnalysis.missingRequiredAttributes.length > 0) {
      enhancements.push('필수 노출속성을 모두 포함하여 생성해주세요. 특히 누락된 속성들을 반드시 포함해야 합니다.');
    }

    if (errorAnalysis.emptyValueAttributes.length > 0) {
      enhancements.push('모든 속성에 적절한 값을 입력해주세요. 빈 값이나 공백은 허용되지 않습니다.');
    }

    if (errorAnalysis.orderIssues.length > 0) {
      enhancements.push('스키마에서 정의한 순서대로 속성을 배치해주세요.');
    }

    return {
      ...baseRequest,
      enhancedInstructions: enhancements.join(' '),
      isRetry: true
    };
  },

  /**
   * 상품의 스키마 검증을 수행합니다.
   * @param {Object} product - 상품 객체
   * @returns {Promise<Object>} 검증 결과
   */
  async validateProductSchema(product) {
    try {
      // 스키마가 없으면 기본 통과
      if (!product.rocketRegInfo?.schemaString) {
        console.warn('스키마가 없어 검증을 건너뜁니다.');
        return {
          isValid: true,
          completionRate: 1.0,
          errors: [],
          chineseValidation: true,
          validOptions: 1,
          totalOptions: 1
        };
      }

      const validator = new PracticalSchemaValidator(product.rocketRegInfo.schemaString);

      if (!validator.schema) {
        console.warn('스키마 파싱 실패로 검증을 건너뜁니다.');
        return {
          isValid: true,
          completionRate: 1.0,
          errors: [],
          chineseValidation: true,
          validOptions: 1,
          totalOptions: 1
        };
      }

      let totalValid = 0;
      let totalOptions = 0;
      const allErrors = [];
      let hasChineseIssues = false;

      if (product.coupangDoc?.jsonDocument) {
        for (const [index, docData] of product.coupangDoc.jsonDocument.entries()) {
          const result = validator.validateDocument(docData);

          // 기본 스키마 검증
          if (result.isValid) {
            totalValid++;
          } else {
            allErrors.push(`옵션 ${index + 1}: ${result.errors.join(', ')}`);

            // 중국어 관련 오류 체크
            const chineseErrors = result.errors.filter(error => error.includes('중국어'));
            if (chineseErrors.length > 0) {
              hasChineseIssues = true;
            }
          }

          // 추가: 순서와 항목 완성도 검증
          const attributeData = this.extractAttributesFromDocData(docData);
          const completenessValidation = this.validateAttributeCompleteness(attributeData, product.rocketRegInfo.schemaString);

          if (!completenessValidation.isValid) {
            console.warn(`🔍 [스키마 검증] 옵션 ${index + 1} 순서/완성도 문제:`, completenessValidation);

            if (!completenessValidation.orderValid) {
              allErrors.push(`옵션 ${index + 1} 순서 오류: ${completenessValidation.orderIssues.join(', ')}`);
            }

            if (!completenessValidation.completenessValid) {
              allErrors.push(`옵션 ${index + 1} 필수 속성 누락: ${completenessValidation.missingRequired.join(', ')}`);
            }
          }

          totalOptions++;
        }
      }

      // 전체 완성도 계산
      const overallCompletionRate = totalOptions > 0 ?
        product.coupangDoc.jsonDocument.reduce((sum, docData) => {
          return sum + validator.calculateCompletionRate(docData);
        }, 0) / totalOptions : 0;

      return {
        isValid: totalValid === totalOptions && totalOptions > 0,
        completionRate: overallCompletionRate,
        validOptions: totalValid,
        totalOptions: totalOptions,
        errors: allErrors,
        chineseValidation: !hasChineseIssues
      };

    } catch (error) {
      console.error('스키마 검증 중 오류:', error);
      return {
        isValid: false,
        completionRate: 0,
        errors: [error.message],
        chineseValidation: true,
        validOptions: 0,
        totalOptions: 0
      };
    }
  },

  /**
   * 분리된 상품들에 대해 개별적으로 노출속성 검증 및 재분리 수행
   * @param {Object} originalProduct - 원본 상품 객체
   * @param {Array} newProducts - 새로 생성된 상품들
   * @returns {Promise<void>}
   */
  /**
   * 분리된 상품마다 최종 검증(validateProductSchema)을 돌려, 못 넘은 것은 분리 안 된 경로와 같은 꼴로 error 를 남긴다.
   * @returns {Promise<Array<{registrationId, errors}>>} 못 넘은 상품
   */
  async applyFinalValidationToSplitProducts(products) {
    const invalid = [];
    for (const p of products) {
      if (!p || !p._id) continue;
      try {
        const v = await this.validateProductSchema(p);
        if (v.isValid) continue;
        invalid.push({ registrationId: p.registrationId, errors: v.errors });
        const processError = { message: '최종 검증 실패', errors: v.errors, timestamp: Date.now() };
        await store.dispatch('updateProductPartial', {
          productId: p._id,
          updateFields: { 'quotationStatus': 'error', 'processError': processError, 'updatedAt': Date.now() }
        });
        p.quotationStatus = 'error';
        p.processError = processError;
      } catch (e) {
        console.error(`❌ 분리 상품 ${p.registrationId} 최종 검증 실패 처리 중 오류:`, e);
      }
    }
    return invalid;
  },

  async processExposedAttributesForSplitProducts(originalProduct, newProducts) {
    try {
      console.log(`🔍 분리된 상품들 개별 검증 시작: ${newProducts.length}개 상품`);

      // 원본 상품 먼저 검증
      await this.processIndividualProductExposedAttributes(originalProduct);

      // 새로 생성된 상품들 각각 검증
      for (const newProduct of newProducts) {
        await this.processIndividualProductExposedAttributes(newProduct);
      }

      console.log('✅ 모든 분리된 상품들의 개별 검증 완료');
    } catch (error) {
      console.error('❌ 분리된 상품들 개별 검증 중 오류:', error);
    }
  },

  /**
   * 개별 상품에 대한 노출속성 검증 및 재분리 처리
   * @param {Object} product - 검증할 상품 객체
   * @returns {Promise<void>}
   */
  async processIndividualProductExposedAttributes(product) {
    try {
      console.log(`🔍 상품 ${product._id || product.registrationId} 개별 노출속성 검증 시작`);

      // 옵션이 1개 이하인 경우 검사 건너뛰기
      const optionCount = product.options?.length || 0;
      if (optionCount <= 1) {
        console.log(`ℹ️ 상품 ${product._id || product.registrationId}: 옵션이 ${optionCount}개이므로 노출속성 중복 검사 건너뜀 → completed 처리`);
        await store.dispatch('updateProductPartial', {
          productId: product._id,
          updateFields: {
            'quotationStatus': 'completed',
            'updatedAt': Date.now()
          }
        });
        return;
      }

      // 노출속성 중복 검사 (기존 로직 재사용)
      console.log(`🔍 상품 ${product._id || product.registrationId} 노출속성 중복 검사 시작 (옵션 ${optionCount}개)`);

      let duplicateGroups = [];
      if (this.checkCoupangDocExists(product) && product.coupangDoc.jsonDocument) {
        const exposedAttributesMap = new Map();

        product.coupangDoc.jsonDocument.forEach((doc, index) => {
          if (doc && doc.productPage && doc.productPage.commonAttributes && doc.productPage.commonAttributes.exposedAttributes) {
            const exposedAttrs = doc.productPage.commonAttributes.exposedAttributes;
            const attrKey = JSON.stringify(exposedAttrs.map(attr => ({ name: attr.attributeName, value: attr.attributeValue })));

            console.log(`📋 옵션 ${index} 노출속성:`, exposedAttrs.map(attr => `${attr.attributeName}:${attr.attributeValue}`).join(', '));

            if (exposedAttributesMap.has(attrKey)) {
              const existingIndex = exposedAttributesMap.get(attrKey);
              let duplicateGroup = duplicateGroups.find(group => group.includes(existingIndex));
              if (!duplicateGroup) {
                duplicateGroup = [existingIndex];
                duplicateGroups.push(duplicateGroup);
              }
              duplicateGroup.push(index);
              console.log(`⚠️ 중복 발견: 옵션 ${existingIndex}와 옵션 ${index} 노출속성 동일`);
            } else {
              exposedAttributesMap.set(attrKey, index);
            }
          }
        });
      }

      console.log(`📊 중복 검사 결과: ${duplicateGroups.length}개 중복 그룹 발견`);
      if (duplicateGroups.length > 0) {
        duplicateGroups.forEach((group, groupIndex) => {
          console.log(`📋 중복 그룹 ${groupIndex + 1}: 옵션 [${group.join(', ')}]`);
        });
      }

      if (duplicateGroups.length === 0) {
        // 중복 없음 → completed 처리
        console.log(`✅ 상품 ${product._id || product.registrationId}: 노출속성 중복 없음 → completed 처리`);
        await store.dispatch('updateProductPartial', {
          productId: product._id,
          updateFields: {
            'quotationStatus': 'completed',
            'updatedAt': Date.now()
          }
        });
      } else {
        // 중복 있음 → 재분리 필요
        console.log(`⚠️ 상품 ${product._id || product.registrationId}: 노출속성 중복 발견 → 재분리 진행`);
        await this.handleProductResplit(product);
      }

    } catch (error) {
      console.error(`❌ 상품 ${product._id || product.registrationId} 개별 검증 중 오류:`, error);
      // 오류 발생 시 completed로 처리
      try {
        await store.dispatch('updateProductPartial', {
          productId: product._id,
          updateFields: {
            'quotationStatus': 'completed',
            'updatedAt': Date.now()
          }
        });
      } catch (updateError) {
        console.error('❌ 오류 발생 후 completed 처리 실패:', updateError);
      }
    }
  },

  /**
   * 상품 재분리 처리
   * @param {Object} product - 재분리할 상품 객체
   * @returns {Promise<void>}
   */
  async handleProductResplit(product) {
    try {
      console.log(`🔄 상품 ${product._id || product.registrationId} 재분리 시작`);

      // 추가 속성 감지 및 분리 로직 실행 (기존 로직 재사용)
      // 여기서는 간단히 추가 속성 감지를 다시 수행
      const optionAnalysisData = [];

      if (product.options) {
        product.options.forEach((option, index) => {
          const doc = product.coupangDoc.jsonDocument[index];
          if (doc && doc.productPage && doc.productPage.commonAttributes && doc.productPage.commonAttributes.exposedAttributes) {
            optionAnalysisData.push({
              optionNumber: option.optionNumber,
              optionName: option.korName,
              exposedAttributes: doc.productPage.commonAttributes.exposedAttributes
            });
          }
        });
      }

      if (optionAnalysisData.length > 0) {
        // 현재 상품의 중복된 노출속성 추출 (재분리용)
        const duplicateGroups = this.getDuplicateGroupsForProduct(product);
        const duplicatedExposedAttributes = this.extractDuplicatedExposedAttributes(duplicateGroups, product);
        console.log('📋 재분리용 중복된 노출속성:', duplicatedExposedAttributes);

        // 🔥 간단한 텍스트 형식으로 응답 길이 단축
        console.log('📊 간단한 텍스트 형식으로 추가 속성 감지 시작 (재분리용)');
        let additionalAttrResult = await this.detectAdditionalAttributesSimpleFormat(product, duplicatedExposedAttributes, optionAnalysisData);

        // 🔥 모든 옵션이 동일한 추가 속성을 가지는지 확인 후 재시도
        if (additionalAttrResult && additionalAttrResult.success && additionalAttrResult.attributes && additionalAttrResult.attributes.length > 1) {
          const firstAttrKey = JSON.stringify(additionalAttrResult.attributes[0].attributes);
          const allSame = additionalAttrResult.attributes.every(attrData =>
            JSON.stringify(attrData.attributes) === firstAttrKey
          );

          if (allSame) {
            console.log('⚠️ 모든 옵션이 동일한 추가 속성을 가지고 있습니다. 프롬프트를 개선하여 재시도합니다.');
            additionalAttrResult = await this.detectAdditionalAttributesSimpleFormat(product, duplicatedExposedAttributes, optionAnalysisData, true);
          }
        }

        if (additionalAttrResult && additionalAttrResult.success && additionalAttrResult.attributes && additionalAttrResult.attributes.length > 0) {
          // 상품 분리 실행
          console.log(`🔄 상품 ${product._id || product.registrationId} 분리 실행: ${additionalAttrResult.attributes.length}개 추가 속성`);
          const splitResult = await this.splitProductByAdditionalAttributes(product, additionalAttrResult.attributes);

          if (splitResult.success) {
            console.log(`✅ 상품 ${product._id || product.registrationId} 재분리 완료`);

            // 재분리된 상품들에 대해 다시 개별 검증 수행
            if (splitResult.newProducts && splitResult.newProducts.length > 0) {
              await this.processExposedAttributesForSplitProducts(product, splitResult.newProducts);
            } else {
              // 새 상품이 없으면 원본 상품만 completed 처리
              console.log(`ℹ️ 상품 ${product._id || product.registrationId}: 새 상품 없음 → completed 처리`);
              await store.dispatch('updateProductPartial', {
                productId: product._id,
                updateFields: {
                  'quotationStatus': 'completed',
                  'updatedAt': Date.now()
                }
              });
            }
          } else {
            console.log(`❌ 상품 ${product._id || product.registrationId} 재분리 실패 → completed 처리`);
            await store.dispatch('updateProductPartial', {
              productId: product._id,
              updateFields: {
                'quotationStatus': 'completed',
                'updatedAt': Date.now(),
                'processError': {
                  message: '재분리 실패',
                  timestamp: Date.now(),
                  error: splitResult.error
                }
              }
            });
          }
        } else {
          console.log(`ℹ️ 상품 ${product._id || product.registrationId}: 추가 속성 감지되지 않음 → completed 처리`);
          await store.dispatch('updateProductPartial', {
            productId: product._id,
            updateFields: {
              'quotationStatus': 'completed',
              'updatedAt': Date.now(),
              'processError': additionalAttrResult.error ? {
                message: '추가 속성 감지 실패',
                timestamp: Date.now(),
                error: additionalAttrResult.error
              } : null
            }
          });
        }
      } else {
        console.log(`ℹ️ 상품 ${product._id || product.registrationId}: 분석할 옵션 데이터 없음 → completed 처리`);
        await store.dispatch('updateProductPartial', {
          productId: product._id,
          updateFields: {
            'quotationStatus': 'completed',
            'updatedAt': Date.now()
          }
        });
      }

    } catch (error) {
      console.error(`❌ 상품 ${product._id || product.registrationId} 재분리 중 오류:`, error);
      // 오류 발생 시 completed로 처리
      await store.dispatch('updateProductPartial', {
        productId: product._id,
        updateFields: {
          'quotationStatus': 'completed',
          'updatedAt': Date.now()
        }
      });
    }
  },

  /**
   * 실제로 중복된 노출속성만 추출
   * @param {Array} duplicateGroups - 중복 그룹 배열
   * @param {Object} product - 상품 객체
   * @returns {Array} 중복된 노출속성명 배열
   */
  extractDuplicatedExposedAttributes(duplicateGroups, product) {
    const duplicatedAttributeNames = new Set();

    duplicateGroups.forEach(group => {
      if (group.length > 0) {
        // 첫 번째 옵션의 노출속성을 기준으로 중복된 속성 확인
        const firstOptionIndex = group[0];
        const firstDoc = product.coupangDoc?.jsonDocument?.[firstOptionIndex];

        if (firstDoc?.productPage?.commonAttributes?.exposedAttributes) {
          firstDoc.productPage.commonAttributes.exposedAttributes.forEach(attr => {
            // 이 속성이 그룹 내 다른 옵션들과도 동일한지 확인
            const isAttributeDuplicated = group.every(optionIndex => {
              const doc = product.coupangDoc.jsonDocument[optionIndex];
              if (!doc?.productPage?.commonAttributes?.exposedAttributes) return false;

              return doc.productPage.commonAttributes.exposedAttributes.some(otherAttr =>
                otherAttr.attributeName === attr.attributeName &&
                otherAttr.attributeValue === attr.attributeValue
              );
            });

            if (isAttributeDuplicated) {
              duplicatedAttributeNames.add(attr.attributeName);
            }
          });
        }
      }
    });

    const result = Array.from(duplicatedAttributeNames);
    console.log(`📊 중복된 노출속성 추출 결과: [${result.join(', ')}]`);
    return result;
  },

  /**
   * 상품의 중복 그룹을 가져오는 함수 (재분리용)
   * @param {Object} product - 상품 객체
   * @returns {Array} 중복 그룹 배열
   */
  getDuplicateGroupsForProduct(product) {
    const duplicateGroups = [];
    if (this.checkCoupangDocExists(product) && product.coupangDoc.jsonDocument) {
      const exposedAttributesMap = new Map();

      product.coupangDoc.jsonDocument.forEach((doc, index) => {
        if (doc && doc.productPage && doc.productPage.commonAttributes && doc.productPage.commonAttributes.exposedAttributes) {
          const exposedAttrs = doc.productPage.commonAttributes.exposedAttributes;
          const attrKey = JSON.stringify(exposedAttrs.map(attr => ({ name: attr.attributeName, value: attr.attributeValue })));

          if (exposedAttributesMap.has(attrKey)) {
            const existingIndex = exposedAttributesMap.get(attrKey);
            let duplicateGroup = duplicateGroups.find(group => group.includes(existingIndex));
            if (!duplicateGroup) {
              duplicateGroup = [existingIndex];
              duplicateGroups.push(duplicateGroup);
            }
            duplicateGroup.push(index);
          } else {
            exposedAttributesMap.set(attrKey, index);
          }
        }
      });
    }
    return duplicateGroups;
  },

  /**
   * 간단한 텍스트 형식으로 추가 속성 감지 (응답 길이 단축)
   * @param {Object} product - 상품 객체
   * @param {Array} duplicatedExposedAttributes - 중복된 노출속성
   * @param {Array} optionAnalysisData - 옵션 분석 데이터
   * @param {Boolean} isRetry - 재시도 여부 (기본값: false)
   * @returns {Promise<Object>} 추가 속성 감지 결과
   */
  async detectAdditionalAttributesSimpleFormat(product, duplicatedExposedAttributes, optionAnalysisData, isRetry = false) {
    try {
      console.log(`📊 간단한 텍스트 형식으로 추가 속성 감지: ${optionAnalysisData.length}개 옵션${isRetry ? ' (재시도)' : ''}`);

      // 옵션 정보를 간단하게 포맷팅
      const simpleOptionList = optionAnalysisData.map(option =>
        `${option.optionNumber}: ${option.optionName}`
      ).join('\n');

      console.log('📋 AI에게 전송할 옵션 목록 (간단 형식):', simpleOptionList.substring(0, 200) + '...');

      // 🔥 재시도 시 프롬프트 개선
      const prompt = isRetry ? `다음 옵션들에서 차별화 요소를 찾아주세요.

⚠️ 중요: 속성 "이름"이 아닌 각 옵션의 실제 "값"을 감지해야 합니다!

중복된 노출속성 (이미 구분됨): ${JSON.stringify(duplicatedExposedAttributes)}

옵션 목록:
${simpleOptionList}

작업: 각 옵션명에서 실제로 다른 부분을 찾아주세요.

예시 (잘못된 답변):
{
  "251103001001": ["재질", "색상"],  ❌ 속성 이름만 감지 (잘못됨!)
  "251103001002": ["재질", "색상"]   ❌ 모든 옵션이 동일 (잘못됨!)
}

예시 (올바른 답변):
{
  "251103001001": ["부직포", "회색", "3cm", "3m"],  ✅ 실제 값 감지 (올바름!)
  "251103001002": ["부직포", "회색", "5cm", "5m"],  ✅ 각 옵션마다 다름 (올바름!)
  "251103001009": ["PE", "회색", "3cm", "3m"]       ✅ 재질이 다름 (올바름!)
}

응답 형식 (단순한 JSON):
{
  "옵션번호": ["실제값1", "실제값2", "실제값3"],
  "옵션번호": ["실제값1", "실제값2"]
}

규칙:
- 각 옵션의 실제 차이점을 찾아주세요 (속성 이름이 아닌 값!)
- 차별화 요소는 간단하게 (한글 2-6글자)
- 중국어는 한글로 번역
- 일관된 용어 사용` : `다음 옵션들에서 차별화 요소를 찾아주세요.

중복된 노출속성 (이미 구분됨): ${JSON.stringify(duplicatedExposedAttributes)}

옵션 목록:
${simpleOptionList}

작업: 옵션명에서 노출속성 외의 차별화 요소를 찾아주세요.

응답 형식 (단순한 JSON):
{
  "옵션번호": ["차별화요소1", "차별화요소2"],
  "옵션번호": ["차별화요소"]
}

예시:
{
  "250622008001": ["손가락노출형", "프리미엄"],
  "250622008002": ["손가락없음", "스탠다드"],
  "250622008003": ["손가락노출형", "프리미엄"]
}

규칙:
- 차별화 요소는 간단하게 (한글 2-6글자)
- 중국어는 한글로 번역
- 일관된 용어 사용
- 차별화 요소가 1개면 배열에 1개만, 여러개면 배열에 여러개`;

      const result = await workflowService.requestExtensionWithPromise(
        {
          workType: 'DETECT_ADDITIONAL_ATTRIBUTES',
          productId: product.registrationId,
          requestArr: [{
            prompt: prompt  // 🔥 재시도 여부에 따라 다른 프롬프트 사용
          }],
          duplicatedExposedAttributes: duplicatedExposedAttributes,
          optionAnalysisData: optionAnalysisData
        },
        {
          timeout: 60000, // 60초 타임아웃
          maxRetries: 2   // 최대 2회 재시도
        }
      );

      // 간단한 텍스트 형식 파싱
      if (result && result.batchResults && result.batchResults.length > 0) {
        const batchResult = result.batchResults[0];
        if (batchResult.result || batchResult.processedText) {
          try {
            let responseText = batchResult.result || batchResult.processedText;
            console.log('📋 AI 응답 (원본):', responseText.substring(0, 300) + '...');

            // 단순한 JSON 형식 파싱
            let jsonData = null;
            if (typeof responseText === 'object') {
              jsonData = responseText;
            } else {
              // JSON 블록 제거 후 파싱
              let jsonText = responseText;
              jsonText = jsonText.replace(/```json\n|\n```/g, '');
              jsonText = jsonText.replace(/```\n|\n```/g, '');
              jsonText = jsonText.trim();
              jsonData = JSON.parse(jsonText);
            }

            // 단순한 JSON을 기존 형식으로 변환
            const additionalAttributes = [];
            if (jsonData && typeof jsonData === 'object') {
              for (const [optionNumber, attributeValues] of Object.entries(jsonData)) {
                if (Array.isArray(attributeValues)) {
                  additionalAttributes.push({
                    optionNumber: optionNumber,
                    attributes: attributeValues.map(value => ({ value: value }))
                  });
                }
              }
            }

            console.log(`✅ JSON 파싱 완료: ${additionalAttributes.length}개 추가 속성 감지`);

            if (additionalAttributes.length > 0) {
              return {
                success: true,
                attributes: additionalAttributes
              };
            } else {
              return {
                success: false,
                error: '추가 속성이 감지되지 않았습니다.'
              };
            }

          } catch (parseError) {
            console.error('❌ 텍스트 파싱 오류:', parseError);
            console.log('파싱 실패한 응답:', batchResult.processedText?.substring(0, 200));
            return {
              success: false,
              error: '응답 파싱 실패: ' + parseError.message
            };
          }
        }
      }

      return {
        success: false,
        error: 'AI 응답이 없습니다.'
      };

    } catch (error) {
      console.error('❌ 간단한 텍스트 형식 추가 속성 감지 중 오류:', error);
      return {
        success: false,
        error: error.message || '추가 속성 감지 중 오류가 발생했습니다.'
      };
    }
  },

  /**
   * 중복 그룹별로 개별 추가 속성 감지
   * @param {Object} product - 상품 객체
   * @param {Array} duplicatedExposedAttributes - 중복된 노출속성
   * @param {Array} optionAnalysisData - 옵션 분석 데이터
   * @returns {Promise<Object>} 추가 속성 감지 결과
   */
  async detectAdditionalAttributesByGroups(product, duplicatedExposedAttributes, optionAnalysisData) {
    try {
      console.log(`📊 중복 그룹별 개별 처리: ${optionAnalysisData.length}개 옵션`);

      // 옵션을 최대 5개씩 그룹으로 나누기 (응답 길이 제한 방지)
      const maxOptionsPerGroup = 5;
      const optionGroups = [];

      for (let i = 0; i < optionAnalysisData.length; i += maxOptionsPerGroup) {
        const group = optionAnalysisData.slice(i, i + maxOptionsPerGroup);
        optionGroups.push(group);
      }

      console.log(`📋 ${optionGroups.length}개 그룹으로 분할 (그룹당 최대 ${maxOptionsPerGroup}개 옵션)`);

      const allAdditionalAttributes = [];

      // 각 그룹별로 개별 처리
      for (let groupIndex = 0; groupIndex < optionGroups.length; groupIndex++) {
        const group = optionGroups[groupIndex];
        console.log(`🔍 그룹 ${groupIndex + 1}/${optionGroups.length} 처리 시작 (${group.length}개 옵션)`);

        try {
          const groupResult = await workflowService.requestExtensionWithPromise(
            {
              workType: 'DETECT_ADDITIONAL_ATTRIBUTES',
              productId: product.registrationId,
              requestArr: [{
                prompt: `Extract hierarchical additional attributes to differentiate duplicate options.

EXPOSED ATTRIBUTES: ${JSON.stringify(duplicatedExposedAttributes)}

OPTIONS TO ANALYZE (${group.length} options):
${this.formatDuplicateGroupsForPrompt(group)}

TASK: Find differentiating elements in korName beyond exposed attributes.

LEVELS:
1. Type: 손가락 노출형/손가락 없음, 커버형/오픈형 etc.
2. Grade: 판매량 많음, 고품질, 프리미엄 etc.
3. Feature: 시원한, 스타일리쉬한, 유행 etc.

RULES:
- Translate Chinese (露指→손가락 노출형)
- Each option must be unique
- Consistent terms

OUTPUT:
{
  "additionalAttributes": [
    {
      "optionNumber": "250622008001",
      "attributes": [
        {"level": 1, "value": "손가락 노출형", "original": "손가락 노출형", "category": "type"}
      ]
    }
  ]
}`
              }],
              duplicatedExposedAttributes: duplicatedExposedAttributes,
              optionAnalysisData: group
            },
            {
              timeout: 30000, // 30초 타임아웃 (그룹별로 짧게)
              maxRetries: 1   // 최대 1회 재시도
            }
          );

          // 그룹 결과 파싱
          if (groupResult && groupResult.batchResults && groupResult.batchResults.length > 0) {
            const batchResult = groupResult.batchResults[0];
            if (batchResult.result || batchResult.processedText) {
              try {
                let parsedResult = null;
                if (batchResult.result) {
                  parsedResult = batchResult.result;
                } else if (batchResult.processedText) {
                  // JSON 블록 제거 후 파싱
                  let jsonText = batchResult.processedText;
                  jsonText = jsonText.replace(/```json\n|\n```/g, '');
                  jsonText = jsonText.replace(/```\n|\n```/g, '');
                  jsonText = jsonText.trim();
                  parsedResult = JSON.parse(jsonText);
                }

                if (parsedResult && parsedResult.additionalAttributes) {
                  allAdditionalAttributes.push(...parsedResult.additionalAttributes);
                  console.log(`✅ 그룹 ${groupIndex + 1} 처리 완료: ${parsedResult.additionalAttributes.length}개 속성 감지`);
                } else {
                  console.log(`⚠️ 그룹 ${groupIndex + 1}: 추가 속성 감지되지 않음`);
                }
              } catch (parseError) {
                console.error(`❌ 그룹 ${groupIndex + 1} 결과 파싱 오류:`, parseError);
                console.log('파싱 실패한 응답:', batchResult.processedText?.substring(0, 200));
              }
            }
          } else {
            console.log(`⚠️ 그룹 ${groupIndex + 1}: AI 응답 없음`);
          }

        } catch (groupError) {
          console.error(`❌ 그룹 ${groupIndex + 1} 처리 중 오류:`, groupError);
        }

        // 그룹 간 잠시 대기 (API 부하 방지)
        if (groupIndex < optionGroups.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }

      console.log(`📊 모든 그룹 처리 완료: 총 ${allAdditionalAttributes.length}개 추가 속성 감지`);

      if (allAdditionalAttributes.length > 0) {
        return {
          success: true,
          attributes: allAdditionalAttributes
        };
      } else {
        return {
          success: false,
          error: '추가 속성이 감지되지 않았습니다.'
        };
      }

    } catch (error) {
      console.error('❌ 중복 그룹별 추가 속성 감지 중 오류:', error);
      return {
        success: false,
        error: error.message || '추가 속성 감지 중 오류가 발생했습니다.'
      };
    }
  },

  /**
   * 분리를 마친 뒤 화면에 알리고 분리된 상품마다 노출속성·최종 검증을 돈다.
   * @param {Object} product - 원본 상품(그룹 1로 고쳐진 것)
   * @param {Object} splitResult - splitProductByGroups 결과
   * @returns {Promise<Object>} split_completed 결과
   */
  async finishSplit(product, splitResult) {
    // 분리된 상품들의 ID 반환 (원본 상품 포함)
    const splitResult_final = {
      status: 'split_completed',
      message: `상품이 ${splitResult.totalProducts}개로 분리되었습니다.`,
      originalProductId: product._id,
      splitProductIds: splitResult.productIds,
      splitDetails: splitResult.details,
      needsRefresh: true // 검색 새로고침 필요 플래그
    };

    // 상품 분리 완료 후 AIRocketReg에 분리된 상품들 전달
    console.log('🔍 상품 분리 완료 후 AIRocketReg에 분리된 상품들 전달');
    try {
      // 분리된 상품들의 데이터 수집
      const splitProducts = [];

      // 새로 생성된 상품들 추가
      if (splitResult.newProducts && splitResult.newProducts.length > 0) {
        splitProducts.push(...splitResult.newProducts);
      }

      // 원본 상품도 업데이트된 상태로 추가 (메모리에서 가져오기)
      splitProducts.unshift(product); // 원본 상품을 맨 앞에 추가

      console.log(`📊 전달할 상품 수: ${splitProducts.length}개`);

      // Vue 전역 이벤트 발생
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('product-split-completed', {
          detail: {
            originalProductId: product._id,
            splitProducts: splitProducts, // 분리된 상품들의 실제 데이터
            totalProducts: splitResult.totalProducts,
            message: `상품이 ${splitResult.totalProducts}개로 분리되었습니다.`
          }
        }));
        console.log('✅ 상품 분리 완료 이벤트 발생 (분리된 상품 데이터 포함)');
      }
    } catch (eventError) {
      console.error('❌ 이벤트 발생 실패:', eventError);
    }

    // 분리된 상품들에 대해 개별적으로 노출속성 검증 수행
    console.log('🔄 분리된 상품들에 대한 개별 노출속성 검증 시작');
    await this.processExposedAttributesForSplitProducts(product, splitResult.newProducts);

    // 분리해도 최종 검증은 돈다 — 여기서 바로 돌아가면 아래 최종 검증을 건너뛰어, 포장값이 빈 상품이
    // completed 로 남았다(2026-10-16 PR261016-9F5455 003001·007001 — 분리된 2건만 통과).
    splitResult_final.finalValidationErrors = await this.applyFinalValidationToSplitProducts([product, ...(splitResult.newProducts || [])]);

    return splitResult_final;
  },

  /**
   * 노출속성 고친 값을 메모리·서버에 반영한다.
   * @param {Object} product - 상품 객체
   * @param {Array} changes - [{ index, optionNumber, attributeName, from, to, korName }]
   * @param {string} label - 로그 머리말
   */
  async applyExposedChanges(product, allChanges, label) {
    // 사람이 견적서 화면에서 고친 칸은 원문 맞춤·겹침 정리도 안 바꾼다(보고 3320E1 · quotationUserEdits)
    const { apply: changes, kept } = keepUserEdits(allChanges, product.quotationUserEdits);
    kept.forEach(c => console.warn(`⚠️ ${label}: 옵션 ${c.optionNumber} ${c.attributeName} 은 사람이 고친 값 "${c.from}" 이라 "${c.to}" 로 안 바꿈`));
    if (!changes.length) return kept;
    const options = product.options;
    const jsonDocument = product.coupangDoc.jsonDocument;
    const updateFields = {};
    changes.forEach(c => {
      const attrs = jsonDocument[c.index].productPage.commonAttributes.exposedAttributes;
      const attr = attrs.find(x => x.attributeName === c.attributeName);
      if (attr) attr.attributeValue = c.to;
      if (c.korName && options[c.index].korName !== c.korName) {
        options[c.index].korName = c.korName;
        updateFields[`options.${c.index}.korName`] = c.korName;
      }
      updateFields[`coupangDoc.jsonDocument.${c.index}.productPage.commonAttributes.exposedAttributes`] = attrs;
      console.log(`🔧 ${label}: 옵션 ${c.optionNumber} ${c.attributeName} "${c.from}" → "${c.to}"`);
    });
    await store.dispatch('updateProductPartial', { productId: product._id, updateFields });
    return kept;
  },

  /**
   * 노출속성을 1688 원문에 맞춘다 — 원문 색이 다르면 번역 색상도 다르게, 원문에 없는 차이(사이즈 등)는 하나로.
   * 겹침 검사 전에 돈다(PR261016-0975BF: 军绿色·卡其色 둘 다 「카키」, 사이즈만 FREE/One Size 로 갈려 겹침이 숨음).
   * @param {Object} product - 상품 객체
   */
  async alignExposedAttributesToSource(product) {
    const options = product.options || [];
    const jsonDocument = product.coupangDoc?.jsonDocument;
    if (!Array.isArray(jsonDocument) || jsonDocument.length !== options.length || options.length < 2) return;
    const { changes } = alignExposedToSource(options, jsonDocument, product.rocketRegInfo?.schemaInfo?.exposedAttributes);
    await this.applyExposedChanges(product, changes, '노출속성 원문 맞춤');
  },

  /**
   * 노출속성이 겹치는 옵션 처리 — ① 값을 서로 다르게 고친다(1688 원문 옵션명) ② 못 고친 옵션만 둘째 상품으로.
   * @param {Object} product - 상품 객체(옵션·coupangDoc.jsonDocument 같은 순서)
   * @returns {Promise<{status: 'resolved'|'split'|'failed'|'skip', result?: Object, error?: string}>}
   */
  async resolveExposedDuplicates(product) {
    const options = product.options || [];
    const jsonDocument = product.coupangDoc?.jsonDocument;
    if (!Array.isArray(jsonDocument) || jsonDocument.length !== options.length || !options.length) {
      console.warn('⚠️ 옵션 수와 견적서 문서 수가 달라 노출속성 겹침 정리를 건너뜀', { options: options.length, docs: jsonDocument?.length });
      return { status: 'skip' };
    }

    const { changes, unresolved } = resolveDuplicateExposedValues(options, jsonDocument, product.rocketRegInfo?.schemaInfo?.exposedAttributes);
    const kept = await this.applyExposedChanges(product, changes, '노출속성 겹침 정리');

    // 사람이 고친 값을 지켜 겹침이 남았으면 «해결됨» 이 아니다 — 아래 분리가 실제 문서로 겹친 옵션만 나눈다
    if (!unresolved.length && !kept.length) return { status: 'resolved' };

    // 값을 못 고친 옵션만 다음 상품으로(원본에 나머지 전부)
    const optionGroups = buildMinimalSplitGroups(options, jsonDocument);
    console.log(`📊 노출속성을 못 고친 옵션 분리: ${optionGroups.map(g => g.options.length).join(' + ')}`);
    const splitResult = await this.splitProductByGroups(product, optionGroups);
    if (!splitResult.success) {
      logServiceError('quotationService', 'resolveExposedDuplicates', new Error(splitResult.error || '상품 분리 실패'), {
        errorType: ERROR_TYPES.PROCESSING_ERROR,
        productId: product?._id || product?.registrationId,
        additional: { optionCount: options.length, failedOptionNumbers: splitResult.failedOptionNumbers }
      });
      return { status: 'failed', error: splitResult.error || '상품 분리 실패' };
    }
    return { status: 'split', result: await this.finishSplit(product, splitResult) };
  },

  /**
   * 정해진 그룹대로 상품을 나눈다 — 그룹 1 = 원본 상품, 나머지 = 새 상품.
   * 모든 옵션이 정확히 한 그룹에 있어야 시작하고, 새 상품 저장이 하나라도 실패하면 만든 것을 지우고
   * 원본은 손대지 않은 채 실패로 돌려준다(옵션이 조용히 사라지지 않게).
   * @param {Object} product - 원본 상품 객체
   * @param {Array} optionGroups - [{ additionalAttributeKey, additionalAttributes, options }]
   * @returns {Promise<Object>} 분리 결과
   */
  async splitProductByGroups(product, optionGroups) {
    const originalProduct = JSON.parse(JSON.stringify(product));
    const coverage = assertSplitCoverage(originalProduct.options, optionGroups);
    if (!coverage.ok) {
      const error = `상품 분리 중단 — 옵션이 맞지 않습니다(빠짐 ${coverage.missing.length}·중복 ${coverage.duplicated.length}·없는 옵션 ${coverage.extra.length})`;
      console.error('❌ ' + error, coverage);
      return { success: false, error, failedOptionNumbers: [...coverage.missing, ...coverage.duplicated, ...coverage.extra] };
    }

    console.log(`📊 옵션 그룹화 완료: ${optionGroups.length}개 그룹`);
    optionGroups.forEach((group, index) => {
      console.log(`그룹 ${index + 1}: ${group.options.map(opt => opt.optionNumber).join(', ')} (${group.additionalAttributeKey})`);
    });

    const created = [];
    const rollback = async () => {
      for (const c of created) {
        if (c.reused) continue;   // 앞판 조각(이번에 안 만든 것)은 지우지 않는다
        try {
          await apiService.deleteRocketProduct({ _id: c.productId });
        } catch (e) {
          console.error(`❌ 분리 되돌리기 실패 — 새 상품 ${c.registrationId} 를 지우지 못함:`, e);
        }
      }
    };

    // 새 상품 먼저(원본 백업 데이터 사용)
    for (let i = 1; i < optionGroups.length; i++) {
      const group = optionGroups[i];
      console.log(`🔄 새 상품 생성 (그룹 ${i + 1}): ${group.additionalAttributeKey}`);
      const res = await this.createNewProductForSplit(originalProduct, group, i + 1);
      if (!res.success) {
        await rollback();
        const failedOptionNumbers = group.options.map(opt => opt.optionNumber);
        const error = `분리 상품 ${i + 1} 저장 실패(옵션 ${failedOptionNumbers.join(', ')}): ${res.error}`;
        console.error('❌ ' + error);
        return { success: false, error, failedOptionNumbers };
      }
      created.push({ group, ...res });
    }

    // 마지막에 원본 상품을 그룹 1로
    console.log(`🔄 원본 상품 수정 (그룹 1): ${optionGroups[0].additionalAttributeKey}`);
    const updateResult = await this.updateOriginalProductForSplit(product, optionGroups[0]);
    if (!updateResult.success) {
      await rollback();
      return { success: false, error: `원본 상품 수정 실패: ${updateResult.error}`, failedOptionNumbers: optionGroups[0].options.map(opt => opt.optionNumber) };
    }

    const details = [{
      type: 'original',
      productId: product._id,
      registrationId: product.registrationId,
      additionalAttribute: optionGroups[0].additionalAttributeKey,
      optionNumbers: optionGroups[0].options.map(opt => opt.optionNumber)
    }, ...created.map(c => ({
      type: 'new',
      productId: c.productId,
      registrationId: c.registrationId,
      additionalAttribute: c.group.additionalAttributeKey,
      optionNumbers: c.group.options.map(opt => opt.optionNumber)
    }))];

    console.log('✅ 모든 상품 분리 완료');
    return {
      success: true,
      totalProducts: optionGroups.length,
      productIds: details.map(d => d.productId),
      details,
      newProducts: created.map(c => c.newProduct).filter(Boolean)
    };
  },

  /**
   * 추가 속성을 기준으로 상품을 분리하는 함수
   * @param {Object} product - 원본 상품 객체
   * @param {Array} additionalAttributes - 추가 속성 배열 (옵션별)
   * @returns {Promise<Object>} 분리 결과
   */
  async splitProductByAdditionalAttributes(product, additionalAttributes) {
    try {
      console.log('🔄 상품 분리 시작:', {
        productId: product._id,
        registrationId: product.registrationId,
        optionCount: product.options?.length || 0,
        additionalAttributesCount: additionalAttributes?.length || 0
      });

      // 원본 상품 데이터 백업 (분리 과정에서 원본이 변경되기 때문)
      const originalProduct = JSON.parse(JSON.stringify(product));
      console.log('📋 원본 상품 백업 완료:', {
        optionCount: originalProduct.options?.length || 0,
        jsonDocumentCount: originalProduct.coupangDoc?.jsonDocument?.length || 0
      });

      // 1. 추가 속성을 기준으로 옵션들을 그룹화 (노출속성 중복 확인 포함)
      const optionGroups = this.groupOptionsByAdditionalAttributes(originalProduct.options, additionalAttributes, originalProduct);

      // AI 가 목록에 안 넣은 옵션은 어느 그룹에도 없다 → 겹치지 않는 첫 그룹(원본 상품)에 넣는다(PR261016-0975BF 옵션 3 누락).
      const placed = placeUnlistedOptions(optionGroups, originalProduct.options, originalProduct.coupangDoc?.jsonDocument);
      if (placed.length) console.warn(`⚠️ AI 목록에 없던 옵션 ${placed.length}개를 그룹에 넣음: ${placed.join(', ')}`);

      if (optionGroups.length <= 1) {
        console.log('ℹ️ 분리할 필요가 없습니다. (그룹이 1개 이하)');
        return { success: false, error: '분리할 필요가 없습니다.' };
      }

      return await this.splitProductByGroups(product, optionGroups);

    } catch (error) {
      console.error('❌ 상품 분리 중 오류:', error);
      return {
        success: false,
        error: error.message || '상품 분리 중 오류가 발생했습니다.'
      };
    }
  },

  /**
   * 추가 속성을 기준으로 옵션들을 그룹화 (계층적 형식 지원)
   * @param {Array} options - 옵션 배열
   * @param {Array} additionalAttributes - 추가 속성 배열
   * @param {Object} product - 상품 객체 (노출속성 중복 확인용)
   * @returns {Array} 그룹화된 옵션 배열
   */
  groupOptionsByAdditionalAttributes(options, additionalAttributes, product) {
    const groups = new Map();

    // 모든 옵션의 attributes가 빈 배열인지 확인
    const allAttributesEmpty = additionalAttributes.every(attrData =>
      !Array.isArray(attrData.attributes) || attrData.attributes.length === 0
    );

    // 모든 옵션이 동일한 추가 속성을 가지는지 확인 (AI가 제대로 감지하지 못한 경우)
    let allAttributesSame = false;
    if (!allAttributesEmpty && additionalAttributes.length > 1) {
      const firstAttrKey = JSON.stringify(additionalAttributes[0].attributes);
      allAttributesSame = additionalAttributes.every(attrData =>
        JSON.stringify(attrData.attributes) === firstAttrKey
      );
    }

    if (allAttributesEmpty || allAttributesSame) {
      if (allAttributesEmpty) {
        console.log('⚠️ AI가 추가 속성을 감지하지 못했습니다. 옵션명을 사용하여 각 옵션을 개별 상품으로 분리합니다.');
      } else {
        console.log('⚠️ 모든 옵션이 동일한 추가 속성을 가지고 있습니다. AI가 속성 값을 제대로 감지하지 못했으므로 옵션명을 사용하여 각 옵션을 개별 상품으로 분리합니다.');
        console.log(`📋 동일한 추가 속성: ${JSON.stringify(additionalAttributes[0].attributes)}`);
      }

      // 각 옵션을 개별 그룹으로 만들기 (옵션명을 상품명 뒤에 붙임)
      additionalAttributes.forEach(attrData => {
        const option = options.find(opt => opt.optionNumber === attrData.optionNumber);
        if (!option) return;

        // 옵션명을 추가 속성으로 사용
        const optionName = option.korName || option.name || '';

        // 각 옵션을 개별 그룹으로 (옵션번호를 키로 사용하여 중복 방지)
        groups.set(attrData.optionNumber, {
          additionalAttributeKey: optionName,  // 상품명 뒤에 붙을 텍스트
          additionalAttributes: [{ value: optionName }],
          options: [option]  // 각 그룹에 옵션 1개씩
        });
      });

      console.log(`📊 옵션명 기준 개별 그룹화 완료: ${groups.size}개 그룹 (각 옵션이 개별 상품으로 분리됨)`);
      return Array.from(groups.values());
    }

    // 기존 로직: AI가 감지한 추가 속성으로 그룹화
    additionalAttributes.forEach(attrData => {
      const option = options.find(opt => opt.optionNumber === attrData.optionNumber);
      if (!option) return;

      // 계층적 형식과 기존 형식 모두 지원
      let attributeKey, attributeText;

      if (Array.isArray(attrData.attributes) && attrData.attributes.length > 0) {
        // 첫 번째 요소가 객체인지 확인 (새로운 계층적 형식)
        if (typeof attrData.attributes[0] === 'object' && attrData.attributes[0].level) {
          // 새로운 계층적 형식 처리
          const sortedAttributes = [...attrData.attributes]
            .sort((a, b) => a.level - b.level) // 계층 순서대로 정렬
            .map(attr => `${attr.level}:${attr.value}`); // 계층:값 형태로 변환

          attributeKey = JSON.stringify(sortedAttributes);
          attributeText = attrData.attributes
            .sort((a, b) => a.level - b.level)
            .map(attr => attr.value)
            .join(' ');
        } else if (typeof attrData.attributes[0] === 'object' && attrData.attributes[0].value) {
          // 새로운 단순한 형식: [{value: "값1"}, {value: "값2"}]
          const values = attrData.attributes.map(attr => attr.value);
          const sortedValues = [...values].sort();
          attributeKey = JSON.stringify(sortedValues);
          attributeText = values.join(' ');
        } else {
          // 기존 형식 처리 (문자열 배열)
          const sortedAttributes = [...attrData.attributes].sort();
          attributeKey = JSON.stringify(sortedAttributes);
          attributeText = attrData.attributes.join(' ');
        }
      } else {
        // 빈 배열이거나 속성이 없는 경우 - 옵션명 사용하여 개별 그룹 생성
        const optionName = option.korName || option.name || '';
        // 옵션번호를 키로 사용하여 각 옵션이 개별 그룹이 되도록 함
        attributeKey = `옵션명:${attrData.optionNumber}`;
        attributeText = optionName;
      }

      // 🔥 노출속성 중복 확인을 위한 로직
      // 같은 추가 속성을 가진 그룹이 이미 있는지 확인
      let targetGroup = null;

      if (groups.has(attributeKey)) {
        // 기존 그룹이 있으면 노출속성 중복 확인
        const existingGroup = groups.get(attributeKey);

        // 현재 옵션의 노출속성 가져오기
        const currentOptionIndex = options.indexOf(option);
        const currentExposedAttrs = product?.coupangDoc?.jsonDocument?.[currentOptionIndex]?.productPage?.commonAttributes?.exposedAttributes;

        if (currentExposedAttrs) {
          // 그룹 내 다른 옵션들과 노출속성 중복 확인
          let hasDuplicate = false;

          for (const groupOption of existingGroup.options) {
            const groupOptionIndex = options.indexOf(groupOption);
            const groupExposedAttrs = product?.coupangDoc?.jsonDocument?.[groupOptionIndex]?.productPage?.commonAttributes?.exposedAttributes;

            if (groupExposedAttrs) {
              // 노출속성 비교
              const currentAttrKey = JSON.stringify(currentExposedAttrs.map(attr => ({ name: attr.attributeName, value: attr.attributeValue })));
              const groupAttrKey = JSON.stringify(groupExposedAttrs.map(attr => ({ name: attr.attributeName, value: attr.attributeValue })));

              if (currentAttrKey === groupAttrKey) {
                hasDuplicate = true;
                console.log(`⚠️ 노출속성 중복 발견: 옵션 ${option.optionNumber}와 옵션 ${groupOption.optionNumber}`);
                break;
              }
            }
          }

          if (!hasDuplicate) {
            // 중복 없으면 기존 그룹에 추가
            targetGroup = existingGroup;
          } else {
            // 중복 있으면 새 그룹 생성 (추가 속성은 같지만 노출속성 중복 방지)
            console.log(`📋 노출속성 중복 방지를 위해 새 그룹 생성: ${attributeText} (${option.optionNumber})`);
            // 🔥 전체 그룹 개수를 사용하여 고유한 키 생성
            const newGroupKey = `${attributeKey}_${groups.size}`;
            groups.set(newGroupKey, {
              additionalAttributeKey: attributeText,
              additionalAttributes: attrData.attributes && attrData.attributes.length > 0
                ? attrData.attributes
                : [{ value: attributeText }],
              options: []
            });
            targetGroup = groups.get(newGroupKey);
          }
        } else {
          // 노출속성 정보가 없으면 기존 그룹에 추가
          targetGroup = existingGroup;
        }
      } else {
        // 새 그룹 생성
        groups.set(attributeKey, {
          additionalAttributeKey: attributeText,
          additionalAttributes: attrData.attributes && attrData.attributes.length > 0
            ? attrData.attributes
            : [{ value: attributeText }],
          options: []
        });
        targetGroup = groups.get(attributeKey);
      }

      // 옵션을 타겟 그룹에 추가
      if (targetGroup) {
        targetGroup.options.push(option);
      }
    });

    return Array.from(groups.values());
  },

  /**
   * 원본 상품을 첫 번째 그룹에 맞게 수정
   * @param {Object} product - 원본 상품 객체
   * @param {Object} group - 첫 번째 그룹 정보
   * @returns {Promise<Object>} 수정 결과
   */
  async updateOriginalProductForSplit(product, group) {
    try {
      console.log('🔄 원본 상품 수정 시작');

      // 1. pricing.options 분리 (옵션 번호 재할당 전에 먼저 처리)
      let updatedPricingOptions = null;
      if (product.pricing && product.pricing.options && Array.isArray(product.pricing.options)) {
        // 원본 옵션 번호로 필터링
        updatedPricingOptions = product.pricing.options.filter(pricingOption => {
          return group.options.some(option => option.optionNumber === pricingOption.optionNumber);
        });

        console.log(`📊 pricing.options 분리 (재할당 전): ${product.pricing.options.length} → ${updatedPricingOptions.length}`);
      }

      // 2. 옵션 배열을 해당 그룹의 옵션들로만 제한하고 옵션 번호 재할당
      const filteredOptions = group.options.map((option, index) => ({
        ...option,
        // 옵션 번호를 순차적으로 재할당 (1부터 시작)
        optionNumber: `${product.registrationId}${String(index + 1).padStart(3, '0')}`,
        quotationStatus: 'completed' // 옵션 상태를 completed로 설정
      }));

      // 3. pricing.options의 optionNumber도 새로 할당된 번호로 업데이트
      if (updatedPricingOptions) {
        updatedPricingOptions = updatedPricingOptions.map((pricingOption, index) => {
          const newOptionNumber = `${product.registrationId}${String(index + 1).padStart(3, '0')}`;

          // SEO 옵션명으로 optionName 업데이트
          let updatedOptionName = pricingOption.optionName;
          const matchingFilteredOption = filteredOptions[index]; // 같은 인덱스의 옵션
          if (matchingFilteredOption && product.seo && product.seo.options) {
            const seoOption = product.seo.options.find(seoOpt => seoOpt.optionNumber === group.options[index].optionNumber);
            if (seoOption && seoOption.korName) {
              updatedOptionName = seoOption.korName;
            }
          }

          return {
            ...pricingOption,
            optionNumber: newOptionNumber,
            optionName: updatedOptionName
          };
        });

        console.log(`📊 pricing.options 옵션번호 재할당 완료: ${updatedPricingOptions.length}개`);
      }

      // 4. coupangDoc.jsonDocument를 해당 그룹의 옵션들로만 제한
      const filteredJsonDocument = [];
      group.options.forEach((option) => {
        const originalIndex = product.options.findIndex(opt => opt.optionNumber === option.optionNumber);
        if (originalIndex >= 0 && product.coupangDoc?.jsonDocument?.[originalIndex]) {
          const docItem = JSON.parse(JSON.stringify(product.coupangDoc.jsonDocument[originalIndex]));

          // 상품명에 추가 속성 추가 - 기존 startPage.productName 사용
          const additionalAttrText = group.additionalAttributeKey;
          const baseProductName = docItem.startPage?.productName || product.coupangDoc?.productName || product.productName || '상품명';
          const newProductName = splitProductName(baseProductName, group.options, additionalAttrText);

          // startPage.productName 업데이트
          if (docItem.startPage) {
            const oldProductName = docItem.startPage.productName;
            docItem.startPage.productName = newProductName;
            console.log(`🔄 productName업데이트: "${oldProductName}" > "${newProductName}"`);
          }



          filteredJsonDocument.push(docItem);
        }
      });

      // 5. 상품명 업데이트 - 기존 coupangDoc.productName 사용
      const additionalAttrText = group.additionalAttributeKey;
      const baseProductName = product.coupangDoc?.productName || product.productName || '상품명';
      const newProductName = splitProductName(baseProductName, group.options, additionalAttrText);

      // pricing.options는 이미 위에서 처리됨

      // 6. 모든 상품명 필드에 추가속성 적용
      // 옵션 배열에 이미 quotationStatus가 포함되어 있으므로 개별 설정 불필요
      const updateFields = {
        options: filteredOptions, // 이미 quotationStatus: 'completed'가 포함된 옵션들
        'coupangDoc.jsonDocument': filteredJsonDocument,
        'coupangDoc.productName': newProductName,
        quotationStatus: 'completed', // 원본 상품도 completed로 설정
        updatedAt: Date.now()
      };

      // product.seo.productName 업데이트
      if (product.seo && product.seo.productName) {
        updateFields['seo.productName'] = newProductName;
        console.log(`🔄 seo.productName업데이트: "${product.seo.productName}" > "${newProductName}"`);
      }

      // product.productName 업데이트 (기본 상품명)
      if (product.productName) {
        updateFields['productName'] = newProductName;
        console.log(`🔄 productName업데이트: "${product.productName}" > "${newProductName}"`);
      }

      // product.korName 업데이트 (한글 상품명)
      if (product.korName) {
        updateFields['korName'] = newProductName;
        console.log(`🔄 korName업데이트: "${product.korName}" > "${newProductName}"`);
      }

      // product.productNameSeo 업데이트 (SEO 상품명)
      if (product.productNameSeo) {
        updateFields['productNameSeo'] = newProductName;
        console.log(`🔄 productNameSeo업데이트: "${product.productNameSeo}" > "${newProductName}"`);
      }

      // product.productNameTrans 업데이트 (영문 상품명)
      if (product.productNameTrans) {
        updateFields['productNameTrans'] = newProductName;
        console.log(`🔄 productNameTrans업데이트: "${product.productNameTrans}" > "${newProductName}"`);
      }

      // product.subject 업데이트 (제목)
      if (product.subject) {
        updateFields['subject'] = newProductName;
        console.log(`🔄 subject업데이트: "${product.subject}" > "${newProductName}"`);
      }

      // product.subjectTrans 업데이트 (번역된 제목)
      if (product.subjectTrans) {
        updateFields['subjectTrans'] = newProductName;
        console.log(`🔄 subjectTrans업데이트: "${product.subjectTrans}" > "${newProductName}"`);
      }

      // pricing.options가 있으면 추가
      if (updatedPricingOptions) {
        updateFields['pricing.options'] = updatedPricingOptions;
      }

      const oldCoupangDocProductName = product.coupangDoc?.productName;

      console.log(`🔄 coupangDoc.productName업데이트: "${oldCoupangDocProductName}" > "${newProductName}"`);

      console.log('📊 원본 상품 업데이트 필드:', {
        optionCount: filteredOptions.length,
        jsonDocumentCount: filteredJsonDocument.length,
        newProductName: newProductName
      });

      // 7. 서버에 업데이트
      if (Array.isArray(product.sourcingSkuIds)) updateFields.sourcingSkuIds = sourcingSkuIdsFor(product.sourcingSkuIds, filteredOptions, product.registrationId);
      await apiService.updateRocketProductPartial(product._id, updateFields);

      // 8. 메모리 상태 업데이트
      product.options = filteredOptions;
      if (updateFields.sourcingSkuIds) product.sourcingSkuIds = updateFields.sourcingSkuIds;
      product.quotationStatus = 'completed'; // 원본 상품 상태도 completed로 설정

      // 모든 옵션의 quotationStatus도 메모리에서 'completed'로 설정
      if (product.options && Array.isArray(product.options)) {
        product.options.forEach(option => {
          option.quotationStatus = 'completed';
        });
      }

      if (product.coupangDoc) {
        product.coupangDoc.jsonDocument = filteredJsonDocument;
        product.coupangDoc.productName = newProductName;
      }
      if (updatedPricingOptions && product.pricing) {
        product.pricing.options = updatedPricingOptions;
      }

      // 모든 상품명 필드 메모리 업데이트
      if (product.seo && product.seo.productName) {
        product.seo.productName = newProductName;
      }
      if (product.productName) {
        product.productName = newProductName;
      }
      if (product.korName) {
        product.korName = newProductName;
      }
      if (product.productNameSeo) {
        product.productNameSeo = newProductName;
      }
      if (product.productNameTrans) {
        product.productNameTrans = newProductName;
      }
      if (product.subject) {
        product.subject = newProductName;
      }
      if (product.subjectTrans) {
        product.subjectTrans = newProductName;
      }

      // 9. store 상태 업데이트
      store.commit('updateProductPartial', {
        productId: product._id,
        updateFields: updateFields
      });

      console.log('✅ 원본 상품 수정 완료');
      return { success: true };

    } catch (error) {
      console.error('❌ 원본 상품 수정 중 오류:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * 새 상품을 생성 (특정 그룹의 옵션들로)
   * @param {Object} originalProduct - 원본 상품 객체
   * @param {Object} group - 그룹 정보
   * @param {number} groupIndex - 그룹 인덱스
   * @returns {Promise<Object>} 생성 결과
   */
  async createNewProductForSplit(originalProduct, group, groupIndex) {
    try {
      console.log(`🔄 새 상품 생성 시작 (그룹 ${groupIndex})`);

      // 1. 새 등록번호 생성 — 서버 카운터로 받는다. 그룹 순서(_2, _3)로 붙이면 재분리 때 같은 번호가 또 나온다.
      const newRegistrationId = await apiService.issueSplitRegistrationId(originalProduct.registrationId);

      // 2. 새 상품 객체 생성 (원본 상품의 모든 데이터 복사)
      const newProduct = JSON.parse(JSON.stringify(originalProduct));

      // 3. 고유 필드들 수정
      delete newProduct._id; // MongoDB에서 새 ID 생성하도록
      newProduct.registrationId = newRegistrationId;
      // 어느 등록건에서 나뉘었나 — 바로 위 등록ID(통째 복사로 물려받은 값은 덮는다). listRegistrations splitFrom 열이 싣는다
      newProduct.splitFrom = originalProduct.registrationId;

      // 3-1. 쿠팡 전송 표식 초기화
      // 통째 복사라서 원본이 이미 전송된 건이면 registered·verified 가 그대로 따라왔다.
      // 그러면 **전송된 적이 없는 새 조각**이 전송됨으로 보여 삭제가 거절되고, 작업개시·등록전송
      // 대상에서도 빠진다. 새 조각은 아직 쿠팡에 아무것도 없으니 여기서 끊는다.
      // (coupangDoc.docId 는 전송 전에도 생기는 값이라 전송 표식이 아니다 — 아래 9번에서 일부러 유지한다.)
      newProduct.registered = false;
      newProduct.verified = false;

      // 4. pricing.options 분리 (옵션 번호 재할당 전에 먼저 처리)
      let newPricingOptions = null;
      if (originalProduct.pricing && originalProduct.pricing.options && Array.isArray(originalProduct.pricing.options)) {
        // 원본 옵션 번호로 필터링
        newPricingOptions = originalProduct.pricing.options.filter(pricingOption => {
          return group.options.some(option => option.optionNumber === pricingOption.optionNumber);
        });

        console.log(`📊 새상품 pricing.options 분리 (재할당 전): ${originalProduct.pricing.options.length} → ${newPricingOptions.length}`);
      }

      // 5. 옵션 배열을 해당 그룹의 옵션들로만 제한하고 옵션번호 재할당
      const filteredOptions = group.options.map((option, index) => ({
        ...option,
        optionNumber: `${newRegistrationId}${String(index + 1).padStart(3, '0')}`,
        quotationStatus: 'completed', // 옵션 상태를 completed로 설정
        // 옵션도 원본에서 펼쳐 오므로 전송 표식이 따라온다. 옵션번호가 새로 붙어
        // **쿠팡에 없는 SKU** 가 되는데 표식만 registered 로 남으면 화면·서버가 전송된 것으로 본다.
        // 값은 AIRocketReg.vue 가 되돌릴 때 쓰는 것과 같게 둔다(registrationStatus 는 빈 문자열).
        registered: false,
        verified: false,
        registrationStatus: ''
      }));

      // 6. pricing.options의 optionNumber도 새로 할당된 번호로 업데이트
      if (newPricingOptions) {
        newPricingOptions = newPricingOptions.map((pricingOption, index) => {
          const newOptionNumber = `${newRegistrationId}${String(index + 1).padStart(3, '0')}`;

          // SEO 옵션명으로 optionName 업데이트
          let updatedOptionName = pricingOption.optionName;
          const matchingFilteredOption = filteredOptions[index]; // 같은 인덱스의 옵션
          if (matchingFilteredOption && originalProduct.seo && originalProduct.seo.options) {
            const seoOption = originalProduct.seo.options.find(seoOpt => seoOpt.optionNumber === group.options[index].optionNumber);
            if (seoOption && seoOption.korName) {
              updatedOptionName = seoOption.korName;
            }
          }

          return {
            ...pricingOption,
            optionNumber: newOptionNumber,
            optionName: updatedOptionName
          };
        });

        console.log(`📊 새상품 pricing.options 옵션번호 재할당 완료: ${newPricingOptions.length}개`);
      }

      // 7. coupangDoc.jsonDocument를 해당 그룹의 옵션들로만 제한
      const filteredJsonDocument = [];
      group.options.forEach((option) => {
        const originalIndex = originalProduct.options.findIndex(opt => opt.optionNumber === option.optionNumber);
        console.log(`옵션 ${option.optionNumber} 찾기: originalIndex = ${originalIndex}`);

        if (originalIndex >= 0 && originalProduct.coupangDoc?.jsonDocument?.[originalIndex]) {
          const docItem = JSON.parse(JSON.stringify(originalProduct.coupangDoc.jsonDocument[originalIndex]));

          // 상품명에 추가 속성 추가 - 기존 startPage.productName 사용
          const additionalAttrText = group.additionalAttributeKey;
          const baseProductName = docItem.startPage?.productName || originalProduct.coupangDoc?.productName || originalProduct.productName || '상품명';
          const newProductName = splitProductName(baseProductName, group.options, additionalAttrText);

          // startPage.productName 업데이트
          if (docItem.startPage) {
            const oldProductName = docItem.startPage.productName;
            docItem.startPage.productName = newProductName;
            console.log(`🔄 새상품 productName업데이트: "${oldProductName}" > "${newProductName}"`);
          }



          filteredJsonDocument.push(docItem);
          console.log(`옵션 ${option.optionNumber}의 jsonDocument 추가됨`);
        } else {
          console.warn(`옵션 ${option.optionNumber}의 jsonDocument를 찾을 수 없음 (originalIndex: ${originalIndex})`);
        }
      });

      // 8. 상품명 업데이트 - 기존 coupangDoc.productName 사용
      const additionalAttrText = group.additionalAttributeKey;
      const baseProductName = originalProduct.coupangDoc?.productName || originalProduct.productName || '상품명';
      const newProductName = splitProductName(baseProductName, group.options, additionalAttrText);

      // pricing.options는 이미 위에서 처리됨

      // 9. 새 상품 데이터 설정
      newProduct.options = filteredOptions;
      if (Array.isArray(newProduct.sourcingSkuIds)) newProduct.sourcingSkuIds = sourcingSkuIdsFor(newProduct.sourcingSkuIds, filteredOptions, newProduct.registrationId);

      // 새 상품의 quotationStatus를 'completed'로 설정
      newProduct.quotationStatus = 'completed';

      // 모든 옵션의 quotationStatus도 'completed'로 설정
      if (newProduct.options && Array.isArray(newProduct.options)) {
        newProduct.options.forEach(option => {
          option.quotationStatus = 'completed';
        });
      }

      if (newProduct.coupangDoc) {
        const oldCoupangDocProductName = newProduct.coupangDoc.productName;
        newProduct.coupangDoc.jsonDocument = filteredJsonDocument;
        newProduct.coupangDoc.productName = newProductName;
        // docId는 원본 상품의 값을 그대로 유지 (temp_ 접두사 추가하지 않음)
        // newProduct.coupangDoc.docId는 이미 originalProduct에서 복사된 값이므로 별도 설정 불필요
        console.log(`🔄 새상품 coupangDoc.productName업데이트: "${oldCoupangDocProductName}" > "${newProductName}"`);
        console.log(`🔄 새상품 coupangDoc.docId 유지: ${newProduct.coupangDoc.docId}`);
      }
      if (newPricingOptions && newProduct.pricing) {
        newProduct.pricing.options = newPricingOptions;
      }

      // 새 상품의 모든 상품명 필드 업데이트
      if (newProduct.seo && newProduct.seo.productName) {
        const oldSeoProductName = newProduct.seo.productName;
        newProduct.seo.productName = newProductName;
        console.log(`🔄 새상품 seo.productName업데이트: "${oldSeoProductName}" > "${newProductName}"`);
      }
      if (newProduct.productName) {
        const oldProductName = newProduct.productName;
        newProduct.productName = newProductName;
        console.log(`🔄 새상품 productName업데이트: "${oldProductName}" > "${newProductName}"`);
      }
      if (newProduct.korName) {
        const oldKorName = newProduct.korName;
        newProduct.korName = newProductName;
        console.log(`🔄 새상품 korName업데이트: "${oldKorName}" > "${newProductName}"`);
      }
      if (newProduct.productNameSeo) {
        const oldProductNameSeo = newProduct.productNameSeo;
        newProduct.productNameSeo = newProductName;
        console.log(`🔄 새상품 productNameSeo업데이트: "${oldProductNameSeo}" > "${newProductName}"`);
      }

      console.log('📊 새 상품 데이터:', {
        registrationId: newRegistrationId,
        optionCount: filteredOptions.length,
        jsonDocumentCount: filteredJsonDocument.length,
        newProductName: newProductName
      });

      // 10. 서버에 새 상품 저장
      const createResult = await apiService.saveRocketProduct(newProduct);

      if (!createResult || createResult.res !== 'success') {
        throw new Error('새 상품 생성 실패: ' + (createResult?.message || '서버 응답이 올바르지 않습니다.'));
      }

      // 저장된 상품 정보 가져오기
      const savedProduct = createResult.savedItems && createResult.savedItems.length > 0
        ? createResult.savedItems[0]
        : newProduct;

      // 11. store에 새 상품 추가는 AIRocketReg.vue에서 처리하도록 변경
      // store.commit('UPDATE_ROCKET_PRODUCT', savedProduct); // 제거: 아직 products 배열에 없어서 오류 발생

      console.log(`✅ 새 상품 생성 완료: ${savedProduct._id || savedProduct.registrationId}`);
      // 서버가 같은 원본·같은 옵션 조각을 이미 갖고 있으면 새로 안 만들고 그것을 돌려준다(reused · splitguard-1)
      const reused = savedProduct.reused === true;
      if (reused) console.error(`[quotation] 나눔: ${newRegistrationId} 대신 앞판 조각 ${savedProduct.registrationId} 를 씀(같은 옵션 조각이 이미 있음)`);
      return {
        success: true,
        productId: savedProduct._id || savedProduct.registrationId,
        registrationId: reused ? savedProduct.registrationId : newRegistrationId,
        reused,
        newProduct: savedProduct // 새로 생성된 상품 데이터 반환
      };

    } catch (error) {
      console.error(`❌ 새 상품 생성 중 오류 (그룹 ${groupIndex}):`, error);
      return { success: false, error: error.message };
    }
  },

  /**
   * 스키마를 AI가 이해하기 쉬운 가이드 형태로 변환 (실제 스키마 구조 사용)
   * @param {Object} schema - 원본 스키마
   * @returns {Object} 변환된 가이드
   */
  convertSchemaToGuide(schema) {
    const guide = {
      exposedAttributes: [],
      unexposedAttributes: [],
      legalInfo: [],
      logisticsInfo: []
    };

    try {
      console.log('🔍 스키마 가이드 변환 시작');

      // 노출 속성 추출 (실제 스키마 구조 사용)
      if (schema.properties?.productPage?.properties?.commonAttributes?.properties?.exposedAttributes?.allOf) {
        const exposedAttributesSchema = schema.properties.productPage.properties.commonAttributes.properties.exposedAttributes;

        exposedAttributesSchema.allOf.forEach(config => {
          if (config.contains?.properties?.attributeName) {
            const attrName = config.contains.properties.attributeName;
            const attrValue = config.contains.properties.attributeValue;

            if (attrName.enum?.[0]) {
              const hasDropdown = attrValue.dropdown?.length > 0;

              guide.exposedAttributes.push({
                name: attrName.enum[0],
                title: attrName.title,
                mandatory: attrName.requirement === "필수",
                exposed: attrName.exposed,
                inputType: attrName.attributeInputType,
                dropdown: hasDropdown ? attrValue.dropdown : null,
                guide: attrName.inputGuide || '',
                hint: attrName.inputHint || '',
                examples: attrValue.examples || [],
                unit: attrName.attributeUnit || '',
                dataType: attrName.attributeDataType
              });
            }
          }
        });

        console.log(`✅ 노출 속성 ${guide.exposedAttributes.length}개 추출 완료`);
      }

      // 비노출 속성 추출
      if (schema.properties?.productPage?.properties?.unexposedAttributes?.allOf) {
        const unexposedAttributesSchema = schema.properties.productPage.properties.unexposedAttributes;

        unexposedAttributesSchema.allOf.forEach(config => {
          if (config.contains?.properties?.attributeName) {
            const attrName = config.contains.properties.attributeName;
            const attrValue = config.contains.properties.attributeValue;

            if (attrName.enum?.[0]) {
              const hasDropdown = attrValue.dropdown?.length > 0;

              guide.unexposedAttributes.push({
                name: attrName.enum[0],
                title: attrName.title,
                mandatory: attrName.requirement === "필수",
                exposed: attrName.exposed,
                inputType: attrName.attributeInputType,
                dropdown: hasDropdown ? attrValue.dropdown : null,
                guide: attrName.inputGuide || '',
                hint: attrName.inputHint || '',
                examples: attrValue.examples || [],
                unit: attrName.attributeUnit || '',
                dataType: attrName.attributeDataType
              });
            }
          }
        });

        console.log(`✅ 비노출 속성 ${guide.unexposedAttributes.length}개 추출 완료`);
      }

      // 법적 정보 추출 (고시 정보)
      if (schema.properties?.legalPage?.properties?.notices?.allOf) {
        const noticesSchema = schema.properties.legalPage.properties.notices;

        noticesSchema.allOf.forEach(config => {
          if (config.contains?.properties?.noticeItemName) {
            const noticeName = config.contains.properties.noticeItemName;
            const noticeValue = config.contains.properties.noticeItemValue;

            if (noticeName.enum?.[0]) {
              const hasDropdown = noticeValue.dropdown?.length > 0;

              guide.legalInfo.push({
                name: noticeName.enum[0],
                title: noticeName.title,
                dropdown: hasDropdown ? noticeValue.dropdown : null,
                examples: noticeValue.examples || [],
                guide: noticeName.inputGuide || '',
                description: noticeName.description || ''
              });
            }
          }
        });

        console.log(`✅ 법적 정보 ${guide.legalInfo.length}개 추출 완료`);
      }

      // 물류 정보 추출 (스키마에 logisticsPage가 required에 포함된 경우만)
      if (schema.required?.includes('logisticsPage') && schema.properties?.logisticsPage) {
        const logisticsSchema = schema.properties.logisticsPage;

        // skuUnitBoxDimension 확인
        if (logisticsSchema.required?.includes('skuUnitBoxDimension') && logisticsSchema.properties?.skuUnitBoxDimension) {
          const dimensionSchema = logisticsSchema.properties.skuUnitBoxDimension;
          guide.logisticsInfo.push({
            name: 'skuUnitBoxDimension',
            title: dimensionSchema.title,
            description: dimensionSchema.description,
            examples: dimensionSchema.examples || [],
            unit: 'mm',
            type: 'dimension'
          });
        }

        // skuUnitBoxWeight 확인
        if (logisticsSchema.required?.includes('skuUnitBoxWeight') && logisticsSchema.properties?.skuUnitBoxWeight) {
          const weightSchema = logisticsSchema.properties.skuUnitBoxWeight;
          guide.logisticsInfo.push({
            name: 'skuUnitBoxWeight',
            title: weightSchema.title,
            description: weightSchema.description,
            examples: weightSchema.examples || [],
            unit: 'g',
            type: 'weight'
          });
        }

        console.log(`✅ 물류 정보 ${guide.logisticsInfo.length}개 추출 완료`);
      }

      console.log('✅ 스키마 가이드 변환 완료:', {
        exposedAttributes: guide.exposedAttributes.length,
        unexposedAttributes: guide.unexposedAttributes.length,
        legalInfo: guide.legalInfo.length,
        logisticsInfo: guide.logisticsInfo.length
      });

    } catch (error) {
      console.error('❌ 스키마 가이드 변환 중 오류:', error);
      console.warn('⚠️ 빈 가이드 반환');
    }

    return guide;
  },

  /**
   * 1차: 스키마 분석 및 차별화 속성 분류
   * product.rocketRegInfo.schemaInfo를 기준으로 차별화 속성만 분석
   * @param {Object} product - 상품 객체
   * @param {Array} optionsToProcess - 처리할 옵션 목록
   * @returns {Promise<Object>} 분석 결과
   */
  async analyzeSchemaAndClassifyAttributes(product, optionsToProcess) {
    try {
      console.log('📋 차별화 속성 분석 시작');

      // product.rocketRegInfo.schemaInfo를 기준으로 스키마 정보 사용
      const schemaGuide = product.rocketRegInfo.schemaInfo;

      // 모든 옵션명 수집 (korName 사용, 이미 SEO된 이름)
      const optionNames = optionsToProcess.map(opt => opt.korName || opt.optionNumber);

      // AI에게 차별화 속성 분석 요청 (기존 QUOTATION workType 사용)
      const analysisRequest = {
        workType: 'QUOTATION',
        task: 'ANALYZE_SCHEMA_ATTRIBUTES',
        productId: product.registrationId,
        requestArr: [{
          prompt: `
당신은 옵션들이 서로 다른 특성을 갖는 차별화 속성을 분석하는 분석 전문가입니다.
오직 이 상품의 옵션명들 만을 기준으로 해서 옵션별로 어떤 게 서로 다른 지 (어떤게 차별화 되는지) 살펴보고, 어떤 속성들이 옵션별로 달라질 수 있는지 아래 주어진 속성들 안에서 판단해주세요.
예를 들어 옵션들의 "색상"이 다르고 사이즈가 같다면 "색상"이나 "색상계열" 같은 것들이 차별화 속성일 것이고, "사이즈"나 "크기" 같은 것은 차별화 속성이 아닙니다.
예를 들어 레드5cm,블랙5cm,레드6cm,블랙6cm 와 같은 옵션명이 주어졌다면 "색상"과 "색상계열", "사이즈", "크기"가 차별화 속성일 것입니다. (레드가 여러개가 동일하더라도 블랙도 있으므로 색상도 차별화 속성이 맞다는 점을 유의해주세요.)

## 상품 기본 정보
- 상품명: ${product.seo?.productName || product.productName}

## 옵션명들
- 옵션명들: ${optionNames}

## 속성 정보

### 노출 속성(exposedAttributes):
${schemaGuide.exposedAttributes.map(attr => `
**속성명 : ${attr.name}**
${attr.dropdown ? `- 필수선택(택1): ${JSON.stringify(attr.dropdown)}` : (attr.example ? `- 입력예시: ${attr.example}` : '')}
`).join('\n')}

### 비노출 속성(unexposedAttributes):
${schemaGuide.unexposedAttributes.map(attr => `
**속성명 : ${attr.name}**
${attr.dropdown ? `- 필수선택(택1): ${JSON.stringify(attr.dropdown)}` : (attr.example ? `- 입력예시: ${attr.example}` : '')}
`).join('\n')}

### 물류 정보(logisticsInfo):
${schemaGuide.logisticsInfo.map(attr => `
**속성명 : ${attr.name}**
- 가이드: ${attr.guide}
- 입력예시: ${attr.example}
`).join('\n')}

## 분석 기준
위 옵션명들을 보고 다음을 판단해주세요:

**차별화 속성(옵션끼리 서로 다른 특성)**: 옵션별로 다른 값을 가질 가능성이 있는 속성들만 찾아주세요.
   - 예: 옵션이 "빨강-L", "파랑-M" 형태라면 "색상", "색상계열", "사이즈", "크기" 등의 속성이 차별화 가능
   - 예: 옵션이 "스탠다드", "프리미엄" 형태라면 "등급", "재질" 등이 차별화 가능
   - 예: 옵션이 "100ml", "200ml" 형태라면 "용량", "크기" 등이 차별화 가능

## 중요 지침
한가지 옵션속성이 여러가지의 속성으로 표현될 수 있습니다. 예를 들어 옵션명들이 색상인 경우 차별화 속성은 색상과 관련된 모든 속성을 다 출력해야 함. (색상, 색상계열 등)
옵션명에 포함되지 않은 내용이나 옵션명에 동일하게 표현되는 내용을 별도의 추측을 통해 출력해서는 안됩니다. 옵션명 그대로 해석해야 합니다.
**잘못된 예시**
입력 옵션명들: 1. "빨강 9CM", 2. "분홍 9CM", 3. "화이트 9CM", 4. "브라운 9CM", 5. "블랙 9CM", 6. "오렌지 9CM", 7. "파랑 9CM"
잘못된 결과: "옵션명을 분석한 결과, 색상과 사이즈는 옵션별로 명확히 구분됩니다. 색상이 다르므로 색상계열 속성도 달라질 수 있습니다. 사이즈는 9CM로 동일하지만, 상품의 실제 부피와 무게는 포장 형태에 따라 다를 수 있으므로 물류 정보에 반영해야 합니다."
해석 : 위와 같은 상황에서는 사이즈가 동일하므로 사이즈와 관련된 속성은 출력하면 안됩니다.


## 응답 형식
\`\`\`json
{
  "exposedAttributes": ["색상", "사이즈"],
  "unexposedAttributes": ["색상계열", "크기"],
  "logisticsInfo": ["skuUnitBoxDimension"],
  "reasoning": "옵션명을 분석한 결과, 색상과 사이즈는 옵션별로 다르지만, 브랜드나 제조국은 모든 옵션이 동일할 것으로 판단됩니다."
}
\`\`\`
          `
        }]
      };

      console.log('🤖 AI 차별화 속성 분석 요청:', {
        workType: analysisRequest.workType,
        task: analysisRequest.task,
        productId: analysisRequest.productId,
        optionCount: optionNames.length,
        exposedAttributesCount: schemaGuide.exposedAttributes.length,
        unexposedAttributesCount: schemaGuide.unexposedAttributes.length,
        logisticsInfoCount: schemaGuide.logisticsInfo.length
      });

      const result = await workflowService.requestExtensionWithPromise(
        analysisRequest,
        {
          timeout: 60000,
          maxRetries: 2
        }
      );

      if (result.error) {
        throw new Error(result.error);
      }

      // 결과 검증 및 정리
      const analysis = this.validateSchemaAnalysis(result, schemaGuide);

      console.log('✅ 차별화 속성 분석 완료:', analysis);

      return {
        success: true,
        commonSchema: analysis.commonSchema,
        differentiatedSchema: analysis.differentiatedSchema,
        reasoning: analysis.reasoning,
        originalGuide: schemaGuide
      };

    } catch (error) {
      console.error('❌ 차별화 속성 분석 중 오류:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * 차별화 속성 분석 결과 검증
   * @param {Object} result - AI 분석 결과
   * @param {Object} originalGuide - 원본 가이드 (product.rocketRegInfo.schemaInfo)
   * @returns {Object} 검증된 분석 결과
   */
  validateSchemaAnalysis(result, originalGuide) {
    try {
      // AI 응답에서 JSON 파싱 (batchResults 구조 처리)
      let analysis;

      if (result.batchResults && result.batchResults.length > 0) {
        const batchResult = result.batchResults[0];
        if (batchResult.result) {
          analysis = batchResult.result;
        } else if (batchResult.processedText) {
          // JSON 블록 제거 후 파싱
          let jsonText = batchResult.processedText;
          jsonText = jsonText.replace(/```json\n|\n```/g, '');
          jsonText = jsonText.replace(/```\n|\n```/g, '');
          jsonText = jsonText.trim();
          analysis = JSON.parse(jsonText);
        }
      } else if (typeof result === 'string') {
        analysis = JSON.parse(result);
      } else {
        analysis = result;
      }

      console.log('🔍 파싱된 분석 결과:', analysis);

      // 기본 구조 검증 - AI 분석 결과에 따라 분류
      const validatedAnalysis = {
        commonSchema: {
          exposedAttributes: [],
          unexposedAttributes: [],
          legalInfo: originalGuide.legalInfo || [], // 법적 정보는 기본적으로 공통
          logisticsInfo: []
        },
        differentiatedSchema: {
          exposedAttributes: [],
          unexposedAttributes: [],
          legalInfo: [],
          logisticsInfo: []
        },
        reasoning: analysis.reasoning || '분석 결과 없음'
      };

      // AI가 분석한 차별화 속성에 따라 분류
      ['exposedAttributes', 'unexposedAttributes', 'logisticsInfo'].forEach(category => {
        const originalAttrs = originalGuide[category] || [];
        const diffAttrs = analysis[category] || [];

        console.log(`🔍 ${category} 차별화 속성 분류:`, {
          original: originalAttrs.length,
          differentiated: diffAttrs.length
        });

        // 차별화 속성 설정 (AI가 분석한 속성들)
        validatedAnalysis.differentiatedSchema[category] = originalAttrs.filter(attr =>
          diffAttrs.includes(attr.name)
        );

        // 공통 속성 설정 (AI가 차별화속성으로 분석하지 않은 나머지 속성들)
        validatedAnalysis.commonSchema[category] = originalAttrs.filter(attr =>
          !diffAttrs.includes(attr.name)
        );

        console.log(`✅ ${category} 분류 완료:`, {
          common: validatedAnalysis.commonSchema[category].length,
          differentiated: validatedAnalysis.differentiatedSchema[category].length
        });
      });

      console.log('✅ 스키마 분석 결과 검증 완료:', {
        commonExposed: validatedAnalysis.commonSchema.exposedAttributes.length,
        commonUnexposed: validatedAnalysis.commonSchema.unexposedAttributes.length,
        commonLegal: validatedAnalysis.commonSchema.legalInfo.length,
        commonLogistics: validatedAnalysis.commonSchema.logisticsInfo.length,
        diffExposed: validatedAnalysis.differentiatedSchema.exposedAttributes.length,
        diffUnexposed: validatedAnalysis.differentiatedSchema.unexposedAttributes.length,
        diffLegal: validatedAnalysis.differentiatedSchema.legalInfo.length,
        diffLogistics: validatedAnalysis.differentiatedSchema.logisticsInfo.length
      });

      return validatedAnalysis;

    } catch (error) {
      console.error('스키마 분석 결과 검증 중 오류:', error);
      // 실패 시 안전한 폴백: 법적 정보는 공통, 나머지는 차별화 속성으로 분류
      return {
        commonSchema: {
          exposedAttributes: [],
          unexposedAttributes: [],
          legalInfo: originalGuide.legalInfo || [], // 법적 정보는 기본적으로 공통
          logisticsInfo: []
        },
        differentiatedSchema: {
          exposedAttributes: originalGuide.exposedAttributes || [],
          unexposedAttributes: originalGuide.unexposedAttributes || [],
          legalInfo: [],
          logisticsInfo: originalGuide.logisticsInfo || [] // 분석 실패 시 물류 정보도 차별화로 처리
        },
        reasoning: '분석 실패로 인한 기본 분류 (법적 정보만 공통, 나머지는 차별화 속성으로 처리)'
      };
    }
  },

  /**
   * 2차: 공통 속성 일괄 생성 (분리된 방식)
   * @param {Object} product - 상품 객체
   * @param {Object} commonSchema - 공통 속성 스키마
   * @param {Object} attributeRequest - 속성 요청 데이터
   * @returns {Promise<Object>} 공통 속성 생성 결과
   */
  async generateCommonAttributes(product, commonSchema, attributeRequest) {
    try {
      console.log('🔄 공통 속성 생성 시작 (분리된 방식)');

      // 공통 속성이 없으면 빈 결과 반환
      const hasCommonAttrs = Object.values(commonSchema).some(attrs => attrs.length > 0);
      if (!hasCommonAttrs) {
        console.log('ℹ️ 공통 속성이 없습니다.');
        return {
          success: true,
          attributes: {
            exposedAttributes: [],
            unexposedAttributes: [],
            skuUnitBoxDimension: null,
            skuUnitBoxWeight: null,
            notices: []
          }
        };
      }

      // 1단계: 비노출 속성만 생성
      console.log('📋 1단계: 비노출 속성 생성');
      const unexposedResult = await this.generateCommonUnexposedAttributes(product, commonSchema, attributeRequest);

      // 2단계: 나머지 속성들 생성 (노출속성, 물류정보, 법적정보)
      console.log('📋 2단계: 나머지 공통 속성 생성');
      const otherResult = await this.generateCommonOtherAttributes(product, commonSchema, attributeRequest);

      // 결과 병합
      const mergedAttributes = {
        exposedAttributes: otherResult.attributes?.exposedAttributes || [],
        unexposedAttributes: unexposedResult.attributes?.unexposedAttributes || [],
        skuUnitBoxDimension: otherResult.attributes?.skuUnitBoxDimension || null,
        skuUnitBoxWeight: otherResult.attributes?.skuUnitBoxWeight || null,
        notices: otherResult.attributes?.notices || []
      };

      console.log('✅ 공통 속성 생성 완료 (병합):', {
        exposedCount: mergedAttributes.exposedAttributes.length,
        unexposedCount: mergedAttributes.unexposedAttributes.length,
        hasLogistics: !!(mergedAttributes.skuUnitBoxDimension || mergedAttributes.skuUnitBoxWeight),
        noticesCount: mergedAttributes.notices.length
      });

      return {
        success: true,
        attributes: mergedAttributes
      };

    } catch (error) {
      console.error('❌ 공통 속성 생성 중 오류:', error);
      if (error.mandatoryUnexposed) throw error;
      return {
        success: false,
        error: error.message,
        attributes: {
          exposedAttributes: [],
          unexposedAttributes: [],
          skuUnitBoxDimension: null,
          skuUnitBoxWeight: null,
          notices: []
        }
      };
    }
  },

  /**
   * 공통 비노출 속성만 생성
   * @param {Object} product - 상품 객체
   * @param {Object} commonSchema - 공통 속성 스키마
   * @param {Object} attributeRequest - 속성 요청 데이터
   * @returns {Promise<Object>} 비노출 속성 생성 결과
   */
  async generateCommonUnexposedAttributes(product, commonSchema, attributeRequest) {
    try {
      console.log('🔄 공통 비노출 속성 생성 시작');
      const unitMeta = buildUnitMeta(product.rocketRegInfo?.schemaString); // NUMBER 속성의 허용 단위(프롬프트용)

      // 설정 확인: 비노출속성 자동 생성이 비활성화된 경우
      const settings = store.state.settings[0] || {};
      const defaultSettings = settings.defaultSettings || {};
      const enableUnexposedAttributes = defaultSettings.enableUnexposedAttributes !== false; // 기본값 true

      console.log('🔍 [비노출속성 설정 확인]');
      console.log('- defaultSettings 객체:', defaultSettings);
      console.log('- defaultSettings.enableUnexposedAttributes 원본 값:', defaultSettings.enableUnexposedAttributes);
      console.log('- enableUnexposedAttributes 계산된 값:', enableUnexposedAttributes);

      if (!enableUnexposedAttributes) {
        // 꺼져 있어도 «필수» 비노출 속성은 만든다 — 빠지면 최종 검증(schemaValidator)이 늘 막는다. mandatory = requirement === '필수'
        const requiredOnly = (commonSchema.unexposedAttributes || []).filter(attr => attr.mandatory);
        if (requiredOnly.length === 0) {
          console.log('ℹ️ 비노출속성 자동 생성이 비활성화되어 있습니다. Rate Limiting 방지를 위해 건너뜁니다.');
          return {
            success: true,
            attributes: {
              unexposedAttributes: []
            },
            skipped: true // 건너뛰었음을 표시
          };
        }
        console.log(`ℹ️ 비노출속성 자동 생성이 비활성화되어 있어 필수 비노출 속성 ${requiredOnly.length}개만 생성합니다.`);
        commonSchema = { ...commonSchema, unexposedAttributes: requiredOnly };
      }

      console.log('✅ 비노출속성 자동 생성이 활성화되어 있습니다. 계속 진행합니다.');

      // 비노출 속성이 없으면 빈 결과 반환
      if (!commonSchema.unexposedAttributes || commonSchema.unexposedAttributes.length === 0) {
        console.log('ℹ️ 공통 비노출 속성이 없습니다.');
        return {
          success: true,
          attributes: {
            unexposedAttributes: []
          }
        };
      }

      // 상품명 준비 (번들 정보 포함)
      let promptProductName = product.seo?.productName || product.productName;
      const bundleNotes = bundleOptionService.quotationBundleNotes(product);   // 같음 → «N개 묶음» · 섞임 → 옵션마다 다름 · 0 → 빈 글
      const bundleNote = bundleNotes.note;

      if (bundleNote) {
        promptProductName = `${promptProductName}${bundleNotes.nameSuffix}`;
        console.log(`비노출 속성 프롬프트: 번들 정보 포함 - ${promptProductName}`);
      }

      // 비노출 속성만 포함된 요청 데이터 생성
      const unexposedRequest = {
        workType: 'QUOTATION',
        task: 'GET_COMMON_UNEXPOSED_ATTRIBUTES',
        productId: product.registrationId,
        requestArr: [{
          prompt: `
당신은 쿠팡 상품 등록을 위한 속성 생성 전문가입니다.
다음 상품의 **공통 비노출 속성들**을 생성해주세요. 이 속성들은 모든 옵션에 동일하게 적용됩니다.

## 상품 기본 정보
- 상품명: ${promptProductName}${bundleNote}${bannedWordsRule(product.productFeature)}

## 1688 상품 속성 정보 (참고용)
\`\`\`json
${JSON.stringify(attributeRequest.alibaba?.productAttribute || {}, null, 2)}
\`\`\`

## 남긴 옵션 원문 (사이즈·크기·규격·치수의 근거)
${optionSourceLines(product.options)}

## 생성할 공통 비노출 속성들
${commonSchema.unexposedAttributes.map(attr => `
**속성명 : ${attr.name}**
${attr.dropdown ? `- 필수선택(택1): ${JSON.stringify(attr.dropdown)}` : (attr.example ? `- 입력예시: ${attr.example}` : '')}
${attr.dropdown ? '' : unitHint(unitMeta[attr.name])}
`).join('\n')}

## 중요 지침
1. **필수선택(택1) 속성**: 반드시 제공된 선택지 중에서만 선택해야 함. (가능한 null을 지양하고 최대한 유사한 값을 선택하는 것을 지향)
2. **입력예시 속성**: 예시에 "2025년도", "10m", "1D" 등 단위가 있으면 반드시 해당 단위 사용.
3. **1688 정보 활용**: 1688 상품 속성 정보 (참고용) 에서 관련 속성값 참고
4. **물류 정보**: 1688 배송 정보 (물류 정보 참고용) 에서 포장 정보 참고
5. **모든 옵션 공통**: 모든 옵션에 동일하게 적용될 값들로 생성

## 가장 중요 지침
${unexposedSourceRule()}

## 응답 형식
\`\`\`json
{
  "unexposedAttributes": [
    ${commonSchema.unexposedAttributes.map(attr =>
      `{"attributeName": "${attr.name}", "attributeValue": "..."}`
    ).join(',\n    ')}
  ],
  "guessed": []
}
\`\`\`

⚠️ 중요: 위 JSON에서 "..." 부분만 실제 값으로 채워주세요. 속성명은 절대 변경하지 마세요.
          `
        }]
      };

      console.log('🤖 공통 비노출 속성 생성 요청:', {
        workType: unexposedRequest.workType,
        productId: unexposedRequest.productId,
        unexposedCount: commonSchema.unexposedAttributes?.length || 0
      });

      const result = await workflowService.requestExtensionWithPromise(
        unexposedRequest,
        {
          timeout: 60000,
          maxRetries: 2
        }
      );

      if (result.error) {
        throw new Error(result.error);
      }

      // 결과 검증
      const validatedResult = this.validateAttributeResponse(result, { unexposedAttributes: commonSchema.unexposedAttributes });
      const guessed = guessedNames(result, commonSchema.unexposedAttributes);
      if (guessed.length) console.warn(`⚠️ 공통 비노출 ${guessed.join('·')} = AI 추정(원문 근거 없음)`);
      // 금지 표현·원문과 다른 소재를 뺀 뒤 필수 비노출이 비면 여기서 멈춘다 — 선택지에 원문 소재가 없으면 그 까닭까지(CB70ED «주얼리 소재» 에 «유리» 없음)
      const factSrc = { productFeature: product.productFeature || '', attributes: product.attributes, options: product.options };
      const fact = dropUnsupportedAttributeValues(validatedResult.unexposedAttributes, factSrc);
      fact.removed.forEach(r => console.warn(`⚠️ 공통 비노출 ${r.attributeName}: 근거 없는·금지 표현 «${r.removed.join('·')}» 뺌 ("${r.from}" → ${r.to === null ? '빈 값' : `"${r.to}"`})`));
      validatedResult.unexposedAttributes = fact.attributes;
      const emptyMust = emptyMandatoryAttributes(validatedResult.unexposedAttributes, commonSchema.unexposedAttributes);
      if (emptyMust.length) throw mandatoryUnexposedError(emptyMust, { schemaAttrs: commonSchema.unexposedAttributes, src: factSrc });

      console.log('✅ 공통 비노출 속성 생성 완료:', {
        unexposedCount: validatedResult.unexposedAttributes?.length || 0
      });

      return {
        success: true,
        attributes: {
          unexposedAttributes: validatedResult.unexposedAttributes || []
        }
      };

    } catch (error) {
      console.error('❌ 공통 비노출 속성 생성 중 오류:', error);
      // 필수 비노출 멈춤은 삼키지 않는다 · 통째 실패(빈 배열)는 적용 단계의 필수 비노출 검사가 이름을 적어 멈춘다(applyAttributesToOption)
      if (error.mandatoryUnexposed) throw error;
      return {
        success: false,
        error: error.message,
        attributes: {
          unexposedAttributes: []
        }
      };
    }
  },

  /**
   * 포장 치수를 묻는 속성 요청에 대표이미지 한 장을 같이 보낸다 — 말아서·접어서 포장하는 상품인지 AI 가 보고 판단하게(packageEstimate.js).
   * 이미지가 거절된 오류(isImageRejectedError)일 때만 예전처럼 글만으로 다시 보낸다 — 이미지 탓에 견적서가 멈추지 않게. 시간 초과 등은 그대로 던진다.
   */
  // imageOpts.colorFromImage = 공통 손의 색 계열 노출속성도 대표이미지로 보게(attributeColor.js · PR261021-2650E7)
  async requestWithPackageImage(product, schema, request, options, imageOpts = {}) {
    const imageUrl = needsAttributeImage(schema, imageOpts) ? packageImageUrl(product) : null;
    if (!imageUrl) return workflowService.requestExtensionWithPromise(request, options);
    try {
      const withImage = { ...request, requestArr: request.requestArr.map((r, i) => (i === 0 ? { ...r, images: [imageUrl] } : r)) };
      return await workflowService.requestExtensionWithPromise(withImage, options);
    } catch (error) {
      if (!isImageRejectedError(error)) throw error;
      console.error('⚠️ 대표이미지가 거절됨 — 이미지 없이 다시 보냄:', error.message);
      return workflowService.requestExtensionWithPromise(request, options);
    }
  },

  /**
   * 공통 나머지 속성들 생성 (노출속성, 물류정보, 법적정보)
   * @param {Object} product - 상품 객체
   * @param {Object} commonSchema - 공통 속성 스키마
   * @param {Object} attributeRequest - 속성 요청 데이터
   * @returns {Promise<Object>} 나머지 속성 생성 결과
   */
  async generateCommonOtherAttributes(product, commonSchema, attributeRequest) {
    try {
      console.log('🔄 공통 나머지 속성 생성 시작');
      const unitMeta = buildUnitMeta(product.rocketRegInfo?.schemaString); // NUMBER 속성의 허용 단위(프롬프트용)
      const colorAttrs = (commonSchema.exposedAttributes || []).filter(attr => isColorAttr(attr.name)); // 색 계열 = 출처 차례 지침 + 대표이미지

      // 나머지 속성이 없으면 빈 결과 반환
      const hasOtherAttrs = (commonSchema.exposedAttributes?.length > 0) ||
                           (commonSchema.logisticsInfo?.length > 0) ||
                           (commonSchema.legalInfo?.length > 0);

      if (!hasOtherAttrs) {
        console.log('ℹ️ 공통 나머지 속성이 없습니다.');
        return {
          success: true,
          attributes: {
            exposedAttributes: [],
            skuUnitBoxDimension: null,
            skuUnitBoxWeight: null,
            notices: []
          }
        };
      }

      // 상품명 준비 (번들 정보 포함)
      let promptProductName = product.seo?.productName || product.productName;
      const bundleNotes = bundleOptionService.quotationBundleNotes(product);   // 같음 → «N개 묶음» · 섞임 → 옵션마다 다름 · 0 → 빈 글
      const bundleNote = bundleNotes.note;
      const logisticsBundleNote = bundleNotes.logisticsNote;

      if (bundleNote) {
        promptProductName = `${promptProductName}${bundleNotes.nameSuffix}`;
        console.log(`노출/물류 속성 프롬프트: 번들 정보 포함 - ${promptProductName}`);
      }

      // 나머지 속성들 요청 데이터 생성
      const otherRequest = {
        workType: 'QUOTATION',
        task: 'GET_COMMON_OTHER_ATTRIBUTES',
        productId: product.registrationId,
        requestArr: [{
          prompt: `
당신은 쿠팡 상품 등록을 위한 속성 생성 전문가입니다.
다음 상품의 **공통 속성들** (노출속성, 물류정보, 법적정보)을 생성해주세요.

## 상품 기본 정보
- 상품명: ${promptProductName}${bundleNote}${bannedWordsRule(product.productFeature)}
- 브랜드: ${product.brand}
- 제조사: ${product.manufacturer || product.brand}
- A/S 정보: ${attributeRequest.alibaba?.defaultSettings?.asInfo || '제조사 문의'}
- 원산지: ${attributeRequest.alibaba?.defaultSettings?.origin || '중국'}

## 1688 상품 속성 정보 (참고용)
\`\`\`json
${JSON.stringify(attributeRequest.alibaba?.productAttribute || {}, null, 2)}
\`\`\`

## 1688 배송 정보 (물류 정보 참고용)
\`\`\`json
${JSON.stringify(shippingInfoForAi(attributeRequest.alibaba?.productShippingInfo), null, 2)}
\`\`\`

## 남긴 옵션 원문 (사이즈·크기·규격·치수의 근거)
${optionSourceLines(product.options)}

## 생성할 공통 속성들
${commonSchema.exposedAttributes?.length > 0 ? `

### 노출 속성 (모든 옵션 공통):
${commonSchema.exposedAttributes.map(attr => `
**속성명 : ${attr.name}**
${attr.dropdown ? `- 필수선택(택1): ${JSON.stringify(attr.dropdown)}` : (attr.example ? `- 입력예시: ${attr.example} (예시에 단위가 포함된 경우 반드시 해당 단위를 사용해야 함)` : '')}
${attr.dropdown ? '' : unitHint(unitMeta[attr.name])}
`).join('\n')}

### 법적 정보 (모든 옵션 공통):
${commonSchema.legalInfo.map(notice => `
**${notice.name}**
`).join('\n')}

### 물류 정보 (모든 옵션 공통):${logisticsBundleNote}
${commonSchema.logisticsInfo.map(logistics => `
**${logistics.name}** (${logistics.title})
- 가이드: ${logistics.guide}
- 예시: ${logistics.example}
${packageEstimateGuide()}
`).join('\n')}` : ''}

## 중요 지침
1. **필수선택(택1) 속성**: 반드시 제공된 선택지 중에서만 선택해야 함. (가능한 null을 지양하고 최대한 유사한 값을 선택하는 것을 지향)
2. **입력예시 속성**: 예시에 "2025년도", "10m", "1D" 등 단위가 있으면 반드시 해당 단위 사용.
3. **1688 정보 활용**: 1688 상품 속성 정보 (참고용) 에서 관련 속성값 참고
4. **물류 정보**: 1688 배송 정보 (물류 정보 참고용) 에서 포장 정보 참고
5. **추정 허용**: 정확한 정보가 없어도 단위를 지켜서 합리적으로 추정
${colorSourceGuide(colorAttrs)}

## 🚨 가장 중요한 지침
1. **입력예시 속성**: 절대 null값으로 출력해서는 안된다. 상품과 가장 유사한 값으로 추측해도 된다.${colorAttrs.length ? ' (단 색 계열은 위 6번 차례대로 — 색은 추측하지 않는다)' : ''} 단 사이즈·크기·규격·치수는 «남긴 옵션 원문» 의 값을 그대로 쓰고, 원문에 규격이 없을 때만 추정한다.
2. **⚠️ 공통 속성 생성 원칙**:
   - 공통 속성은 **모든 옵션에 동일하게 적용되는 값**만 생성하세요
   - 만약 속성이 옵션별로 다를 수 있다면 **대표값 하나만** 선택하세요 (여러 값을 합치지 마세요)
   - ✅ 올바른 예: 색상 → "레드" (대표값 하나)
   - ❌ 잘못된 예: 색상 → "레드 / 블루 / 그린" (여러 값 합치면 안됨)
   - ✅ 올바른 예: 사이즈 → "28.5*18*20cm" (대표값 하나)
   - ❌ 잘못된 예: 사이즈 → "28.5*18*20cm / 28.5*18*36.5cm" (여러 값 합치면 안됨)

## 응답 형식
\`\`\`json
{${commonSchema.exposedAttributes?.length > 0 ? `${colorAttrs.length ? `
  ${COLOR_SOURCE_FIELD},` : ''}
  "exposedAttributes": [
    ${commonSchema.exposedAttributes.map(attr =>
      `{"attributeName": "${attr.name}", "attributeValue": "..."}`
    ).join(',\n    ')}
  ]${commonSchema.logisticsInfo?.length > 0 || commonSchema.legalInfo?.length > 0 ? ',' : ''}` : ''}${commonSchema.logisticsInfo?.some(info => info.name === 'skuUnitBoxWeight') ? `
  "skuUnitBoxWeight": "..."${commonSchema.logisticsInfo?.some(info => info.name === 'skuUnitBoxDimension') || commonSchema.legalInfo?.length > 0 ? ',' : ''}` : ''}${commonSchema.logisticsInfo?.some(info => info.name === 'skuUnitBoxDimension') ? `
  ${PACKAGE_FORM_FIELD},
  "skuUnitBoxDimension": "..."${commonSchema.legalInfo?.length > 0 ? ',' : ''}` : ''}${commonSchema.legalInfo?.length > 0 ? `
  "notices": [
    ${commonSchema.legalInfo.map(notice =>
      `{"noticeItemName": "${notice.name}", "noticeItemValue": "..."}`
    ).join(',\n    ')}
  ]` : ''}
}
\`\`\`

⚠️ 중요: 위 JSON에서 "..." 부분만 실제 값으로 채워주세요. 속성명은 절대 변경하지 마세요.
          `
        }]
      };

      console.log('🤖 공통 나머지 속성 생성 요청:', {
        workType: otherRequest.workType,
        productId: otherRequest.productId,
        exposedCount: commonSchema.exposedAttributes?.length || 0,
        legalCount: commonSchema.legalInfo?.length || 0,
        logisticsCount: commonSchema.logisticsInfo?.length || 0
      });

      const result = await this.requestWithPackageImage(
        product,
        commonSchema,
        otherRequest,
        {
          timeout: 90000,
          maxRetries: 2
        },
        { colorFromImage: colorAttrs.length > 0 }
      );

      if (result.error) {
        throw new Error(result.error);
      }

      // 결과 검증
      const validatedResult = this.validateAttributeResponse(result, commonSchema);

      console.log('✅ 공통 나머지 속성 생성 완료:', {
        exposedCount: validatedResult.exposedAttributes?.length || 0,
        hasLogistics: !!(validatedResult.skuUnitBoxDimension || validatedResult.skuUnitBoxWeight),
        noticesCount: validatedResult.notices?.length || 0
      });

      return {
        success: true,
        attributes: {
          exposedAttributes: validatedResult.exposedAttributes || [],
          skuUnitBoxDimension: validatedResult.skuUnitBoxDimension || null,
          skuUnitBoxWeight: validatedResult.skuUnitBoxWeight || null,
          notices: validatedResult.notices || []
        }
      };

    } catch (error) {
      console.error('❌ 공통 나머지 속성 생성 중 오류:', error);
      return {
        success: false,
        error: error.message,
        attributes: {
          exposedAttributes: [],
          skuUnitBoxDimension: null,
          skuUnitBoxWeight: null,
          notices: []
        }
      };
    }
  },

  /**
   * 속성 응답 검증 (기존 로직 재사용)
   * @param {Object} result - AI 응답 결과
   * @param {Object} schema - 스키마
   * @returns {Object} 검증된 속성
   */
  validateAttributeResponse(result, schema) {
    try {
      // AI 응답에서 JSON 파싱 (batchResults 구조 처리)
      let attributes;

      if (result.batchResults && result.batchResults.length > 0) {
        const batchResult = result.batchResults[0];
        if (batchResult.result) {
          attributes = batchResult.result;
        } else if (batchResult.processedText) {
          // JSON 블록 제거 후 파싱
          let jsonText = batchResult.processedText;
          jsonText = jsonText.replace(/```json\n|\n```/g, '');
          jsonText = jsonText.replace(/```\n|\n```/g, '');
          jsonText = jsonText.trim();
          attributes = JSON.parse(jsonText);
        }
      } else if (typeof result === 'string') {
        attributes = JSON.parse(result);
      } else {
        attributes = result;
      }

      console.log('🔍 파싱된 속성 결과:', {
        exposedCount: attributes.exposedAttributes?.length || 0,
        unexposedCount: attributes.unexposedAttributes?.length || 0,
        noticesCount: attributes.notices?.length || 0,
        hasLogistics: !!(attributes.skuUnitBoxDimension || attributes.skuUnitBoxWeight)
      });

      // 기본 구조 생성 (AI 응답에 없는 속성들은 빈 배열/기본값으로 보장)
      // 물류 정보는 sanitize 함수로 정제
      const validatedResult = {
        exposedAttributes: attributes.exposedAttributes || [],
        unexposedAttributes: attributes.unexposedAttributes || [],
        skuUnitBoxDimension: this.sanitizeBoxDimension(attributes.skuUnitBoxDimension),
        skuUnitBoxWeight: this.sanitizeBoxWeight(attributes.skuUnitBoxWeight),
        notices: attributes.notices || []
      };

      console.log('🔧 AI 응답 후 빈 속성 보장:', {
        hasExposed: !!attributes.exposedAttributes,
        hasUnexposed: !!attributes.unexposedAttributes,
        hasLogistics: !!(attributes.skuUnitBoxDimension || attributes.skuUnitBoxWeight),
        hasNotices: !!attributes.notices
      });

      // 노출 속성 검증 (dropdown 값 확인)
      if (validatedResult.exposedAttributes && Array.isArray(validatedResult.exposedAttributes)) {
        validatedResult.exposedAttributes = validatedResult.exposedAttributes.map(attr => {
          const schemaAttr = schema.exposedAttributes?.find(s => s.name === attr.attributeName);
          if (schemaAttr?.dropdown && schemaAttr.dropdown.length > 0) {
            if (!schemaAttr.dropdown.includes(attr.attributeValue)) {
              console.warn(`노출 속성 "${attr.attributeName}"의 값이 dropdown 옵션과 일치하지 않습니다: ${attr.attributeValue}`);
              return {
                attributeName: attr.attributeName,
                attributeValue: schemaAttr.dropdown[0] // 첫 번째 dropdown 값으로 대체
              };
            }
          }
          return attr;
        });
      }

      // 비노출 속성 검증 (dropdown 값 확인)
      if (validatedResult.unexposedAttributes && Array.isArray(validatedResult.unexposedAttributes)) {
        validatedResult.unexposedAttributes = validatedResult.unexposedAttributes.map(attr => {
          const schemaAttr = schema.unexposedAttributes?.find(s => s.name === attr.attributeName);
          if (schemaAttr?.dropdown && schemaAttr.dropdown.length > 0) {
            if (!schemaAttr.dropdown.includes(attr.attributeValue)) {
              console.warn(`비노출 속성 "${attr.attributeName}"의 값이 dropdown 옵션과 일치하지 않습니다: ${attr.attributeValue}`);
              // 필수는 첫 선택지를 지어 넣지 않는다 — 빈 값으로 두면 부른 쪽이 이름·선택지를 적어 멈춘다(CB70ED: 유리 팔찌에 «천연가죽»이 들어갈 뻔)
              return {
                attributeName: attr.attributeName,
                attributeValue: schemaAttr.mandatory ? null : schemaAttr.dropdown[0] // 선택은 첫 번째 dropdown 값으로 대체(기존)
              };
            }
          }

          // 값이 없거나 빈 문자열인 경우
          if (!attr.attributeValue || attr.attributeValue.trim() === "") {
            return {
              attributeName: attr.attributeName,
              attributeValue: null // 값 없음 시 null 설정
            };
          }

          return attr;
        });
      }

      console.log('✅ 속성 응답 검증 완료');
      return validatedResult;

    } catch (error) {
      console.error('속성 응답 검증 중 오류:', error);
      return {
        exposedAttributes: [],
        unexposedAttributes: [],
        skuUnitBoxDimension: null,
        skuUnitBoxWeight: null,
        notices: []
      };
    }
  },

  /**
   * 3차: 차별화 속성 배치 생성 (최대 10개씩 처리)
   * @param {Object} product - 상품 객체
   * @param {Array} optionsToProcess - 처리할 옵션 목록
   * @param {Object} differentiatedSchema - 차별화 속성 스키마
   * @param {Object} attributeRequest - 속성 요청 데이터
   * @returns {Promise<Object>} 차별화 속성 생성 결과
   */
  async generateAllDifferentiatedAttributes(product, optionsToProcess, differentiatedSchema, attributeRequest) {
    try {
      console.log('🎯 차별화 속성 배치 생성 시작 (최대 10개씩 처리)');

      // 차별화 속성이 없으면 빈 결과 반환
      const hasDiffAttrs = Object.values(differentiatedSchema).some(attrs => attrs.length > 0);
      if (!hasDiffAttrs) {
        console.log('ℹ️ 차별화 속성이 없습니다.');
        const results = {};
        optionsToProcess.forEach(option => {
          results[option.optionNumber] = {
            success: true,
            attributes: {
              exposedAttributes: [],
              unexposedAttributes: [],
              skuUnitBoxDimension: null,
              skuUnitBoxWeight: null,
              notices: []
            }
          };
        });
        return { success: true, results, errors: [] };
      }

      // 옵션을 10개씩 분할
      const batchSize = 10;
      const optionBatches = [];
      for (let i = 0; i < optionsToProcess.length; i += batchSize) {
        optionBatches.push(optionsToProcess.slice(i, i + batchSize));
      }

      console.log(`📊 총 ${optionsToProcess.length}개 옵션을 ${optionBatches.length}개 배치로 분할 (배치당 최대 ${batchSize}개)`);

      // 모든 배치 결과를 병합할 객체
      const allResults = {
        success: true,
        results: {},
        errors: []
      };

      // 각 배치별로 처리
      for (let batchIndex = 0; batchIndex < optionBatches.length; batchIndex++) {
        const batch = optionBatches[batchIndex];
        console.log(`🔄 배치 ${batchIndex + 1}/${optionBatches.length} 처리 중 (${batch.length}개 옵션)`);

        try {
          const batchResult = await this.generateDifferentiatedAttributesBatch(
            product,
            batch,
            differentiatedSchema,
            attributeRequest,
            batchIndex + 1
          );

          // 배치 결과를 전체 결과에 병합
          Object.assign(allResults.results, batchResult.results);
          allResults.errors.push(...batchResult.errors);

          console.log(`✅ 배치 ${batchIndex + 1} 완료: ${Object.keys(batchResult.results).length}개 옵션 처리`);

        } catch (batchError) {
          console.error(`❌ 배치 ${batchIndex + 1} 처리 중 오류:`, batchError);

          // 실패한 배치의 옵션들을 기본값으로 설정
          batch.forEach(option => {
            allResults.results[option.optionNumber] = {
              success: false,
              error: batchError.message,
              attributes: {
                exposedAttributes: [],
                unexposedAttributes: [],
                skuUnitBoxDimension: null,
                skuUnitBoxWeight: null,
                notices: []
              }
            };
          });

          allResults.errors.push({
            batch: batchIndex + 1,
            error: batchError.message
          });
        }
      }

      // 전체 성공 여부 결정
      allResults.success = allResults.errors.length === 0;

      console.log('✅ 모든 배치 처리 완료:', {
        totalOptions: optionsToProcess.length,
        totalBatches: optionBatches.length,
        successfulOptions: Object.keys(allResults.results).filter(key =>
          allResults.results[key].success
        ).length,
        errors: allResults.errors.length
      });

      return allResults;

    } catch (error) {
      console.error('❌ 차별화 속성 배치 생성 중 전체 오류:', error);
      return {
        success: false,
        error: error.message,
        results: {},
        errors: [{ error: error.message }]
      };
    }
  },

  /**
   * 차별화 속성 단일 배치 생성 (최대 10개 옵션)
   * @param {Object} product - 상품 객체
   * @param {Array} optionBatch - 처리할 옵션 배치 (최대 10개)
   * @param {Object} differentiatedSchema - 차별화 속성 스키마
   * @param {Object} attributeRequest - 속성 요청 데이터
   * @param {number} batchNumber - 배치 번호
   * @returns {Promise<Object>} 배치 처리 결과
   */
  async generateDifferentiatedAttributesBatch(product, optionBatch, differentiatedSchema, attributeRequest, batchNumber) {
    try {
      console.log(`🎯 배치 ${batchNumber} 차별화 속성 생성 시작 (${optionBatch.length}개 옵션)`);
      const unitMeta = buildUnitMeta(product.rocketRegInfo?.schemaString); // NUMBER 속성의 허용 단위(프롬프트용)

      // 배치 옵션 정보 준비
      const batchOptions = optionBatch.map(option => ({
        optionNumber: option.optionNumber,
        korName: option.korName,
        // 그 옵션 묶음 수(원본 자리 묶음 포함) — 수량·내용량을 «1개» 로 채우던 자리(PR261021-DB0DBE)
        bundleQuantity: bundleOptionService.quotationBundleQuantity(product, option)
      }));

      // 상품명 준비 (번들 정보 포함)
      let promptProductName = product.seo?.productName || product.productName;
      const bundleNotes = bundleOptionService.quotationBundleNotes(product);   // 상품 글 — 옵션마다의 수는 아래 옵션 줄(bundleQuantity)
      const bundleNote = bundleNotes.note;
      const logisticsBundleNote = bundleNotes.logisticsNote;

      if (bundleNote) {
        promptProductName = `${promptProductName}${bundleNotes.nameSuffix}`;
        console.log(`차별화 속성 배치 ${batchNumber} 프롬프트: 번들 정보 포함 - ${promptProductName}`);
      }

      // 차별화 속성 배치 생성 요청
      const batchRequest = {
        workType: 'QUOTATION',
        task: `GET_DIFFERENTIATED_ATTRIBUTES_BATCH_${batchNumber}`,
        productId: product.registrationId,
        requestArr: [{
          prompt: `
당신은 쿠팡 상품 등록을 위한 속성 생성 전문가입니다.
다음 **${batchOptions.length}개 옵션들**에 대한 차별화 속성들을 생성해주세요.

## 상품 기본 정보
- 상품명: ${promptProductName}${bundleNote}${bannedWordsRule(product.productFeature)}

## 🎯 처리할 옵션들 (배치 ${batchNumber})
${batchOptions.map((option, idx) => `${idx+1}. 옵션번호: ${option.optionNumber}, 옵션명: "${option.korName}"${option.bundleQuantity >= 2 ? `, 묶음: ${option.bundleQuantity}개 묶음(이 옵션의 수량·내용량은 ${option.bundleQuantity}개 기준)` : ''}`).join('\n')}

## 1688 상품 속성 정보 (참고용)
\`\`\`json
${JSON.stringify(attributeRequest.alibaba?.productAttribute || {}, null, 2)}
\`\`\`

## 1688 배송 정보 (물류 정보 참고용)${logisticsBundleNote}
\`\`\`json
${JSON.stringify(shippingInfoForAi(attributeRequest.alibaba?.productShippingInfo), null, 2)}
\`\`\`

## 생성할 차별화 속성들 (옵션별로 다른 값)
${differentiatedSchema.exposedAttributes?.length > 0 ? `

${differentiatedSchema.exposedAttributes?.length > 0 ? "### 노출 속성 (옵션별 차별화):" : ""}
${differentiatedSchema.exposedAttributes.map(attr => `
**속성명 : ${attr.name}**
${attr.dropdown ? `- 필수선택(택1): ${JSON.stringify(attr.dropdown)}` : (attr.example ? `- 입력예시: ${attr.example} (예시에 단위가 포함된 경우 반드시 해당 단위를 사용해야 함)` : '')}
${attr.dropdown ? '' : unitHint(unitMeta[attr.name])}
`).join('\n')}

${differentiatedSchema.unexposedAttributes?.length > 0 ? "### 비노출 속성 (옵션별 차별화):" : ""}
${differentiatedSchema.unexposedAttributes.map(attr => `
**속성명 : ${attr.name}**
${attr.dropdown ? `- 필수선택(택1): ${JSON.stringify(attr.dropdown)}` : (attr.example ? `- 입력예시: ${attr.example}` : '')}
${attr.dropdown ? '' : unitHint(unitMeta[attr.name])}
`).join('\n')}

${differentiatedSchema.legalInfo?.length > 0 ? "### 법적 정보 (옵션별 차별화):" : ""}
${differentiatedSchema.legalInfo.map(notice => `
**${notice.name}**
`).join('\n')}

${differentiatedSchema.logisticsInfo?.length > 0 ? "### 물류 정보 (옵션별 차별화):" : ""}
${differentiatedSchema.logisticsInfo.map(logistics => `
**${logistics.name}** (${logistics.title})
- 가이드: ${logistics.guide}
- 예시: ${logistics.example}
${packageEstimateGuide()}
`).join('\n')}` : ''}

## 중요 지침
1. **필수선택(택1) 속성**: 반드시 제공된 선택지 중에서만 선택해야 함. (가능한 null을 지양하고 최대한 유사한 값을 선택하는 것을 지향)
2. **입력예시 속성**: 예시에 "2025년도", "10m", "1D" 등 단위가 있으면 반드시 해당 단위 사용.
3. **1688 정보 활용**: 1688 상품 속성 정보 (참고용) 에서 관련 속성값 참고
4. **물류 정보**: 1688 배송 정보 (물류 정보 참고용) 에서 포장 정보 참고
5. **추정 허용**: 정확한 정보가 없어도 단위를 지켜서 합리적으로 추정

## 🚨 가장 중요한 지침 - 옵션별 독립 생성
**각 옵션의 모든 속성은 반드시 해당 옵션명에만 기반하여 독립적으로 생성하세요.**
- ✅ 올바른 예시:
  * 옵션1 "28.5*18*20cm" → 사이즈: "28.5*18*20cm", 수량: "2개"
  * 옵션2 "28.5*18*36.5cm" → 사이즈: "28.5*18*36.5cm", 수량: "2개"

- ❌ 잘못된 예시 (절대 금지):
  * 옵션1 "28.5*18*20cm" → 사이즈: "28.5*18*20cm / 28.5*18*36.5cm" (다른 옵션 정보 포함 금지!)
  * 옵션1 "28.5*18*20cm" → 수량: "2개 / 3개" (다른 옵션 정보 포함 금지!)

**다른 옵션의 정보를 절대 합치거나 포함하지 마세요. 각 옵션은 완전히 독립적으로 처리하세요.**

## 응답 형식 (배치 ${batchNumber} 옵션들)
다음 JSON 구조로 응답하세요. 각 옵션번호와 속성명은 정확히 일치해야 합니다:

\`\`\`json
{
  ${batchOptions.map(option => {
    const parts = [];

    if (differentiatedSchema.exposedAttributes?.length > 0) {
      parts.push(`"exposedAttributes": [
        ${differentiatedSchema.exposedAttributes.map(attr =>
          `{"attributeName": "${attr.name}", "attributeValue": "..."}`
        ).join(',\n      ')}
      ]`);
    }

    if (differentiatedSchema.unexposedAttributes?.length > 0) {
      parts.push(`"unexposedAttributes": [
        ${differentiatedSchema.unexposedAttributes.map(attr =>
          `{"attributeName": "${attr.name}", "attributeValue": "..."}`
        ).join(',\n      ')}
      ]`);
    }

    if (differentiatedSchema.logisticsInfo?.some(info => info.name === 'skuUnitBoxDimension')) {
      parts.push(PACKAGE_FORM_FIELD);
      parts.push(`"skuUnitBoxDimension": "..."`);
    }

    if (differentiatedSchema.logisticsInfo?.some(info => info.name === 'skuUnitBoxWeight')) {
      parts.push(`"skuUnitBoxWeight": "..."`);
    }

    if (differentiatedSchema.legalInfo?.length > 0) {
      parts.push(`"notices": [
        ${differentiatedSchema.legalInfo.map(notice =>
          `{"noticeItemName": "${notice.name}", "noticeItemValue": "..."}`
        ).join(',\n      ')}
      ]`);
    }

    return `  "${option.optionNumber}": {
      ${parts.join(',\n    ')}
    }`;
  }).join(',\n')}
}
\`\`\`

⚠️ 중요:
1. 위 JSON에서 "..." 부분만 각 옵션에 맞는 실제 값으로 채워주세요
2. 옵션번호와 속성명은 절대 변경하지 마세요
3. 각 옵션별로 서로 다른 값을 생성해주세요
          `
        }]
      };

      console.log(`🤖 배치 ${batchNumber} 차별화 속성 생성 요청:`, {
        workType: batchRequest.workType,
        productId: batchRequest.productId,
        optionCount: batchOptions.length,
        exposedCount: differentiatedSchema.exposedAttributes?.length || 0,
        unexposedCount: differentiatedSchema.unexposedAttributes?.length || 0,
        legalCount: differentiatedSchema.legalInfo?.length || 0,
        logisticsCount: differentiatedSchema.logisticsInfo?.length || 0
      });

      const result = await this.requestWithPackageImage(
        product,
        differentiatedSchema,
        batchRequest,
        {
          timeout: 90000, // 배치당 90초
          maxRetries: 2
        }
      );

      if (result.error) {
        throw new Error(result.error);
      }

      // 결과 검증 및 정리
      const validatedResults = this.validateBatchDifferentiatedResults(result, optionBatch, differentiatedSchema);

      console.log(`✅ 배치 ${batchNumber} 차별화 속성 생성 완료:`, {
        optionCount: optionBatch.length,
        successfulOptions: Object.keys(validatedResults.results).length,
        errors: validatedResults.errors.length
      });

      return validatedResults;

    } catch (error) {
      console.error(`❌ 배치 ${batchNumber} 차별화 속성 생성 중 오류:`, error);

      // 실패한 배치의 모든 옵션을 기본값으로 설정
      const fallbackResults = {
        success: false,
        results: {},
        errors: [{ batch: batchNumber, error: error.message }]
      };

      optionBatch.forEach(option => {
        fallbackResults.results[option.optionNumber] = {
          success: false,
          error: error.message,
          attributes: {
            exposedAttributes: [],
            unexposedAttributes: [],
            skuUnitBoxDimension: null,
            skuUnitBoxWeight: null,
            notices: []
          }
        };
      });

      return fallbackResults;
    }
  },

  /**
   * 배치 차별화 속성 결과 검증
   * @param {Object} result - AI 배치 응답 결과
   * @param {Array} optionsToProcess - 처리할 옵션 목록
   * @param {Object} differentiatedSchema - 차별화 속성 스키마
   * @returns {Object} 검증된 배치 결과
   */
  validateBatchDifferentiatedResults(result, optionsToProcess, differentiatedSchema) {
    try {
      // AI 응답에서 JSON 파싱 (batchResults 구조 처리)
      let batchResults;

      if (result.batchResults && result.batchResults.length > 0) {
        const batchResult = result.batchResults[0];
        if (batchResult.result) {
          batchResults = batchResult.result;
        } else if (batchResult.processedText) {
          // JSON 블록 제거 후 파싱
          let jsonText = batchResult.processedText;
          jsonText = jsonText.replace(/```json\n|\n```/g, '');
          jsonText = jsonText.replace(/```\n|\n```/g, '');
          jsonText = jsonText.trim();
          batchResults = JSON.parse(jsonText);
        }
      } else if (typeof result === 'string') {
        batchResults = JSON.parse(result);
      } else {
        batchResults = result;
      }

      console.log('🔍 파싱된 배치 결과:', Object.keys(batchResults || {}));

      const validatedResults = {
        success: true,
        results: {},
        errors: []
      };

      // 각 옵션별로 결과 검증
      optionsToProcess.forEach(option => {
        const optionNumber = option.optionNumber;
        const optionResult = batchResults[optionNumber];

        if (optionResult) {
          try {
            // 개별 옵션 결과 검증 (빈 속성들은 자동으로 보장됨)
            const validatedAttributes = this.validateAttributeResponse(optionResult, differentiatedSchema);

            validatedResults.results[optionNumber] = {
              success: true,
              attributes: validatedAttributes
            };

            console.log(`✅ 옵션 ${optionNumber} 차별화 속성 검증 완료`);

          } catch (error) {
            console.error(`❌ 옵션 ${optionNumber} 차별화 속성 검증 실패:`, error);
            validatedResults.errors.push({
              optionNumber: optionNumber,
              error: error.message
            });

            // 실패한 옵션도 기본값으로 설정
            validatedResults.results[optionNumber] = {
              success: false,
              error: error.message,
              attributes: {
                exposedAttributes: [],
                unexposedAttributes: [],
                skuUnitBoxDimension: null,
                skuUnitBoxWeight: null,
                notices: []
              }
            };
          }
        } else {
          console.warn(`⚠️ 옵션 ${optionNumber}에 대한 결과가 없습니다.`);
          validatedResults.errors.push({
            optionNumber: optionNumber,
            error: '결과 없음'
          });

          // 결과가 없는 옵션도 기본값으로 설정
          validatedResults.results[optionNumber] = {
            success: false,
            error: '결과 없음',
            attributes: {
              exposedAttributes: [],
              unexposedAttributes: [],
              skuUnitBoxDimension: null,
              skuUnitBoxWeight: null,
              notices: []
            }
          };
        }
      });

      // 전체 성공 여부 결정
      validatedResults.success = validatedResults.errors.length === 0;

      console.log('✅ 배치 차별화 속성 결과 검증 완료:', {
        totalOptions: optionsToProcess.length,
        successfulOptions: Object.keys(validatedResults.results).filter(key =>
          validatedResults.results[key].success
        ).length,
        failedOptions: validatedResults.errors.length
      });

      return validatedResults;

    } catch (error) {
      console.error('배치 차별화 속성 결과 검증 중 오류:', error);

      // 전체 실패 시 모든 옵션에 빈 결과 설정
      const fallbackResults = {
        success: false,
        results: {},
        errors: [{ error: error.message }]
      };

      optionsToProcess.forEach(option => {
        fallbackResults.results[option.optionNumber] = {
          success: false,
          error: error.message,
          attributes: {
            exposedAttributes: [],
            unexposedAttributes: [],
            skuUnitBoxDimension: null,
            skuUnitBoxWeight: null,
            notices: []
          }
        };
      });

      return fallbackResults;
    }
  },

  /**
   * 4차: 결과 합성 및 적용 (모든 옵션 한꺼번에)
   * @param {Object} product - 상품 객체
   * @param {Array} optionsToProcess - 처리할 옵션 목록
   * @param {Object} commonAttributes - 공통 속성 결과
   * @param {Object} differentiatedResults - 차별화 속성 결과
   * @returns {Promise<Object>} 합성 및 적용 결과
   */
  async mergeAndApplyBatchResults(product, optionsToProcess, commonAttributes, differentiatedResults) {
    try {
      console.log('🔧 결과 합성 및 적용 시작 (모든 옵션 한꺼번에)');

      let successCount = 0;
      const successfulOptions = [];
      const failedOptions = [];
      const optionErrors = []; // 실패한 옵션마다 이유 «옵션 N: …» — 받는 쪽(process)·processError 에 그대로 넘긴다
      const allMergedAttributes = {}; // 모든 옵션의 합성된 속성 저장

      // 1단계: 모든 옵션의 속성 합성
      console.log('📋 1단계: 모든 옵션의 속성 합성');
      const optionIndexMapping = {}; // optionNumber -> optionIndex 매핑

      for (let i = 0; i < optionsToProcess.length; i++) {
        const option = optionsToProcess[i];
        const optionIndex = product.options.findIndex(opt => opt.optionNumber === option.optionNumber);

        if (optionIndex === -1) {
          console.warn(`옵션 ${option.optionNumber}을 찾을 수 없습니다.`);
          failedOptions.push(option.optionNumber);
          optionErrors.push(`옵션 ${option.optionNumber}: 상품 옵션 목록에 없음`);
          continue;
        }

        // 매핑 저장
        optionIndexMapping[option.optionNumber] = optionIndex;

        try {
          console.log(`🔄 옵션 ${option.optionNumber} (인덱스: ${optionIndex}) 결과 합성 중...`);

          // 공통 속성과 차별화 속성 합성
          const mergedAttributes = this.mergeAttributes(
            commonAttributes.attributes,
            differentiatedResults.results[option.optionNumber]?.attributes || {}
          );

          // 1688 이 옵션별로 준 실측 포장값이 있으면 AI 추정 대신 그것을 쓴다(없는 칸만 AI 값). 사람이 저장한 값은
          // applyAttributesToOption 이 그대로 둔다(PR261016-B5A4A0 — 1688 에 15×11×1cm·20g 이 있는데 «포장값 없음»).
          const from1688 = packageFrom1688(product, product.options[optionIndex]);
          if (from1688.skuUnitBoxDimension && this.isValidBoxDimension(from1688.skuUnitBoxDimension)) {
            mergedAttributes.skuUnitBoxDimension = from1688.skuUnitBoxDimension;
          }
          if (from1688.skuUnitBoxWeight) mergedAttributes.skuUnitBoxWeight = from1688.skuUnitBoxWeight;
          if (from1688.note) console.warn(`⚠️ 옵션 ${option.optionNumber}: ${from1688.note}`);
          // 1688 크기를 펼친 크기로 보고 버렸으면(판 #733 ②) 남은 크기는 AI 짐작 — logisticsPage.packageSource 로 «AI 짐작» 표시(fillLogisticsPackage)
          if (from1688.untrusted && mergedAttributes.skuUnitBoxDimension) mergedAttributes.packageSource = 'ai-guess';

          // 사이즈 계열 노출속성은 그 옵션의 1688 원문 규격이 있으면 그 값 — 사이즈가 공통속성으로 가면 AI 대표값 하나가 옵션마다 복사됐다
          // (PR261021-761EC8 45–50mm → «40mm» · PR261021-C8F601 4규격 → 다 «39*33*4cm»). 숫자칸은 단일 값만 · 못 넣으면 AI 추정 그대로(로그 한 줄)
          const sizeFix = applySkuSize(mergedAttributes.exposedAttributes, product.options[optionIndex], buildUnitMeta(product.rocketRegInfo?.schemaString), product.productFeature);
          sizeFix.replaced.forEach(r => console.warn(`⚠️ 옵션 ${option.optionNumber} ${r.name}: AI «${r.from}» → 원문 «${r.to}»`));
          if (sizeFix.guessed.length) console.warn(`⚠️ 옵션 ${option.optionNumber} ${sizeFix.guessed.join('·')} = AI 추정(원문 규격 없음)`);

          console.log(`📝 옵션 ${option.optionNumber} 합성된 속성:`, {
            exposedCount: mergedAttributes.exposedAttributes?.length || 0,
            unexposedCount: mergedAttributes.unexposedAttributes?.length || 0,
            hasLogistics: !!(mergedAttributes.skuUnitBoxDimension || mergedAttributes.skuUnitBoxWeight),
            noticesCount: mergedAttributes.notices?.length || 0
          });

          // 속성 생성 완료 후 검증
          const attributeValidation = this.validateAttributeCompleteness(mergedAttributes, product.rocketRegInfo?.schemaString);
          console.log(`🔍 [속성 검증] 옵션 ${option.optionNumber} 검증 결과:`, attributeValidation);

          // 합성된 속성 저장 (optionNumber를 키로 사용, 검증 결과 포함)
          allMergedAttributes[option.optionNumber] = {
            optionIndex: optionIndex,
            attributes: mergedAttributes,
            validationResult: attributeValidation // 검증 결과 저장
          };
          successfulOptions.push(option.optionNumber);
          successCount++;

        } catch (error) {
          console.error(`❌ 옵션 ${option.optionNumber} 합성 실패:`, error);
          failedOptions.push(option.optionNumber);
          optionErrors.push(`옵션 ${option.optionNumber}: 속성 합성 실패 — ${error.message}`);
        }
      }

      // 2단계: 모든 옵션에 속성 한꺼번에 적용
      console.log('📋 2단계: 모든 옵션에 속성 한꺼번에 적용');
      // 옵션마다 따로 적용 — 한 옵션의 오류(예: 속성 단위 오류)로 나머지 옵션까지 실패 처리하지 않고, 그 옵션만 실패 + 이유를 남긴다
      for (const [optionNumber, optionData] of Object.entries(allMergedAttributes)) {
        const { optionIndex, attributes } = optionData;
        try {
          await this.applyAttributesToOption(product, optionIndex, attributes);
          console.log(`✅ 옵션 ${optionNumber} (인덱스: ${optionIndex}) 속성 적용 완료`);
        } catch (error) {
          console.error(`❌ 옵션 ${optionNumber} 속성 적용 중 오류:`, error);
          // allMergedAttributes 의 키는 문자열 — successfulOptions 에 든 원래 값(숫자/문자)으로 찾는다
          const index = successfulOptions.findIndex(optNum => String(optNum) === optionNumber);
          if (index > -1) {
            failedOptions.push(successfulOptions[index]);
            successfulOptions.splice(index, 1);
            successCount--;
          }
          delete allMergedAttributes[optionNumber]; // 적용 못 한 옵션은 저장 대상에서 뺀다
          optionErrors.push(`옵션 ${optionNumber}: ${error.message}`);
        }
      }

      // 3단계: 모든 옵션 상태 업데이트 (processing 유지 - 최종 검증 후 상태 결정)
      console.log('📋 3단계: 모든 옵션 상태 업데이트 (processing 유지)');
      const updatedOptions = [...product.options];
      successfulOptions.forEach(optionNumber => {
        const optionIndex = updatedOptions.findIndex(opt => opt.optionNumber === optionNumber);
        if (optionIndex !== -1) {
          // 검증 결과 확인 (로깅용)
          const optionData = allMergedAttributes[optionNumber];
          const validationPassed = optionData?.validationResult?.isValid !== false;

          updatedOptions[optionIndex] = {
            ...updatedOptions[optionIndex],
            // 아직 processing 유지 - 최종 검증 후 completed 또는 error로 변경됨
            quotationStatus: 'processing',
            updatedAt: Date.now()
          };

          if (!validationPassed) {
            console.warn(`⚠️ 옵션 ${optionNumber} 검증 실패 감지 → 최종 검증에서 재시도 예정`);
          }
        }
      });

      // 4단계: 견적서 데이터 생성
      console.log('📋 4단계: 견적서 데이터 생성');
      let quotationData = null;
      try {
        if (this.checkCoupangDocExists(product)) {
          quotationData = this.extractQuotationFromCoupangDoc(product);
          console.log('✅ 견적서 데이터 생성 완료');
        }
      } catch (error) {
        console.error('❌ 견적서 데이터 생성 중 오류:', error);
      }

      // 5단계: Vuex와 서버에 모든 옵션 한꺼번에 저장 (성공 여부에 따른 상태 설정)
      console.log('📋 5단계: Vuex와 서버에 모든 옵션 한꺼번에 저장');

      // 성공 여부에 따른 상품 레벨 상태 결정
      let quotationStatus;
      if (successCount === optionsToProcess.length) {
        quotationStatus = 'processing'; // 모든 옵션 성공 → 상품 레벨에서 노출속성 검증 및 상품 분리 진행 예정
        console.log('✅ 모든 옵션 성공 → 상품 레벨 processing 상태 (노출속성 검증 및 상품 분리 예정)');
      } else if (successCount > 0) {
        quotationStatus = 'partially_completed'; // 일부 성공 → 부분 완료
        console.log(`⚠️ 일부 옵션 성공 (${successCount}/${optionsToProcess.length}) → partially_completed 상태`);
      } else {
        quotationStatus = 'error'; // 모든 옵션 실패 → 오류
        console.log('❌ 모든 옵션 실패 → error 상태');
      }

      const updateFields = {
        'quotationStatus': quotationStatus,
        'updatedAt': Date.now(),
        'options': updatedOptions
      };

      // 실패한 옵션이 있는 경우 오류 정보 추가
      if (failedOptions.length > 0) {
        updateFields['processError'] = {
          message: `${failedOptions.length}개 옵션 처리 중 오류가 발생했습니다.`,
          timestamp: Date.now(),
          failedOptions,
          ...(optionErrors.length ? { errors: optionErrors } : {})
        };
      }

      if (quotationData) {
        updateFields['quotation'] = quotationData;
      }

      // ⚠️ 전체 coupangDoc 덮어쓰기 대신 점 표기법으로 각 옵션별 필드만 업데이트 (2025-01-20 수정)
      // 이유: detailImageService가 저장한 imagePage.details.detailedImage, altText 값이 덮어씌워지는 문제 방지
      // 해결: 각 옵션별로 합성된 속성(allMergedAttributes)을 점 표기법으로 저장
      if (product.coupangDoc && product.coupangDoc.jsonDocument) {
        console.log('📋 coupangDoc 필드를 점 표기법으로 추가 (각 옵션별 속성)');

        for (const [optionNumber, optionData] of Object.entries(allMergedAttributes)) {
          const { optionIndex } = optionData;

          // applyAttributesToOption에서 스키마 순서대로 값이 배치된 docItem에서 가져옴
          // AI 생성 순서(attributes.xxx)가 아닌 스키마 순서를 유지하기 위함
          const docItem = product.coupangDoc.jsonDocument[optionIndex];

          if (!docItem) {
            console.warn(`⚠️ 옵션 ${optionNumber} (인덱스: ${optionIndex})의 docItem이 없습니다.`);
            continue;
          }

          // 노출 속성 저장 (스키마 순서 유지 - 배열 전체가 아닌 각 요소의 값만 업데이트)
          if (docItem.productPage?.commonAttributes?.exposedAttributes?.length > 0) {
            docItem.productPage.commonAttributes.exposedAttributes.forEach((attr, attrIndex) => {
              // attributeValue만 업데이트 (배열 순서 유지)
              updateFields[`coupangDoc.jsonDocument.${optionIndex}.productPage.commonAttributes.exposedAttributes.${attrIndex}.attributeValue`] = attr.attributeValue;
            });
            console.log(`✅ 옵션 ${optionNumber} (인덱스: ${optionIndex}) 노출속성 ${docItem.productPage.commonAttributes.exposedAttributes.length}개 값 업데이트`);
          }

          // 비노출 속성 저장 (스키마 순서 유지 - 배열 전체가 아닌 각 요소의 값만 업데이트)
          if (docItem.productPage?.unexposedAttributes?.length > 0) {
            docItem.productPage.unexposedAttributes.forEach((attr, attrIndex) => {
              // attributeValue만 업데이트 (배열 순서 유지)
              updateFields[`coupangDoc.jsonDocument.${optionIndex}.productPage.unexposedAttributes.${attrIndex}.attributeValue`] = attr.attributeValue;
            });
          }

          // 물류 정보 저장 — applyAttributesToOption 이 넣은 값(하한 포함, 사장님 #260)을 DB 에도 남긴다
          if (typeof docItem.logisticsPage?.skuUnitBoxDimension === 'string') {
            updateFields[`coupangDoc.jsonDocument.${optionIndex}.logisticsPage.skuUnitBoxDimension`] = docItem.logisticsPage.skuUnitBoxDimension;
          }
          if (typeof docItem.logisticsPage?.skuUnitBoxWeight === 'string') {
            updateFields[`coupangDoc.jsonDocument.${optionIndex}.logisticsPage.skuUnitBoxWeight`] = docItem.logisticsPage.skuUnitBoxWeight;
          }

          // 고시 정보 저장 (스키마 순서 유지 - 배열 전체가 아닌 각 요소의 값만 업데이트)
          if (docItem.legalPage?.notices?.length > 0) {
            docItem.legalPage.notices.forEach((notice, noticeIndex) => {
              // noticeContent만 업데이트 (배열 순서 유지)
              updateFields[`coupangDoc.jsonDocument.${optionIndex}.legalPage.notices.${noticeIndex}.noticeContent`] = notice.noticeContent;
            });
            console.log(`✅ 옵션 ${optionNumber} (인덱스: ${optionIndex}) 고시정보 ${docItem.legalPage.notices.length}개 값 업데이트`);
          }
        }

        console.log('✅ coupangDoc 점 표기법 필드 추가 완료');
      }

      await store.dispatch('updateProductPartial', {
        productId: product._id,
        updateFields: updateFields
      });

      console.log(`✅ 5단계 완료: ${successCount}/${optionsToProcess.length}개 성공`);

      // ===== 성공 여부에 따른 다음 단계 결정 =====
      if (successCount === optionsToProcess.length) {
        console.log('✅ 모든 옵션 견적서 작업 완료 → 상품 레벨 노출속성 검증 및 상품 분리 진행');

        return {
          success: true,
          successCount,
          successfulOptions,
          failedOptions,
          totalProcessed: optionsToProcess.length,
          shouldContinueToExposedAttributeCheck: true // 상품 레벨 노출속성 검증 및 상품 분리 진행 플래그
        };
      } else {
        console.log(`⚠️ 일부 옵션 속성 생성 실패 (${failedOptions.length}개) → 노출속성 검증 건너뛰고 완료`);

        // 이미 5단계에서 적절한 상태(partially_completed 또는 error)로 저장됨
        // 추가 저장 없이 바로 종료
        return {
          success: successCount > 0,
          successCount,
          successfulOptions,
          failedOptions,
          totalProcessed: optionsToProcess.length,
          shouldContinueToExposedAttributeCheck: false, // 노출속성 검증 건너뛰기
          finalStatus: quotationStatus,
          error: optionErrors.length ? optionErrors.join(' / ') : undefined // 멈춘 이유 — 실패 옵션마다 하나씩 · process 가 그대로 보여 준다
        };
      }

    } catch (error) {
      console.error('❌ 결과 합성 및 적용 중 전체 오류:', error);
      return {
        success: false,
        error: error.message,
        successCount: 0,
        successfulOptions: [],
        failedOptions: optionsToProcess.map(opt => opt.optionNumber),
        totalProcessed: optionsToProcess.length
      };
    }
  },

  /**
   * 속성 완성도 검증 (순서 + 항목)
   * @param {Object} attributes - 속성 데이터
   * @param {Object} schema - 스키마 객체
   * @returns {Object} 검증 결과
   */
  validateAttributeCompleteness(attributes, schema) {
    try {
      const validation = {
        isValid: true,
        orderValid: true,
        completenessValid: true,
        missingRequired: [],
        orderIssues: [],
        warnings: []
      };

      if (!schema || !schema.properties) {
        validation.warnings.push('스키마 정보가 없어 검증을 건너뜁니다.');
        return validation;
      }

      // 1. 필수 속성 완성도 검증
      const requiredValidation = this.validateRequiredAttributes(attributes, schema);
      validation.missingRequired = requiredValidation.missing;
      validation.completenessValid = requiredValidation.missing.length === 0;

      // 2. 속성 순서 검증
      const orderValidation = this.validateAttributeOrder(attributes, schema);
      validation.orderValid = orderValidation.isValid;
      validation.orderIssues = orderValidation.issues;

      // 3. 전체 검증 결과
      validation.isValid = validation.orderValid && validation.completenessValid;

      return validation;
    } catch (error) {
      console.error('속성 완성도 검증 중 오류:', error);
      return {
        isValid: false,
        orderValid: false,
        completenessValid: false,
        missingRequired: [],
        orderIssues: [],
        warnings: ['검증 중 오류가 발생했습니다.']
      };
    }
  },

  /**
   * 필수 속성 완성도 검증
   * @param {Object} attributes - 속성 데이터 (exposedAttributes, unexposedAttributes)
   * @param {Object} schema - 스키마 객체
   * @returns {Object} 필수 속성 검증 결과
   */
  validateRequiredAttributes(attributes, schema) {
    const missing = [];

    // 노출속성 필수 항목 검증
    if (schema.properties?.productPage?.properties?.commonAttributes?.properties?.exposedAttributes?.allOf) {
      schema.properties.productPage.properties.commonAttributes.properties.exposedAttributes.allOf.forEach(config => {
        const attrConfig = config.contains?.properties?.attributeName;
        if (attrConfig?.mandatory === true || attrConfig?.requirement === '필수') {
          const attrName = attrConfig.enum?.[0];
          if (attrName) {
            const hasValidValue = attributes.exposedAttributes?.some(attr =>
              attr.attributeName === attrName &&
              attr.attributeValue &&
              attr.attributeValue.trim() !== ''
            );
            if (!hasValidValue) {
              missing.push(`노출속성: ${attrName}`);
            }
          }
        }
      });
    }

    // 비노출속성 필수 항목 검증
    if (schema.properties?.productPage?.properties?.unexposedAttributes?.allOf) {
      schema.properties.productPage.properties.unexposedAttributes.allOf.forEach(config => {
        const attrConfig = config.contains?.properties?.attributeName;
        if (attrConfig?.mandatory === true || attrConfig?.requirement === '필수') {
          const attrName = attrConfig.enum?.[0];
          if (attrName) {
            const hasValidValue = attributes.unexposedAttributes?.some(attr =>
              attr.attributeName === attrName &&
              attr.attributeValue &&
              attr.attributeValue.trim() !== ''
            );
            if (!hasValidValue) {
              missing.push(`비노출속성: ${attrName}`);
            }
          }
        }
      });
    }

    // 고시정보 필수 항목 검증
    if (schema.properties?.legalPage?.properties?.notices?.allOf) {
      schema.properties.legalPage.properties.notices.allOf.forEach(config => {
        const noticeConfig = config.contains?.properties?.noticeItemName;
        if (noticeConfig?.mandatory === true || noticeConfig?.requirement === '필수') {
          const noticeName = noticeConfig.enum?.[0];
          if (noticeName) {
            const hasValidValue = attributes.notices?.some(notice =>
              notice.noticeItemName === noticeName &&
              notice.noticeItemValue &&
              notice.noticeItemValue.trim() !== ''
            );
            if (!hasValidValue) {
              missing.push(`고시정보: ${noticeName}`);
            }
          }
        }
      });
    }

    return { missing };
  },

  /**
   * 속성 순서 검증
   * @param {Object} attributes - 속성 데이터
   * @param {Object} schema - 스키마 객체
   * @returns {Object} 순서 검증 결과
   */
  validateAttributeOrder(attributes, schema) {
    const issues = [];

    // 노출속성 순서 검증
    if (attributes.exposedAttributes && schema.properties?.productPage?.properties?.commonAttributes?.properties?.exposedAttributes?.allOf) {
      const schemaOrder = schema.properties.productPage.properties.commonAttributes.properties.exposedAttributes.allOf
        .map(config => config.contains?.properties?.attributeName?.enum?.[0])
        .filter(name => name);

      const actualOrder = attributes.exposedAttributes.map(attr => attr.attributeName);
      const orderIssue = this.checkAttributeOrder(actualOrder, schemaOrder, '노출속성');
      if (orderIssue) issues.push(orderIssue);
    }

    // 비노출속성 순서 검증
    if (attributes.unexposedAttributes && schema.properties?.productPage?.properties?.unexposedAttributes?.allOf) {
      const schemaOrder = schema.properties.productPage.properties.unexposedAttributes.allOf
        .map(config => config.contains?.properties?.attributeName?.enum?.[0])
        .filter(name => name);

      const actualOrder = attributes.unexposedAttributes.map(attr => attr.attributeName);
      const orderIssue = this.checkAttributeOrder(actualOrder, schemaOrder, '비노출속성');
      if (orderIssue) issues.push(orderIssue);
    }

    // 고시정보 순서 검증
    if (attributes.notices && schema.properties?.legalPage?.properties?.notices?.allOf) {
      const schemaOrder = schema.properties.legalPage.properties.notices.allOf
        .map(config => config.contains?.properties?.noticeItemName?.enum?.[0])
        .filter(name => name);

      const actualOrder = attributes.notices.map(notice => notice.noticeItemName);
      const orderIssue = this.checkAttributeOrder(actualOrder, schemaOrder, '고시정보');
      if (orderIssue) issues.push(orderIssue);
    }

    return {
      isValid: issues.length === 0,
      issues
    };
  },

  /**
   * 개별 속성 타입의 순서 검증
   * @param {Array} actualOrder - 실제 순서
   * @param {Array} schemaOrder - 스키마 순서
   * @param {string} type - 속성 타입
   * @returns {string|null} 순서 문제 설명
   */
  checkAttributeOrder(actualOrder, schemaOrder, type) {
    const schemaIndexMap = {};
    schemaOrder.forEach((name, index) => {
      schemaIndexMap[name] = index;
    });

    let lastSchemaIndex = -1;
    for (const attrName of actualOrder) {
      if (Object.prototype.hasOwnProperty.call(schemaIndexMap, attrName)) {
        const currentSchemaIndex = schemaIndexMap[attrName];
        if (currentSchemaIndex < lastSchemaIndex) {
          return `${type} 순서 오류: "${attrName}"이 잘못된 위치에 있습니다.`;
        }
        lastSchemaIndex = currentSchemaIndex;
      }
    }

    return null;
  },

  /**
   * docData에서 속성 데이터 추출
   * @param {Object} docData - coupangDoc의 jsonDocument 항목
   * @returns {Object} 추출된 속성 데이터
   */
  extractAttributesFromDocData(docData) {
    try {
      return {
        exposedAttributes: docData?.productPage?.commonAttributes?.exposedAttributes || [],
        unexposedAttributes: docData?.productPage?.unexposedAttributes || [],
        notices: docData?.legalPage?.notices || []
      };
    } catch (error) {
      console.error('docData에서 속성 추출 중 오류:', error);
      return {
        exposedAttributes: [],
        unexposedAttributes: [],
        notices: []
      };
    }
  },

  /**
   * 스키마 순서에 맞춰 속성 재정렬
   * @param {Object} attributes - 속성 데이터
   * @param {Object} schema - 스키마 객체
   * @returns {Object} 재정렬된 속성 데이터
   */
  reorderAttributesBySchema(attributes, schema) {
    try {
      console.log('📋 스키마 순서에 맞춰 속성 재정렬 시작');

      if (!schema || !schema.properties) {
        console.warn('⚠️ 스키마 정보가 없어 재정렬을 건너뜁니다.');
        return attributes;
      }

      const reorderedAttributes = {
        exposedAttributes: [],
        unexposedAttributes: []
      };

      // 각 페이지별로 처리
      Object.keys(schema.properties).forEach(pageName => {
        const pageSchema = schema.properties[pageName];

        if (!pageSchema.properties) {
          return;
        }

        console.log(`📄 ${pageName} 페이지 속성 재정렬 중...`);

        // 노출 속성 allOf 순서대로 재정렬
        if (pageSchema.properties.commonAttributes?.properties?.exposedAttributes?.allOf) {
          const exposedAllOf = pageSchema.properties.commonAttributes.properties.exposedAttributes.allOf;

          exposedAllOf.forEach(config => {
            if (config.contains?.properties?.attributeName?.enum?.[0]) {
              const fieldName = config.contains.properties.attributeName.enum[0];

              const exposedAttr = attributes.exposedAttributes?.find(attr => attr.attributeName === fieldName);
              if (exposedAttr) {
                reorderedAttributes.exposedAttributes.push(exposedAttr);
                console.log(`✅ 노출 속성 재정렬: ${fieldName}`);
              }
            }
          });
        }

        // 비노출 속성 allOf 순서대로 재정렬
        if (pageSchema.properties.unexposedAttributes?.allOf) {
          const unexposedAllOf = pageSchema.properties.unexposedAttributes.allOf;

          unexposedAllOf.forEach(config => {
            if (config.contains?.properties?.attributeName?.enum?.[0]) {
              const fieldName = config.contains.properties.attributeName.enum[0];

              const unexposedAttr = attributes.unexposedAttributes?.find(attr => attr.attributeName === fieldName);
              if (unexposedAttr) {
                reorderedAttributes.unexposedAttributes.push(unexposedAttr);
                console.log(`✅ 비노출 속성 재정렬: ${fieldName}`);
              }
            }
          });
        }
      });

      // 스키마에 없는 속성들도 뒤에 추가 (기존 순서 유지)
      attributes.exposedAttributes?.forEach(attr => {
        if (!reorderedAttributes.exposedAttributes.find(reordered => reordered.attributeName === attr.attributeName)) {
          reorderedAttributes.exposedAttributes.push(attr);
          console.log(`📎 스키마 외 노출 속성 추가: ${attr.attributeName}`);
        }
      });

      attributes.unexposedAttributes?.forEach(attr => {
        if (!reorderedAttributes.unexposedAttributes.find(reordered => reordered.attributeName === attr.attributeName)) {
          reorderedAttributes.unexposedAttributes.push(attr);
          console.log(`📎 스키마 외 비노출 속성 추가: ${attr.attributeName}`);
        }
      });

      console.log('✅ 속성 재정렬 완료:', {
        원본_노출속성: attributes.exposedAttributes?.length || 0,
        재정렬_노출속성: reorderedAttributes.exposedAttributes.length,
        원본_비노출속성: attributes.unexposedAttributes?.length || 0,
        재정렬_비노출속성: reorderedAttributes.unexposedAttributes.length
      });

      return {
        ...attributes,
        exposedAttributes: reorderedAttributes.exposedAttributes,
        unexposedAttributes: reorderedAttributes.unexposedAttributes
      };

    } catch (error) {
      console.error('❌ 속성 재정렬 중 오류:', error);
      return attributes; // 오류 시 원본 반환
    }
  },

  /**
   * 공통 속성과 차별화 속성 합성
   * @param {Object} commonAttrs - 공통 속성
   * @param {Object} diffAttrs - 차별화 속성
   * @returns {Object} 합성된 속성
   */
  mergeAttributes(commonAttrs, diffAttrs) {
    const merged = {
      exposedAttributes: [],
      unexposedAttributes: [],
      skuUnitBoxDimension: null,
      skuUnitBoxWeight: null,
      notices: []
    };

    // 노출 속성 합성
    if (commonAttrs.exposedAttributes && Array.isArray(commonAttrs.exposedAttributes)) {
      merged.exposedAttributes = [...commonAttrs.exposedAttributes];
    }
    if (diffAttrs.exposedAttributes && Array.isArray(diffAttrs.exposedAttributes)) {
      // 차별화 속성 추가 (중복 제거)
      diffAttrs.exposedAttributes.forEach(diffAttr => {
        const exists = merged.exposedAttributes.find(attr =>
          attr.attributeName === diffAttr.attributeName
        );
        if (!exists) {
          merged.exposedAttributes.push(diffAttr);
        } else {
          // 중복된 경우 차별화 속성 값으로 덮어쓰기
          const index = merged.exposedAttributes.findIndex(attr =>
            attr.attributeName === diffAttr.attributeName
          );
          merged.exposedAttributes[index] = diffAttr;
        }
      });
    }

    // 비노출 속성 합성
    if (commonAttrs.unexposedAttributes && Array.isArray(commonAttrs.unexposedAttributes)) {
      merged.unexposedAttributes = [...commonAttrs.unexposedAttributes];
    }
    if (diffAttrs.unexposedAttributes && Array.isArray(diffAttrs.unexposedAttributes)) {
      // 차별화 속성 추가 (중복 제거)
      diffAttrs.unexposedAttributes.forEach(diffAttr => {
        const exists = merged.unexposedAttributes.find(attr =>
          attr.attributeName === diffAttr.attributeName
        );
        if (!exists) {
          merged.unexposedAttributes.push(diffAttr);
        } else {
          // 중복된 경우 차별화 속성 값으로 덮어쓰기
          const index = merged.unexposedAttributes.findIndex(attr =>
            attr.attributeName === diffAttr.attributeName
          );
          merged.unexposedAttributes[index] = diffAttr;
        }
      });
    }

    // 물류 정보 합성 (차별화 속성 우선)
    merged.skuUnitBoxDimension = diffAttrs.skuUnitBoxDimension || commonAttrs.skuUnitBoxDimension || null;
    merged.skuUnitBoxWeight = diffAttrs.skuUnitBoxWeight || commonAttrs.skuUnitBoxWeight || null;

    // 고시 정보 합성
    if (commonAttrs.notices && Array.isArray(commonAttrs.notices)) {
      merged.notices = [...commonAttrs.notices];
    }
    if (diffAttrs.notices && Array.isArray(diffAttrs.notices)) {
      // 차별화 고시 정보 추가 (중복 제거)
      diffAttrs.notices.forEach(diffNotice => {
        const exists = merged.notices.find(notice =>
          notice.noticeItemName === diffNotice.noticeItemName
        );
        if (!exists) {
          merged.notices.push(diffNotice);
        } else {
          // 중복된 경우 차별화 속성 값으로 덮어쓰기
          const index = merged.notices.findIndex(notice =>
            notice.noticeItemName === diffNotice.noticeItemName
          );
          merged.notices[index] = diffNotice;
        }
      });
    }

    console.log('🔄 속성 합성 완료:', {
      exposedCount: merged.exposedAttributes.length,
      unexposedCount: merged.unexposedAttributes.length,
      hasLogistics: !!(merged.skuUnitBoxDimension || merged.skuUnitBoxWeight),
      noticesCount: merged.notices.length
    });

    return merged;
  },

  /**
   * 속성 배열을 스키마 순서에 맞춰 재정렬 (coupangDoc용)
   * @param {Array} attributeArray - 속성 배열 [{attributeName, attributeValue}, ...] 또는 [{noticeItemName, noticeItemValue}, ...]
   * @param {Object} schema - 스키마 객체
   * @param {string} attributeType - 속성 타입 ('exposedAttributes', 'unexposedAttributes', 'notices')
   * @returns {Array} 재정렬된 속성 배열
   */
  reorderAttributesBySchemaArray(attributeArray, schema, attributeType) {
    try {
      if (!attributeArray || !Array.isArray(attributeArray) || attributeArray.length === 0) {
        return attributeArray || [];
      }

      if (!schema || !schema.properties) {
        console.warn('⚠️ 스키마 정보가 없어 배열 재정렬을 건너뜁니다.');
        return attributeArray;
      }

      const reorderedArray = [];
      const processedAttributeNames = new Set();

      // 각 페이지별로 처리
      Object.keys(schema.properties).forEach(pageName => {
        const pageSchema = schema.properties[pageName];

        if (!pageSchema.properties) {
          return;
        }

        // allOf 배열 순서대로 속성 재정렬
        if (attributeType === 'exposedAttributes' && pageSchema.properties.commonAttributes?.properties?.exposedAttributes?.allOf) {
          const exposedAllOf = pageSchema.properties.commonAttributes.properties.exposedAttributes.allOf;

          exposedAllOf.forEach(config => {
            if (config.contains?.properties?.attributeName?.enum?.[0]) {
              const fieldName = config.contains.properties.attributeName.enum[0];

              // 속성 배열에서 해당 속성 찾기
              const foundAttr = attributeArray.find(attr => attr.attributeName === fieldName);
              if (foundAttr) {
                reorderedArray.push(foundAttr);
                processedAttributeNames.add(fieldName);
                console.log(`📋 노출속성 재정렬: ${fieldName}`);
              }
            }
          });
        }

        if (attributeType === 'unexposedAttributes' && pageSchema.properties.unexposedAttributes?.allOf) {
          const unexposedAllOf = pageSchema.properties.unexposedAttributes.allOf;

          unexposedAllOf.forEach(config => {
            if (config.contains?.properties?.attributeName?.enum?.[0]) {
              const fieldName = config.contains.properties.attributeName.enum[0];

              // 속성 배열에서 해당 속성 찾기
              const foundAttr = attributeArray.find(attr => attr.attributeName === fieldName);
              if (foundAttr) {
                reorderedArray.push(foundAttr);
                processedAttributeNames.add(fieldName);
                console.log(`📋 비노출속성 재정렬: ${fieldName}`);
              }
            }
          });
        }

        // notice 배열 재정렬 추가
        if (attributeType === 'notices' && pageSchema.properties.notices?.allOf) {
          const noticesAllOf = pageSchema.properties.notices.allOf;

          noticesAllOf.forEach(config => {
            if (config.contains?.properties?.noticeItemName?.enum?.[0]) {
              const fieldName = config.contains.properties.noticeItemName.enum[0];

              // notice 배열에서 해당 항목 찾기
              const foundNotice = attributeArray.find(notice => notice.noticeItemName === fieldName);
              if (foundNotice) {
                reorderedArray.push(foundNotice);
                processedAttributeNames.add(fieldName);
                console.log(`📋 고시정보 재정렬: ${fieldName}`);
              }
            }
          });
        }
      });

      // 스키마에 없는 속성들도 뒤에 추가 (기존 순서 유지)
      attributeArray.forEach(attr => {
        const keyName = attributeType === 'notices' ? attr.noticeItemName : attr.attributeName;
        if (!processedAttributeNames.has(keyName)) {
          reorderedArray.push(attr);
        }
      });

      console.log(`📋 ${attributeType} 배열 재정렬: ${attributeArray.length} → ${reorderedArray.length}개`);

      return reorderedArray;

    } catch (error) {
      console.error('❌ 속성 배열 재정렬 중 오류:', error);
      return attributeArray; // 오류 시 원본 반환
    }
  },

  /**
   * 스마트 배치 처리로 옵션별 속성 처리 (메인 함수)
   * @param {Object} product - 상품 객체
   * @param {Array} optionsToProcess - 처리할 옵션 목록
   * @param {Object} attributeRequest - 속성 요청 데이터
   * @returns {Promise<Object>} 처리 결과
   */
  async processBatchAttributes(product, optionsToProcess, attributeRequest) {
    try {
      console.log('🚀 스마트 배치 처리 시작');

      // 1차: 스키마 분석 + 가이드 생성
      console.log('📋 1차: 스키마 분석 및 속성 분류');
      const schemaAnalysis = await this.analyzeSchemaAndClassifyAttributes(product, optionsToProcess);

      if (!schemaAnalysis.success) {
        console.warn('⚠️ 1차 스키마 분석 실패, 즉시 기존 방식으로 폴백:', schemaAnalysis.error);
        throw new Error('스키마 분석 실패: ' + schemaAnalysis.error);
      }

      // 분석 결과 검증 - 모든 속성이 0개인 경우 실패로 간주
      const totalCommonAttrs = Object.values(schemaAnalysis.commonSchema).reduce((sum, attrs) => sum + (attrs?.length || 0), 0);
      const totalDiffAttrs = Object.values(schemaAnalysis.differentiatedSchema).reduce((sum, attrs) => sum + (attrs?.length || 0), 0);

      if (totalCommonAttrs === 0 && totalDiffAttrs === 0) {
        console.warn('⚠️ 스키마 분석 결과가 비어있음, 기존 방식으로 폴백');
        throw new Error('스키마 분석 결과 없음 - 모든 속성이 0개로 분류됨');
      }

      console.log('✅ 1차 완료 - 속성 분류:', {
        commonExposed: schemaAnalysis.commonSchema.exposedAttributes.length,
        commonUnexposed: schemaAnalysis.commonSchema.unexposedAttributes.length,
        commonLegal: schemaAnalysis.commonSchema.legalInfo.length,
        commonLogistics: schemaAnalysis.commonSchema.logisticsInfo.length,
        diffExposed: schemaAnalysis.differentiatedSchema.exposedAttributes.length,
        diffUnexposed: schemaAnalysis.differentiatedSchema.unexposedAttributes.length,
        diffLegal: schemaAnalysis.differentiatedSchema.legalInfo.length,
        diffLogistics: schemaAnalysis.differentiatedSchema.logisticsInfo.length
      });

      // 2차: 공통 속성 일괄 생성
      console.log('🔄 2차: 공통 속성 일괄 생성');
      const commonAttributes = await this.generateCommonAttributes(product, schemaAnalysis.commonSchema, attributeRequest);

      if (!commonAttributes.success) {
        console.warn('⚠️ 공통 속성 생성 실패:', commonAttributes.error);
        // 공통 속성 실패해도 차별화 속성은 계속 진행
      }

      console.log('✅ 2차 완료 - 공통 속성 생성:', {
        success: commonAttributes.success,
        exposedCount: commonAttributes.attributes.exposedAttributes?.length || 0,
        unexposedCount: commonAttributes.attributes.unexposedAttributes?.length || 0,
        hasLogistics: !!(commonAttributes.attributes.skuUnitBoxDimension || commonAttributes.attributes.skuUnitBoxWeight),
        noticesCount: commonAttributes.attributes.notices?.length || 0
      });

      // 3차: 차별화 속성 배치 생성
      console.log('🎯 3차: 차별화 속성 배치 생성');
      const differentiatedResults = await this.generateAllDifferentiatedAttributes(product, optionsToProcess, schemaAnalysis.differentiatedSchema, attributeRequest);

      if (!differentiatedResults.success) {
        console.warn('⚠️ 차별화 속성 생성 실패:', differentiatedResults.error);
        // 차별화 속성 실패 시에도 공통 속성이 있으면 부분적으로 진행
      }

      console.log('✅ 3차 완료 - 차별화 속성 생성:', {
        success: differentiatedResults.success,
        totalOptions: optionsToProcess.length,
        successfulOptions: Object.keys(differentiatedResults.results).length,
        errors: differentiatedResults.errors.length
      });

      // 4차: 결과 합성 및 적용
      console.log('🔧 4차: 결과 합성 및 적용');
      const mergeResult = await this.mergeAndApplyBatchResults(product, optionsToProcess, commonAttributes, differentiatedResults);

      console.log('✅ 4차 완료 - 결과 합성 및 적용:', {
        success: mergeResult.success,
        successCount: mergeResult.successCount,
        totalProcessed: mergeResult.totalProcessed,
        shouldContinue: mergeResult.shouldContinueToExposedAttributeCheck
      });

      // ===== 성공 여부에 따른 다음 단계 결정 =====
      if (!mergeResult.shouldContinueToExposedAttributeCheck) {
        console.log('⚠️ 일부 옵션 속성 생성 실패로 인해 노출속성 검증 건너뜀');
        // 받는 쪽(process)은 .success/.error 만 읽는다 — success 가 없으면 이유가 «배치 처리 실패»로 덮였다(PR261020-3B91B3)
        return {
          success: false,
          status: mergeResult.finalStatus || 'error',
          message: `속성 생성 단계에서 ${mergeResult.totalProcessed - mergeResult.successCount}개 옵션 실패`,
          error: mergeResult.error, // 실패 옵션마다 이유(옵션 없음·합성 실패·적용 실패)
          details: {
            total: mergeResult.totalProcessed,
            success: mergeResult.successCount,
            failed: mergeResult.totalProcessed - mergeResult.successCount,
            successfulOptions: mergeResult.successfulOptions,
            failedOptions: mergeResult.failedOptions
          }
        };
      }

      // 전체 성공 여부 결정
      const overallSuccess = mergeResult.success && mergeResult.successCount > 0;

      console.log(`🎉 스마트 배치 처리 완료: ${overallSuccess ? '성공' : '실패'}`);

      return {
        success: overallSuccess,
        successCount: mergeResult.successCount,
        successfulOptions: mergeResult.successfulOptions,
        failedOptions: mergeResult.failedOptions,
        totalProcessed: mergeResult.totalProcessed,
        details: {
          schemaAnalysis,
          commonAttributes,
          differentiatedResults,
          mergeResult
        }
      };

    } catch (error) {
      console.error('❌ 스마트 배치 처리 중 오류:', error);
      if (error.mandatoryUnexposed) throw error;
      logServiceError('quotationService', 'processSmartBatch', error, {
        errorType: ERROR_TYPES.AI_REQUEST_FAILED,
        productId: product?.registrationId,
        additional: { optionCount: optionsToProcess?.length }
      });
      return {
        success: false,
        error: error.message,
        successCount: 0,
        successfulOptions: [],
        failedOptions: optionsToProcess.map(opt => opt.optionNumber),
        totalProcessed: optionsToProcess.length
      };
    }
  },

  /**
   * 기존 방식으로 폴백 처리
   * @param {Object} product - 상품 객체
   * @param {Array} optionsToProcess - 처리할 옵션 목록
   * @param {Object} attributeRequest - 속성 요청 데이터
   * @param {number} initialSuccessCount - 초기 성공 카운트
   * @param {Array} initialSuccessfulOptions - 초기 성공한 옵션 목록
   * @returns {Promise<Object>} 폴백 처리 결과
   */
  async processFallbackIndividual(product, optionsToProcess, attributeRequest, initialSuccessCount = 0, initialSuccessfulOptions = []) {
    console.log('🔄 기존 방식으로 폴백 처리 시작');

    let successCount = initialSuccessCount;
    let failCount = 0;
    const successfulOptions = [...initialSuccessfulOptions];
    const failedOptions = [];

    for (let i = 0; i < optionsToProcess.length; i++) {
      const currentOption = optionsToProcess[i];
      const optionIndex = product.options.findIndex(opt => opt.optionNumber === currentOption.optionNumber);

      // 이미 성공한 옵션은 건너뛰기
      if (successfulOptions.includes(currentOption.optionNumber)) {
        console.log(`⏭️ 옵션 ${currentOption.optionNumber}은 이미 처리됨, 건너뛰기`);
        continue;
      }

      // 진행 상태 업데이트
      store.dispatch('setProcessing', {
        isProcessing: true,
        message: `등록 세부정보 생성 중... (폴백 ${i+1}/${optionsToProcess.length}, 성공: ${successCount}, 실패: ${failCount})`
      });

      console.log(`폴백 옵션 ${i+1}/${optionsToProcess.length} 처리 중: ${currentOption.optionNumber} (${currentOption.korName})`);

      try {
        // 기존 processOptionAttributes 함수 사용
        await this.processOptionAttributes(product, optionIndex, attributeRequest, 0, 3);
        successCount++;
        successfulOptions.push(currentOption.optionNumber);

        // 옵션 상태 업데이트
        const updatedOptions = [...product.options];
        updatedOptions[optionIndex] = {
          ...updatedOptions[optionIndex],
          quotationStatus: 'completed'
        };

        // 현재까지 처리된 옵션으로 임시 견적서 데이터 생성
        let tempQuotationData = null;
        try {
          if (this.checkCoupangDocExists(product)) {
            tempQuotationData = this.extractQuotationFromCoupangDoc(product);
            console.log(`폴백 옵션 ${i+1}/${optionsToProcess.length} 처리 후 임시 견적서 데이터 생성 완료`);
          }
        } catch (error) {
          console.error('임시 견적서 데이터 생성 중 오류:', error);
        }

        // 상품 상태 업데이트 (부분 업데이트)
        const updateFields = {
          'quotationStatus': 'processing',
          'updatedAt': Date.now()
        };

        // 옵션별 점표기법으로 개별 업데이트
        if (updatedOptions && updatedOptions.length > 0) {
          updatedOptions.forEach((option, optionIndex) => {
            if (option.optionNumber) {
              updateFields[`options.${optionIndex}.quotationStatus`] = option.quotationStatus || 'processing';
              // 필요한 다른 옵션 필드들도 점표기법으로 추가
              if (Object.prototype.hasOwnProperty.call(option, 'updatedAt')) {
                updateFields[`options.${optionIndex}.updatedAt`] = option.updatedAt;
              }
            }
          });
        }

        if (tempQuotationData) {
          updateFields['quotation'] = tempQuotationData;
        }

        console.log('🔍 [서버 전송 데이터 확인] updateFields:', JSON.stringify(updateFields, null, 2));
        console.log('🔍 [서버 전송 데이터 확인] quotation 데이터:', JSON.stringify(updateFields.quotation, null, 2));

        // ⚠️ 전체 coupangDoc 덮어쓰기 제거 (2025-01-06)
        // 이유: detailImageService가 저장한 imagePage.details.detailedImage, altText 값이 덮어씌워지는 문제
        // 폴백 처리에서도 점 표기법으로 필요한 필드만 업데이트
        // if (product.coupangDoc) {
        //   updateFields['coupangDoc'] = product.coupangDoc;
        //   console.log('🔍 [서버 전송 데이터 확인] coupangDoc도 함께 업데이트');
        // }

        await store.dispatch('updateProductPartial', {
          productId: product._id,
          updateFields: updateFields
        });

        console.log(`✅ 폴백 옵션 ${currentOption.optionNumber} 처리 완료`);

      } catch (error) {
        console.error(`❌ 폴백 옵션 ${currentOption.optionNumber} 처리 실패:`, error);
        failCount++;
        failedOptions.push(currentOption.optionNumber);
      }
    }

    console.log(`✅ 폴백 처리 완료: ${successCount}/${optionsToProcess.length}개 성공`);

    return {
      success: successCount > 0,
      successCount,
      successfulOptions,
      failedOptions,
      totalProcessed: optionsToProcess.length
    };
  },

  /**
   * 지연 시간을 추가하는 유틸리티 함수
   * @param {number} ms - 지연 시간(밀리초)
   * @returns {Promise<void>} 지연 시간 후 해결되는 Promise
   */
  sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  },

  /**
   * 단일 옵션의 속성 처리 (재시도 로직 포함)
   * @param {Object} product - 상품 객체
   * @param {number} optionIndex - 옵션 인덱스
   * @param {Object} attributeRequest - 속성 요청 데이터
   * @param {number} retryCount - 현재 재시도 횟수 (기본값: 0)
   * @param {number} maxRetries - 최대 재시도 횟수 (기본값: 3)
   * @returns {Promise<boolean>} 성공 여부
   */
  async processOptionAttributes(product, optionIndex, attributeRequest, retryCount = 0, maxRetries = 3) {
    try {
      const option = product.options[optionIndex];
      console.log(`옵션 ${option.optionNumber} (${option.korName}) 속성 처리 시작 (시도: ${retryCount + 1}/${maxRetries + 1})`);

      // 재시도 시에만 지연 시간 추가 (지수 백오프 적용)
      if (retryCount > 0) {
        // 재시도 시 지연 시간 적용 (2초, 4초, 8초...)
        const delayTime = 2000 * Math.pow(2, retryCount);
        console.log(`재시도 대기 중... ${delayTime}ms (${delayTime/1000}초)`);
        await this.sleep(delayTime);
      }
      // 첫 번째 시도에서는 지연 없이 바로 진행 (Rate Limiting은 workflowService에서 처리)

      // 옵션별 alibabaInfo 수정 - 현재 처리 중인 옵션을 selectedOption으로 설정
      const modifiedAlibabaInfo = JSON.parse(JSON.stringify(attributeRequest.alibaba));

      // 현재 옵션을 selectedOption으로 설정
      if (option) {
        // 옵션의 SEO된 korName 찾기
        let seoKorName = option.korName; // 기본값으로 원래 korName 사용

        // 상품에 SEO 정보가 있고, 옵션별 SEO 정보가 있는 경우
        if (product.seo && product.seo.options && Array.isArray(product.seo.options)) {
          const seoOption = product.seo.options.find(seoOpt => seoOpt.optionNumber === option.optionNumber);
          if (seoOption && seoOption.korName) {
            seoKorName = seoOption.korName;
          }
        }

        modifiedAlibabaInfo.selectedOption = {
          optionNumber: option.optionNumber,
          korName: option.korName,
          seoKorName: seoKorName,
          option1: option.option1,
          option2: option.option2
        };
      }

      try {
        // workflowService.requestExtensionWithPromise 사용
        const result = await workflowService.requestExtensionWithPromise(
          {
            workType: 'GET_ATTRIBUTE_INFO',
            productId: product.registrationId,
            coupangSchema: attributeRequest.schema,
            alibabaProductInfo: modifiedAlibabaInfo,
            defaultSettings: store.state.settings[0]?.defaultSettings || {},
            applyToAllOptions: false, // 단일 옵션 모드로 설정
            selectedOptionIndex: optionIndex, // 현재 처리 중인 옵션 인덱스
            currentMode: 'single_option' // 항상 단일 옵션 모드
          },
          {
            timeout: 90000, // 90초 타임아웃
            maxRetries: maxRetries   // 재시도 횟수
          }
        );

        console.log(`옵션 ${option.optionNumber} 속성 생성 결과:`, result);

        if (result.error) {
          console.error(`옵션 ${option.optionNumber} 속성 생성 오류:`, result.error);
          throw new Error(result.error);
        }

        // 응답 데이터 검증 및 정제
        const validatedResponse = this.validateResponseData(result, product.rocketRegInfo);

        // 상품 레벨의 coupangDoc이 없으면 생성
        if (!product.coupangDoc) {
          product.coupangDoc = this.createEmptyCoupangDoc();
        }

        // 해당 옵션의 jsonDocument 인덱스에 속성 적용
        console.log(`📝 옵션 ${option.optionNumber} (인덱스: ${optionIndex}) 속성 적용 시작`);

        // coupangDoc 상태 로깅
        if (product.coupangDoc) {
          console.log(`📊 속성 적용 전 coupangDoc 상태:`, {
            version: product.coupangDoc.version,
            scope: product.coupangDoc.scope,
            hasJsonDocument: !!product.coupangDoc.jsonDocument,
            jsonDocumentLength: product.coupangDoc.jsonDocument?.length || 0
          });

          // jsonDocument 배열 길이 확인
          if (product.coupangDoc.jsonDocument) {
            if (optionIndex < product.coupangDoc.jsonDocument.length) {
              console.log(`✅ jsonDocument[${optionIndex}] 존재함`);
            } else {
              console.warn(`⚠️ jsonDocument[${optionIndex}] 존재하지 않음 (배열 길이: ${product.coupangDoc.jsonDocument.length})`);
            }
          } else {
            console.warn('⚠️ jsonDocument 배열이 없음');
          }
        } else {
          console.warn('⚠️ product.coupangDoc이 없음');
        }

        await this.applyAttributesToOption(product, optionIndex, {
          exposedAttributes: validatedResponse.exposedAttributes,
          unexposedAttributes: validatedResponse.unexposedAttributes,
          skuUnitBoxDimension: validatedResponse.skuUnitBoxDimension,
          skuUnitBoxWeight: validatedResponse.skuUnitBoxWeight,
          notices: validatedResponse.notices
        });

        console.log(`✅ 옵션 ${option.optionNumber} 속성 처리 완료`);

        // 속성 적용 후 서버에 업데이트
        try {
          // coupangDoc 상태 로깅
          if (product.coupangDoc) {
            console.log(`📊 속성 적용 후 coupangDoc 상태:`, {
              version: product.coupangDoc.version,
              scope: product.coupangDoc.scope,
              hasJsonDocument: !!product.coupangDoc.jsonDocument,
              jsonDocumentLength: product.coupangDoc.jsonDocument?.length || 0
            });

            // jsonDocument 배열 길이 확인
            if (product.coupangDoc.jsonDocument) {
              if (optionIndex < product.coupangDoc.jsonDocument.length) {
                const docItem = product.coupangDoc.jsonDocument[optionIndex];
                console.log(`📑 jsonDocument[${optionIndex}] 구조:`, {
                  hasStartPage: !!docItem.startPage,
                  hasProductPage: !!docItem.productPage,
                  hasLogisticsPage: !!docItem.logisticsPage,
                  hasLegalPage: !!docItem.legalPage
                });
              }
            }
          }

          // 견적서 데이터 추출
          console.log(`🔍 옵션 ${option.optionNumber}의 견적서 데이터 추출 시작`);
          const quotationData = this.extractQuotationFromCoupangDoc(product, optionIndex);
          console.log(`✅ 옵션 ${option.optionNumber}의 견적서 데이터 추출 완료`);

          // 서버에 업데이트 - 두 단계로 분리
          console.log(`💾 옵션 ${option.optionNumber}의 coupangDoc 서버 업데이트 시작`);

          // 견적서 데이터 및 coupangDoc 점 표기법 업데이트
          console.log('🔄 견적서 데이터 및 coupangDoc 점 표기법 업데이트 시작...');
          const updateFields = {
            'quotation': quotationData,
            'quotationStatus': 'completed',
            'updatedAt': Date.now()
          };

          // coupangDoc 점 표기법 필드 추가
          if (product.coupangDoc && product.coupangDoc.jsonDocument) {
            this.addCoupangDocFieldsToUpdateFields(updateFields, optionIndex, quotationData);
          }

          // 옵션 정보 업데이트
          if (product.options && product.options.length > 0) {
            product.options.forEach((opt, idx) => {
              if (opt.optionNumber) {
                updateFields[`options.${idx}.quotationStatus`] = 'completed';
              }
            });
          }

          await apiService.updateRocketProductPartial(product._id, updateFields);
          console.log(`✅ 옵션 ${option.optionNumber}의 견적서 데이터 및 coupangDoc 업데이트 완료`);

          // store 상태 부분 업데이트
          store.commit('updateProductPartial', {
            productId: product._id,
            updateFields: updateFields
          });

          console.log(`✅ 옵션 ${option.optionNumber}의 coupangDoc 서버 업데이트 완료`);
        } catch (updateError) {
          console.error(`❌ 옵션 ${option.optionNumber}의 coupangDoc 서버 업데이트 중 오류:`, updateError);
          // 업데이트 오류는 무시하고 계속 진행 (메모리에는 이미 적용됨)
        }

        // 성공 응답 반환
        return {
          success: true,
          optionIndex: optionIndex,
          optionNumber: option.optionNumber,
          attributeData: validatedResponse
        };
      } catch (error) {
        console.error(`옵션 속성 처리 중 오류:`, error);

        // 예외 발생 시 재시도 로직
        if (retryCount < maxRetries) {
          console.log(`옵션 ${product.options[optionIndex].optionNumber} 처리 중 오류 발생, 재시도 중... (${retryCount + 1}/${maxRetries})`);
          // 재시도 시 지연 시간 추가 (2초, 4초, 8초...)
          const retryDelay = 2000 * (retryCount + 1);
          await new Promise(resolve => setTimeout(resolve, retryDelay));
          return this.processOptionAttributes(product, optionIndex, attributeRequest, retryCount + 1, maxRetries);
        }

        throw error;
      }
    } catch (error) {
      console.error(`processOptionAttributes 전체 오류:`, error);

      // 예외 발생 시 재시도 로직
      if (retryCount < maxRetries) {
        console.log(`processOptionAttributes 전체 오류 발생, 재시도 중... (${retryCount + 1}/${maxRetries})`);
        // 재시도 시 지연 시간 추가 (2초, 4초, 8초...)
        const retryDelay = 2000 * (retryCount + 1);
        await new Promise(resolve => setTimeout(resolve, retryDelay));
        return this.processOptionAttributes(product, optionIndex, attributeRequest, retryCount + 1, maxRetries);
      }

      throw error;
    }
  },

  /**
   * 상품 속성을 한국어로 변환하는 함수 - workflowService.requestExtensionWithPromise 사용
   * @param {Object} product - 상품 객체
   * @returns {Promise<Object>} 변환된 속성이 포함된 상품 객체
   */
  async requestTranslateAttributes(product) {
    try {
      console.log('상품 속성 한국어 변환 요청 시작');

      // 속성이 없는 경우 처리
      if (!product.attributes || !Array.isArray(product.attributes) || product.attributes.length === 0) {
        console.log('변환할 상품 속성이 없습니다. 원본 상품 반환');
        return product;
      }

      // API 키 확인
      if (!store.state.settings[0]?.hasGeminiKey) {
        console.warn('Gemini API Key가 설정되지 않았습니다. 원본 상품 반환');
        return product;
      }

      // 원본 속성 로깅
      console.log('원본 상품 속성:', JSON.stringify(product.attributes).substring(0, 500) + '...');

      // workflowService.requestExtensionWithPromise 사용
      const result = await workflowService.requestExtensionWithPromise(
        {
          workType: 'QUOTATION',
          task: 'TRANSLATE_ATTRIBUTES',
          productId: product.registrationId,
          requestArr: [{
            prompt: `
# 작업: 중국어 상품 속성을 한국어로 변환

## 입력 데이터
${JSON.stringify(product.attributes, null, 2)}

## 요구사항
1. 각 속성의 attributeName과 value를 한국어로 변환하세요.
2. 중국어 단위를 적절한 한국어/영어 단위로 변환하세요:
   - 斤 -> kg (1斤 = 0.5kg로 계산)
   - 码 -> size
   - 寸 -> cm
   - 米 -> m
   - 厘米 -> cm
   - 毫米 -> mm
   - 克 -> g
   - 千克 -> kg
   - 公斤 -> kg
   - 升 -> L
   - 毫升 -> ml
   - 平方米 -> m²
   - 平方厘米 -> cm²
   - 立方米 -> m³
   - 立方厘米 -> cm³
   - 件 -> 개
   - 个 -> 개
   - 双 -> 쌍
   - 套 -> 세트
   - 包 -> 팩
   - 盒 -> 박스
   - 瓶 -> 병
   - 袋 -> 봉지
3. 아래 재질 낱말은 글자대로 옮기지 말고 이 뜻으로 옮기세요(铜版·铜板 의 «铜» 은 구리가 아닙니다):
${materialTermsPrompt()}
4. 단위 변환 시 필요한 경우 수치도 적절히 변환하세요.
5. 원본 구조를 유지하면서 내용만 변환하세요.
6. 응답은 JSON 배열 형식으로 반환하세요.

## 응답 형식
[
  {
    "attributeName": "한국어 속성명",
    "value": "한국어 속성값"
  },
  ...
]
            `
          }],
          attributes: product.attributes
        },
        {
          timeout: 60000, // 60초 타임아웃
          maxRetries: 2   // 최대 2회 재시도
        }
      );

      console.log('속성 변환 결과 수신:', result);

      // 결과 처리
      let translatedAttributes = [];

      if (result.batchResults && result.batchResults.length > 0) {
        // 배치 결과에서 첫 번째 결과 사용
        const batchResult = result.batchResults[0];
        if (batchResult.result) {
          console.log('배치 결과에서 result 객체 사용');
          translatedAttributes = batchResult.result;
        } else if (batchResult.processedText) {
          // 텍스트 결과를 JSON으로 파싱 시도
          try {
            console.log('배치 결과에서 processedText 파싱 시도');
            let jsonText = batchResult.processedText;

            // ```json 블록 제거
            jsonText = jsonText.replace(/```json\n|\n```/g, '');
            // ``` 블록 제거 (언어 지정 없는 경우)
            jsonText = jsonText.replace(/```\n|\n```/g, '');
            // 앞뒤 공백 제거
            jsonText = jsonText.trim();

            translatedAttributes = JSON.parse(jsonText);
            console.log('JSON 파싱 성공, 항목 수:', translatedAttributes.length);
          } catch (parseError) {
            console.error('속성 변환 결과 파싱 오류:', parseError);
            // 대체 파싱 방법 시도
            try {
              const jsonMatch = batchResult.processedText.match(/\[\s*\{.*\}\s*\]/s);
              if (jsonMatch) {
                translatedAttributes = JSON.parse(jsonMatch[0]);
                console.log('대체 파싱 성공, 항목 수:', translatedAttributes.length);
              }
            } catch (alternativeError) {
              console.error('대체 파싱 방법도 실패:', alternativeError);
            }
          }
        }
      } else if (typeof result === 'object') {
        // 직접 결과 객체가 반환된 경우
        console.log('직접 결과 객체 사용');
        translatedAttributes = result;
      }

      // 유효한 결과인지 확인
      if (!Array.isArray(translatedAttributes) || translatedAttributes.length === 0) {
        console.warn('유효한 변환 결과가 없습니다. 원본 속성 사용');
        return product;
      }

      console.log('변환된 상품 속성:', JSON.stringify(translatedAttributes).substring(0, 500) + '...');

      // 변환된 속성으로 상품 객체 업데이트 (부분 업데이트 사용)
      await store.dispatch('updateProductPartial', {
        productId: product._id,
        updateFields: {
          'attributes': translatedAttributes,
          'updatedAt': Date.now()
        }
      });

      // 변환된 속성이 포함된 상품 객체 반환
      const updatedProduct = { ...product };
      updatedProduct.attributes = translatedAttributes;
      return updatedProduct;
    } catch (error) {
      console.error('상품 속성 한국어 변환 중 오류:', error);
      console.warn('오류로 인해 원본 상품 반환');
      return product;
    }
  },






  /**
 * 확장 프로그램에 전송할 속성 요청 데이터 준비
 * @param {Object} coupangDoc - 쿠팡 문서 객체
 * @param {Object} product - 상품 정보
 * @returns {Object} 속성 요청 데이터
 */
  prepareAttributeRequest(product) {
    try {
      // 스키마 정보 추출
      let schema = null;
      if (product.rocketRegInfo && product.rocketRegInfo.schemaString) {
        schema = JSON.parse(product.rocketRegInfo.schemaString);
      } else {
        throw new Error('쿠팡 스키마 정보를 찾을 수 없습니다.');
      }

      // SEO 정보 가져오기
      const seoInfo = product.seo || {};

      // DefaultSettings 정보
      const defaultSettings = store.state.settings[0]?.defaultSettings || {};

      // 선택된 옵션
      const selectedOption = store.state.selectedOption ||
        (product.options && product.options.length > 0 ? product.options[0] : null);

      // 모든 옵션의 SEO된 정보 수집
      const allOptions = [];
      if (product.options && product.options.length > 0) {
        product.options.forEach(option => {
          // 옵션의 SEO된 korName 찾기
          let seoKorName = option.korName; // 기본값으로 원래 korName 사용

          // 상품에 SEO 정보가 있고, 옵션별 SEO 정보가 있는 경우
          if (seoInfo && seoInfo.options && Array.isArray(seoInfo.options)) {
            const seoOption = seoInfo.options.find(seoOpt => seoOpt.optionNumber === option.optionNumber);
            if (seoOption && seoOption.korName) {
              seoKorName = seoOption.korName;
            }
          }

          allOptions.push({
            optionNumber: option.optionNumber,
            korName: option.korName,
            seoKorName: seoKorName, // SEO된 korName 추가
            option1: option.option1,
            option2: option.option2,
            selected: option.selected || false
          });
        });
      }

      // 1688 상품 정보 구성
      const alibabaInfo = {
        // 기본 상품 정보
        productName: product.productName,
        productNameTrans: product.productNameTrans,
        subject: product.productName,
        subjectTrans: product.productNameTrans,

        // 브랜드명 처리 - 쉼표로 구분된 경우 첫 번째 값만 사용
        brand: this.getFirstBrandName(product.brand || defaultSettings.brand || ''),

        // SEO 정보
        seo: {
          productName: seoInfo.productName || product.productNameTrans || '',
          metaDescription: seoInfo.metaDescription || '',
          keywords: seoInfo.keywords || [],
          searchTags: seoInfo.keywords ? seoInfo.keywords.join(', ') : ''
        },

        // 상품 속성 정보
        productAttribute: product.attributes || [],

        // 배송 정보 (추정)
        productShippingInfo: product.productShippingInfo || {
          skuShippingInfoList: [
            {
              weightKg: 1, // 기본값 (1kg)
              lengthCm: 30,
              widthCm: 20,
              heightCm: 10
            }
          ]
        },

        // 옵션 정보 - 선택된 옵션과 모든 옵션 정보 포함
        selectedOption: selectedOption ? {
          optionNumber: selectedOption.optionNumber,
          korName: selectedOption.korName,
          option1: selectedOption.option1,
          option2: selectedOption.option2
        } : null,

        // 모든 옵션 정보 추가 (SEO된 korName 포함)
        allOptions: allOptions,

        // 상품 이미지
        productImage: {
          images: product.mainImages || []
        },

        // 상품 설명
        description: product.description || '',

        // 카테고리 정보
        categoryPath: product.rocketRegInfo.categoryPath || '',
        categoryId: product.rocketRegInfo.categoryId || 0,

        // 설정 정보
        importStatus: product.importStatus || defaultSettings.importStatus || '수입대상아님',

        // defaultSettings 정보 추가
        defaultSettings: {
          asInfo: defaultSettings.asInfo || '제조사 문의',
          origin: defaultSettings.origin || '중국',
          manufacturer: defaultSettings.manufacturer || '',
          brand: defaultSettings.brand || '',
          importStatus: defaultSettings.importStatus || '수입대상아님',
          transactionType: defaultSettings.transactionType || '기타 도소매업자'
        }
      };

      return {
        schema: schema,
        alibaba: alibabaInfo
      };
    } catch (error) {
      console.error('속성 요청 데이터 준비 중 오류:', error);
      throw error;
    }
  },



  /**
  * 응답 데이터 검증 및 정제
  * @param {Object} response - 원본 응답 데이터
  * @param {Object} rocketRegInfo - 상품 등록 정보 (스키마 포함)
  * @returns {Object} 검증 및 정제된 데이터
  */
  validateResponseData(response, rocketRegInfo) {
    // 원본 응답 데이터 구조 확인
    const originalData = response.result || response;
    if (!originalData) return {};

    // 깊은 복사로 데이터 복제
    const result = JSON.parse(JSON.stringify(originalData));

    try {
      // 스키마 정보 추출 (있는 경우)
      let schema = null;
      if (rocketRegInfo && rocketRegInfo.schemaString) {
        try {
          schema = JSON.parse(rocketRegInfo.schemaString);
        } catch (e) {
          console.warn('스키마 파싱 오류:', e);
        }
      }

      // 1. 노출 속성 검증
      if (result.exposedAttributes && Array.isArray(result.exposedAttributes)) {
        result.exposedAttributes = result.exposedAttributes.map(attr => {
          // dropdown 옵션 확인
          const dropdownValues = this.getDropdownValues(schema, 'exposedAttributes', attr.attributeName);

          if (dropdownValues && dropdownValues.length > 0 &&
            !dropdownValues.includes(attr.attributeValue)) {
            console.warn(`노출 속성 "${attr.attributeName}"의 값이 dropdown 옵션과 일치하지 않습니다: ${attr.attributeValue}`);
            return {
              attributeName: attr.attributeName,
              attributeValue: "상세설명 참조" // dropdown 불일치 시 기본값
            };
          }

          // 값이 없거나 빈 문자열인 경우
          if (!attr.attributeValue || attr.attributeValue.trim() === "") {
            return {
              attributeName: attr.attributeName,
              attributeValue: "상세설명 참조" // 값 없음 시 기본값
            };
          }

          return attr;
        });
      }

      // 2. 비노출 속성 검증
      if (result.unexposedAttributes && Array.isArray(result.unexposedAttributes)) {
        result.unexposedAttributes = result.unexposedAttributes.map(attr => {
          const dropdownValues = this.getDropdownValues(schema, 'unexposedAttributes', attr.attributeName);

          if (dropdownValues && dropdownValues.length > 0 &&
            !dropdownValues.includes(attr.attributeValue)) {
            console.warn(`비노출 속성 "${attr.attributeName}"의 값이 dropdown 옵션과 일치하지 않습니다: ${attr.attributeValue}`);
            return {
              attributeName: attr.attributeName,
              attributeValue: null // dropdown 불일치 시 null 설정
            };
          }

          // 값이 없거나 빈 문자열인 경우
          if (!attr.attributeValue || attr.attributeValue.trim() === "") {
            return {
              attributeName: attr.attributeName,
              attributeValue: null // 값 없음 시 null 설정
            };
          }

          return attr;
        });
      }

      // 3. 상품 고시 정보 검증
      if (result.notices && Array.isArray(result.notices)) {
        // 기본 고시 정보 설정 (오버라이드용)
        const defaultNotices = {};

        // store에서 defaultSettings 가져오기
        const defaultSettings = store.state.settings[0]?.defaultSettings;

        // defaultSettings가 있으면 기본값 설정
        if (defaultSettings) {
          defaultNotices["제조자(수입자)"] = defaultSettings.manufacturer || '';
          defaultNotices["A/S 책임자와 전화번호"] = defaultSettings.asInfo || '';
          defaultNotices["제조국"] = defaultSettings.importStatus === '수입대상아님' ? '한국' : '중국';
          defaultNotices["품질보증기준"] = "제품 이상 시 공정거래위원회 고시 소비자분쟁해결기준에 의거 보상합니다.";
        }

        result.notices = result.notices.map(notice => {
          // 1. 기본값이 있으면 기본값 우선 적용
          if (defaultNotices[notice.noticeItemName] !== undefined) {
            return {
              noticeItemName: notice.noticeItemName,
              noticeItemValue: defaultNotices[notice.noticeItemName]
            };
          }

          // 2. 드롭다운 검증
          const dropdownValues = this.getDropdownValues(schema, 'notices', notice.noticeItemName);

          if (dropdownValues && dropdownValues.length > 0 &&
            !dropdownValues.includes(notice.noticeItemValue)) {
            console.warn(`고시 정보 "${notice.noticeItemName}"의 값이 dropdown 옵션과 일치하지 않습니다: ${notice.noticeItemValue}`);
            return {
              noticeItemName: notice.noticeItemName,
              noticeItemValue: "상세설명 참조" // dropdown 불일치 시 기본값
            };
          }

          // 3. 값이 없거나 빈 문자열인 경우
          if (!notice.noticeItemValue || notice.noticeItemValue.trim() === "") {
            return {
              noticeItemName: notice.noticeItemName,
              noticeItemValue: "상세설명 참조" // 값 없음 시 기본값
            };
          }

          return notice;
        });

        // 4. 중복 검사 및 고유한 값만 유지
        const uniqueNotices = [];
        const noticeNames = new Set();

        result.notices.forEach(notice => {
          if (!noticeNames.has(notice.noticeItemName)) {
            noticeNames.add(notice.noticeItemName);
            uniqueNotices.push(notice);
          }
        });

        result.notices = uniqueNotices;

        // 5. 스키마에 정의된 모든 notice 항목이 포함되어 있는지 확인
        if (schema && schema.properties?.legalPage?.properties?.notices?.allOf) {
          console.log('📋 스키마 기반 notices 완전성 검증 시작');

          const schemaNoticeNames = schema.properties.legalPage.properties.notices.allOf
            .map(config => config.contains?.properties?.noticeItemName?.enum?.[0])
            .filter(name => name);

          console.log(`스키마에 정의된 notice 항목: ${schemaNoticeNames.length}개`, schemaNoticeNames);
          console.log(`현재 result.notices 항목: ${result.notices.length}개`, result.notices.map(n => n.noticeItemName));

          // 누락된 항목 찾기
          const existingNoticeNames = new Set(result.notices.map(n => n.noticeItemName));
          const missingNotices = schemaNoticeNames.filter(name => !existingNoticeNames.has(name));

          if (missingNotices.length > 0) {
            console.warn(`⚠️ 스키마에 정의되었지만 누락된 notice 항목 발견: ${missingNotices.length}개`, missingNotices);

            // 누락된 항목 추가
            missingNotices.forEach(noticeName => {
              const noticeValue = defaultNotices[noticeName] || "상세설명 참조";
              result.notices.push({
                noticeItemName: noticeName,
                noticeItemValue: noticeValue
              });
              console.log(`✅ 누락된 notice 항목 추가: ${noticeName} = ${noticeValue}`);
            });
          } else {
            console.log('✅ 모든 스키마 notice 항목이 포함되어 있습니다.');
          }
        }
      }

      // 4. 물류 정보 검증 및 정제 (sanitize 함수 사용)
      result.skuUnitBoxDimension = this.sanitizeBoxDimension(result.skuUnitBoxDimension);
      result.skuUnitBoxWeight = this.sanitizeBoxWeight(result.skuUnitBoxWeight);

    } catch (error) {
      console.error('응답 데이터 검증 중 오류:', error);
    }

    return result;
  },

  /**
  * 스키마에서 dropdown 값 가져오기
  * @param {Object} schema - 쿠팡 스키마
  * @param {string} section - 섹션 이름 (exposedAttributes, unexposedAttributes, notices)
  * @param {string} itemName - 속성/항목 이름
  * @returns {Array|null} dropdown 값 배열 또는 null
  */
  getDropdownValues(schema, section, itemName) {
    if (!schema) return null;

    try {
      let sectionSchema = null;
      let valueProperty = 'attributeValue';
      let nameProperty = 'attributeName';

      // 섹션별 스키마 및 속성명 결정
      if (section === 'exposedAttributes') {
        sectionSchema = schema.properties.productPage.properties.commonAttributes.properties.exposedAttributes;
      } else if (section === 'unexposedAttributes') {
        sectionSchema = schema.properties.productPage.properties.unexposedAttributes;
      } else if (section === 'notices') {
        sectionSchema = schema.properties.legalPage.properties.notices;
        valueProperty = 'noticeItemValue';
        nameProperty = 'noticeItemName';
      }

      if (!sectionSchema || !sectionSchema.allOf) return null;

      // 해당 항목의 설정 찾기
      const itemConfig = sectionSchema.allOf.find(config =>
        config.contains.properties[nameProperty] &&
        config.contains.properties[nameProperty].enum &&
        config.contains.properties[nameProperty].enum[0] === itemName
      );

      if (!itemConfig) return null;

      // dropdown 값 반환
      return itemConfig.contains.properties[valueProperty].dropdown || null;

    } catch (error) {
      console.warn(`dropdown 값 조회 중 오류 (${section}, ${itemName}):`, error);
      return null;
    }
  },

  /**
  * notices를 productNotices 형식으로 변환
  * @param {Array} notices - notices 배열 (noticeItemName/noticeItemValue 형식)
  * @returns {Array} productNotices 배열 (name/value 형식)
  */
  convertNoticesToProductNotices(notices) {
    if (!notices || !Array.isArray(notices)) return [];

    return notices.map(notice => ({
      name: notice.noticeItemName,
      value: notice.noticeItemValue || "상세설명 참조"
    }));
  },

  /**
  * 상자 크기 형식 및 최소 크기 검증
  * @param {string} dimension - 상자 크기 문자열 (mm 단위, 정수*정수*정수 형식)
  * @returns {boolean} 유효성 여부
  */
  isValidBoxDimension(dimension) {
    // 문자열이 아니면 실패
    if (typeof dimension !== 'string') {
      return false;
    }

    // 정확히 정수*정수*정수 형식만 허용 (소수점, 단위, 공백 등 불허)
    if (!/^\d+\*\d+\*\d+$/.test(dimension)) {
      console.warn(`⚠️ 포장 사이즈 형식 오류: "${dimension}" (정수*정수*정수 형식만 허용)`);
      return false;
    }

    // 최소 크기 검증 (각 면 10mm 이상, 부피 1000mm³ 이상)
    const [width, height, depth] = dimension.split('*').map(Number);

    // 각 면이 10mm 이상인지 검증
    if (width < 10 || height < 10 || depth < 10) {
      console.warn(`⚠️ 포장 사이즈 최소 크기 미달: "${dimension}" (각 면 10mm 이상 필요)`);
      return false;
    }

    // 부피가 1000mm³ 이상인지 검증
    const volume = width * height * depth;
    if (volume < 1000) {
      console.warn(`⚠️ 포장 사이즈 부피 미달: "${dimension}" (부피 1000mm³ 이상 필요)`);
      return false;
    }

    return true;
  },

  /**
  * 상자 무게 형식 검증
  * @param {string} weight - 상자 무게 문자열 (g 단위, 1~100000 사이 정수)
  * @returns {boolean} 유효성 여부
  */
  isValidBoxWeight(weight) {
    // 문자열이 아니면 실패
    if (typeof weight !== 'string') {
      return false;
    }

    // 정수만 허용 (소수점, 단위, 공백 등 불허)
    if (!/^\d+$/.test(weight)) {
      console.warn(`⚠️ 포장 무게 형식 오류: "${weight}" (정수만 허용, 단위/소수점 불가)`);
      return false;
    }

    // 1~100000 사이인지 검증
    const numWeight = parseInt(weight, 10);
    if (numWeight < 1 || numWeight > 100000) {
      console.warn(`⚠️ 포장 무게 범위 오류: "${weight}" (1~100000 사이만 허용)`);
      return false;
    }

    return true;
  },

  /**
  * 상자 크기 정제 (유효하지 않으면 null)
  * @param {string} dimension - 상자 크기 문자열
  * @returns {string|null} 정제된 상자 크기
  */
  sanitizeBoxDimension(dimension) {
    // 기본값(100*100*100)으로 채우지 않는다 — 사람이 저장한 포장값을 덮고, 틀린 값으로 견적이 통과했다.
    // 못 읽으면 null → applyAttributesToOption 이 저장값을 두고, 저장값도 없으면 하한값(100*100*100mm·100g, 사장님 #260)을 넣는다.
    const DEFAULT_DIMENSION = null;

    if (!dimension || typeof dimension !== 'string') {
      console.log(`📦 포장 사이즈 없음 → 비워 둠`);
      return DEFAULT_DIMENSION;
    }

    // 단위(mm·cm·m)를 mm 로 바꾸고 소수는 반올림한다(packageEstimate.normalizeBoxDimension)
    // 읽히면 포장 하한(사장님 #260)을 적용한다 — 세 변 모두 100mm 이하면 100*100*100, 아니면 10mm 미만 변만 10mm 로
    const cleaned = normalizeBoxDimension(dimension);
    if (cleaned) {
      const floored = floorBoxDimension(cleaned);
      if (this.isValidBoxDimension(floored)) return floored;
    }

    console.log(`📦 포장 사이즈 유효하지 않음: "${dimension}" → 비워 둠`);
    return DEFAULT_DIMENSION;
  },

  /**
  * 상자 무게 정제 (유효하지 않으면 null)
  * @param {string} weight - 상자 무게 문자열
  * @returns {string|null} 정제된 상자 무게
  */
  sanitizeBoxWeight(weight) {
    const DEFAULT_WEIGHT = null; // 위 sanitizeBoxDimension 과 같은 이유

    if (typeof weight === 'number' && Number.isFinite(weight)) weight = String(weight);
    if (!weight || typeof weight !== 'string') {
      console.log(`⚖️ 포장 무게 없음 → 비워 둠`);
      return DEFAULT_WEIGHT;
    }

    // kg 는 g 로 바꾸고 소수는 반올림한다(packageEstimate.normalizeBoxWeight)
    const cleaned = normalizeBoxWeight(weight);
    if (cleaned) {
      return cleaned;
    }

    console.log(`⚖️ 포장 무게 유효하지 않음: "${weight}" → 비워 둠`);
    return DEFAULT_WEIGHT;
  },

  /**
   * 기본 견적서 데이터 생성
   * @param {Object} product - 상품 정보
   * @returns {Object} 기본 견적서 데이터
   */
  createBaseQuotationData(product) {
    // rocketRegInfo에서 데이터 추출
    const regInfo = product.rocketRegInfo || {};

    // 카테고리 경로 처리 (ROOT> 접두사 제거)
    let categoryPath = regInfo.categoryPath || '';
    if (categoryPath.startsWith('ROOT>')) {
      categoryPath = categoryPath.substring(5);
    }

    // 카테고리 ID와 코드 추출
    let categoryId = 0;
    let displayCategoryCode = 0;

    if (regInfo.categoryId) {
      categoryId = regInfo.categoryId;
    }

    if (regInfo.displayCategoryCode) {
      displayCategoryCode = regInfo.displayCategoryCode;
    } else {
      // 카테고리 경로에서 추출 시도
      const categoryPathMatch = categoryPath.match(/\((\d+)\)$/);
      if (categoryPathMatch && categoryPathMatch[1]) {
        displayCategoryCode = parseInt(categoryPathMatch[1]);
        // 카테고리 ID 추정 (실제로는 정확한 매핑이 필요)
        categoryId = displayCategoryCode - 74000;
      }
    }

    // 선택된 옵션 정보 추출
    // const selectedOption = store.state.selectedOption ||
    //   (product.options && product.options.length > 0 ? product.options[0] : null);

    // SEO된 상품명 또는 기본 상품명 사용
    let productName = product.seo?.productName || product.productName || '';

    // 모든 옵션이 같은 묶음 수일 때만 상품명에 추가(섞이면 옵션마다 — 상품 하나 이름에 «최대 N» 을 붙이지 않는다)
    const { nameSuffix } = bundleOptionService.quotationBundleNotes(product);
    if (nameSuffix) {
      productName = `${productName}${nameSuffix}`;
      console.log(`견적서 기본 데이터: 번들 정보 포함 - ${productName}`);
    }

    // 옵션이 있는 경우 옵션명 추가
    // if (selectedOption && selectedOption.korName) {
    //   productName = `${productName}, ${selectedOption.korName}`;
    // }

    // DefaultSettings 정보 가져오기
    const defaultSettings = store.state.settings[0]?.defaultSettings || {};

    // 브랜드명 처리 - 콤마로 구분된 경우 첫 번째 브랜드만 사용(견적서 앞단 점검과 같은 함수)
    const brandName = firstBrandName(product.brand, defaultSettings.brand);

    // 모델번호 길이 제한 (50자 제한)
    let modelNumber = productName;
    if (modelNumber.length > 50) {
      console.log(`[createBaseQuotationData] 모델번호 길이(${modelNumber.length})가 50자를 초과합니다. 50자로 자릅니다.`);
      modelNumber = modelNumber.substring(0, 50);
      console.log(`[createBaseQuotationData] 최종 모델번호 길이: ${modelNumber.length}, 값: ${modelNumber}`);
    }

    // 기본 견적서 데이터 구성
    return {
      // 상품 기본 정보
      productName: productName,
      brand: brandName,
      modelNumber: modelNumber,
      manufacturer: quotationManufacturer({ settingsManufacturer: defaultSettings.manufacturer, brandName }),
      businessType: defaultSettings.transactionType || '기타 도소매업자',
      taxationType: '과세',
      importType: defaultSettings.importStatus || '수입대상아님',

      // 카테고리 정보
      categoryPath: categoryPath,
      categoryId: categoryId,
      displayCategoryCode: displayCategoryCode,

      // 가격 정보
      barcode: '바코드 없음(쿠팡 바코드 생성 요청)',
      purchasePrice: this.getDefaultPurchasePrice(product),
      salePrice: this.getDefaultSalePrice(product),
      msrp: this.getDefaultMSRP(product),

      // 인증 정보 - 스키마에서 추출
      kcMarkType: '해당사항없음',
      certificates: this.getDefaultCertificates(regInfo),

      // 물류 정보 기본값
      totalSkusInBox: defaultSettings.totalSKUsInBox || 24,
      daysToExpiration: defaultSettings.daysToExpiration || 0,

      // 메타 정보
      generatedAt: new Date().toISOString(),
      validUntil: this.getExpiryDate(30) // 30일 기본 유효기간
    };
  },

  /**
   * 공급가 기본값 가져오기
   * @param {Object} product - 상품 정보
   * @returns {number} 공급가
   */
  getDefaultPurchasePrice(product) {
    console.log('getDefaultPurchasePrice 호출됨');

    // 선택된 옵션 또는 첫 번째 옵션 가져오기
    const targetOption = store.state.selectedOption ||
      (product.options && product.options.length > 0 ? product.options[0] : null);

    // 1. 먼저 pricing 정보에서 supplyPrice 확인
    if (product.pricing && product.pricing.options && product.pricing.options.length > 0) {
      const firstPricingOption = product.pricing.options[0];
      if (firstPricingOption.supplyPrice) {
        console.log('product.pricing.options[0].supplyPrice 사용:', firstPricingOption.supplyPrice);
        return firstPricingOption.supplyPrice;
      }
    }

    // 2. product.pricing.supplyPrice 확인
    if (product.pricing && product.pricing.supplyPrice) {
      console.log('product.pricing.supplyPrice 사용:', product.pricing.supplyPrice);
      return product.pricing.supplyPrice;
    }

    // 3. 옵션의 supplyPrice 확인
    if (targetOption) {
      if (targetOption.supplyPrice) {
        console.log('option.supplyPrice 사용:', targetOption.supplyPrice);
        return targetOption.supplyPrice;
      }
      // 4. 옵션의 purchasePrice 확인 (fallback)
      else if (targetOption.purchasePrice) {
        console.log('대체값: option.purchasePrice 사용:', targetOption.purchasePrice);
        return targetOption.purchasePrice;
      }
    }

    // 5. 마지막으로 product.pricing.purchasePrice 확인 (fallback)
    if (product.pricing && product.pricing.purchasePrice) {
      console.log('대체값: product.pricing.purchasePrice 사용:', product.pricing.purchasePrice);
      return product.pricing.purchasePrice;
    }

    console.log('공급가 정보를 찾을 수 없어 0으로 설정');
    return 0;
  },

  /**
   * 판매가 기본값 가져오기
   * @param {Object} product - 상품 정보
   * @returns {number} 판매가
   */
  getDefaultSalePrice(product) {
    // 가격 정보가 있는 경우
    if (product.pricing && product.pricing.salePrice) {
      return product.pricing.salePrice;
    }

    // 옵션에 가격 정보가 있는 경우
    if (product.options && product.options.length > 0) {
      const option = store.state.selectedOption || product.options[0];
      if (option.sellingPrice) {
        return option.sellingPrice;
      } else if (option.supplyPrice) {
        return option.supplyPrice;
      }
    }

    return 0;
  },

  /**
   * 권장소비자가 기본값 가져오기
   * @param {Object} product - 상품 정보
   * @returns {number} 권장소비자가
   */
  getDefaultMSRP(product) {
    // 가격 정보가 있는 경우
    if (product.pricing && product.pricing.msrp) {
      return product.pricing.msrp;
    }

    // 옵션에 가격 정보가 있는 경우
    if (product.options && product.options.length > 0) {
      const option = store.state.selectedOption || product.options[0];
      if (option.msrp) {
        return option.msrp;
      } else if (option.sellingPrice) {
        return option.sellingPrice;
      } else if (option.supplyPrice) {
        return option.supplyPrice;
      }
    }

    // coupangDoc에서 msrp 또는 osrp 확인
    if (product.coupangDoc && product.coupangDoc.jsonDocument && product.coupangDoc.jsonDocument.length > 0) {
      const docItem = product.coupangDoc.jsonDocument[0];
      if (docItem.productPage && docItem.productPage.commonAttributes) {
        if (docItem.productPage.commonAttributes.osrp) {
          return docItem.productPage.commonAttributes.osrp;
        } else if (docItem.productPage.commonAttributes.msrp) {
          return docItem.productPage.commonAttributes.msrp;
        }
      }
    }

    // 판매가로 대체
    return this.getDefaultSalePrice(product);
  },

  /**
   * 기본 인증 정보 가져오기 - 스키마에서 인증정보 추출
   * @param {Object} rocketRegInfo - 로켓 등록 정보 (스키마 포함)
   * @returns {Array} 인증 정보 배열
   */
  getDefaultCertificates(rocketRegInfo) {
    // 기본 인증 정보 (스키마 파싱 실패 시 사용)
    const defaultCerts = [
      { name: '전기용품 및 생활용품, 어린이 (KC) 인증번호', value: '해당사항없음' },
      { name: '방송통신 기자재 (EMC) 인증 번호', value: '해당사항없음' },
      { name: '안전기준적합확인 신고번호', value: '해당사항없음' }
    ];

    // rocketRegInfo 또는 스키마가 없으면 기본값 사용
    if (!rocketRegInfo || !rocketRegInfo.schemaString) {
      console.log('스키마 정보가 없어 기본 인증 정보를 사용합니다.');
      return defaultCerts;
    }

    try {
      // 스키마 파싱
      const schema = JSON.parse(rocketRegInfo.schemaString);

      // legalPage가 필수인지 확인
      if (!schema.required || !schema.required.includes('legalPage')) {
        console.log('legalPage가 필수 항목이 아니어서 기본값을 사용합니다.');
        return defaultCerts;
      }

      // certificates가 필수인지 확인
      if (!schema.properties?.legalPage?.required ||
          !schema.properties.legalPage.required.includes('certificates')) {
        console.log('certificates가 필수 항목이 아니어서 기본값을 사용합니다.');
        return defaultCerts;
      }

      // 인증 정보 필드 경로 확인
      if (!schema.properties?.legalPage?.properties?.certificates?.allOf) {
        console.log('스키마에서 인증 정보 경로를 찾을 수 없어 기본값을 사용합니다.');
        return defaultCerts;
      }

      // 스키마에서 인증 항목 추출
      const certItems = [];

      // allOf 배열을 순회하며 인증 항목 추출
      schema.properties.legalPage.properties.certificates.allOf.forEach(item => {
        // 필수 항목인지 확인 (required 배열에 certificateName과 certificateValue가 있는지)
        if (item.contains?.required &&
            item.contains.required.includes('certificateName') &&
            item.contains.required.includes('certificateValue')) {

          // certificateName 속성 확인
          if (item.contains?.properties?.certificateName?.title) {
            const certName = item.contains.properties.certificateName.title;
            console.log(`스키마에서 필수 인증 항목 추출: ${certName}`);

            certItems.push({
              name: certName,
              value: '해당사항없음'
            });
          }
        }
      });

      console.log(`스키마에서 추출한 인증 항목 수: ${certItems.length}`);

      // 추출된 인증 항목이 있으면 반환, 없으면 기본값 사용
      return certItems.length > 0 ? certItems : defaultCerts;

    } catch (error) {
      console.error('스키마 파싱 중 오류:', error);
      return defaultCerts;
    }
  },



  /**
   * 견적서 유효기간 계산
   * @param {number} days - 유효 기간(일)
   * @returns {string} ISO 형식의 날짜 문자열
   */
  getExpiryDate(days = 30) {
    const today = new Date();
    const expiryDate = new Date();
    expiryDate.setDate(today.getDate() + parseInt(days));
    return expiryDate.toISOString();
  },

  /**
   * 상품의 coupangDoc에서 모델 번호 길이 확인 및 제한
   * @param {Object} product - 상품 객체
   */
  enforceModelNumberLimitsInAllOptions(product) {
    try {
      console.log('[enforceModelNumberLimitsInAllOptions] 시작: 상품 coupangDoc의 모델번호 검사');

      // 상품 레벨의 coupangDoc만 확인 (옵션 레벨 coupangDoc은 더 이상 사용하지 않음)
      if (product.coupangDoc &&
          product.coupangDoc.jsonDocument &&
          product.coupangDoc.jsonDocument.length > 0) {

        // 각 jsonDocument 항목 확인
        product.coupangDoc.jsonDocument.forEach((docItem, index) => {
          if (docItem.productPage && docItem.productPage.modelNumber) {
            const modelNumber = docItem.productPage.modelNumber;
            if (modelNumber.length > 50) {
              console.log(`[enforceModelNumberLimitsInAllOptions] jsonDocument[${index}]의 모델번호 길이(${modelNumber.length})가 50자를 초과합니다. 50자로 자릅니다.`);
              product.coupangDoc.jsonDocument[index].productPage.modelNumber = modelNumber.substring(0, 50);
              console.log(`최종 모델번호 길이: ${product.coupangDoc.jsonDocument[index].productPage.modelNumber.length}`);
            }
          }
        });
      } else {
        console.log('[enforceModelNumberLimitsInAllOptions] 상품에 유효한 coupangDoc이 없습니다.');
      }

      console.log('[enforceModelNumberLimitsInAllOptions] 완료');
    } catch (error) {
      console.error('[enforceModelNumberLimitsInAllOptions] 오류:', error);
      // 오류가 있어도 프로세스 계속 진행
    }
  },

  /**
   * 수동으로 작성된 견적서 데이터 처리
   * @param {Object} product - 상품 정보
   * @param {Object} manualData - 수동 입력 데이터
   * @param {Object} targetOption - 선택된 옵션 (선택적)
   * @param {boolean} applyToAllOptions - 모든 옵션에 적용할지 여부 (기본값: false)
   * @returns {Promise<Object>} 처리된 견적서 데이터
   */
  async processManualQuotation(product, manualData, targetOption, applyToAllOptions = false) {
    try {
      console.log('=========== [디버깅] processManualQuotation 시작 ===========');
      console.log('[1] 입력값 확인:');
      console.log('- product.registrationId:', product?.registrationId);
      console.log('- manualData의 주요 필드:', Object.keys(manualData || {}).join(', '));
      console.log('- manualData.productName:', manualData?.productName);
      console.log('- manualData.modelNumber:', manualData?.modelNumber);

      if (!product || !manualData) {
        throw new Error('유효하지 않은 데이터입니다.');
      }

      // 모든 coupangDoc의 모델 번호 길이 확인 및 제한
      this.enforceModelNumberLimitsInAllOptions(product);

      // manualData의 모델 번호도 확인 및 제한
      if (manualData.modelNumber && manualData.modelNumber.length > 50) {
        console.log(`[processManualQuotation] 모델번호 길이(${manualData.modelNumber.length})가 50자를 초과합니다. 50자로 자릅니다.`);
        manualData.modelNumber = manualData.modelNumber.substring(0, 50);
        console.log(`[processManualQuotation] 최종 모델번호 길이: ${manualData.modelNumber.length}`);
      }

      // 1. 최신 상품 데이터 가져오기 (다른 작업이 업데이트했을 수 있음)
      let latestProduct = null;

      try {
        // 최신 상품 데이터 가져오기
        const allProducts = store.state.rocketProducts;
        const foundProduct = allProducts.find(p => p.registrationId === product.registrationId);

        if (foundProduct) {
          console.log('최신 상품 데이터 발견, 수동 견적서 처리에 사용');
          latestProduct = foundProduct;
        } else {
          latestProduct = product;
        }
      } catch (findError) {
        console.warn('최신 상품 데이터 조회 중 오류:', findError);
        // 조회 실패 시 원본 product 사용
        latestProduct = product;
      }

      // 2. coupangDoc에서 견적서 데이터 추출 시도 (일부 필드는 자동 생성된 데이터 활용)
      let coupangDocData = null;
      if (this.checkCoupangDocExists(latestProduct)) {
        try {
          console.log('[2] 기존 coupangDoc 확인:');

          // product.coupangDoc 확인
          if (latestProduct.coupangDoc && latestProduct.coupangDoc.jsonDocument && latestProduct.coupangDoc.jsonDocument.length > 0) {
            console.log(`- 상품의 coupangDoc 발견 (jsonDocument 항목 수: ${latestProduct.coupangDoc.jsonDocument.length})`);

            // 선택된 옵션 인덱스 확인
            let targetIndex = 0;

            // 선택된 옵션이 있는 경우 해당 인덱스 찾기
            if (targetOption && latestProduct.options && latestProduct.options.length > 0) {
              const optionIndex = latestProduct.options.findIndex(opt => opt.optionNumber === targetOption.optionNumber);
              if (optionIndex !== -1 && optionIndex < latestProduct.coupangDoc.jsonDocument.length) {
                targetIndex = optionIndex;
                console.log(`- 선택된 옵션(${targetOption.optionNumber})에 해당하는 jsonDocument 인덱스: ${targetIndex}`);
              }
            }

            // 해당 인덱스의 coupangDoc 값 로깅
            if (targetIndex < latestProduct.coupangDoc.jsonDocument.length) {
              const doc = latestProduct.coupangDoc.jsonDocument[targetIndex];
              console.log(`- jsonDocument[${targetIndex}].startPage.productName:`, doc.startPage?.productName);
              console.log(`- jsonDocument[${targetIndex}].productPage.modelNumber:`, doc.productPage?.modelNumber);
            }
          } else {
            console.log('- 상품에 유효한 coupangDoc이 없습니다.');
          }

          coupangDocData = this.extractQuotationFromCoupangDoc(latestProduct);
          console.log('[3] coupangDoc에서 견적서 기본 데이터 추출 성공');
          console.log('- 추출된 productName:', coupangDocData?.productName);
          console.log('- 추출된 modelNumber:', coupangDocData?.modelNumber);
        } catch (extractError) {
          console.warn('[!] coupangDoc에서 견적서 데이터 추출 실패:', extractError);
        }
      }

      // 바코드 처리
      let barcode = '바코드 없음(쿠팡 바코드 생성 요청)';
      if (manualData.barcodeType === 'custom' && manualData.barcode) {
        barcode = manualData.barcode;
      }

      // 인증 정보 처리
      const certificates = manualData.certificates.map(cert => {
        return {
          name: cert.name,
          value: cert.value === 'custom' ? cert.customValue : cert.value
        };
      });

      // 기본 견적서 정보 생성
      const baseData = this.createBaseQuotationData(latestProduct);

      console.log('[4] 데이터 우선순위 병합 준비:');
      console.log('- manualData.productName:', manualData.productName);
      console.log('- manualData keys:', Object.keys(manualData));
      console.log('- coupangDocData?.productName:', coupangDocData?.productName);
      console.log('- baseData.productName:', baseData.productName);

      // 수동 데이터 병합 (우선순위: 수동 데이터 > coupangDoc > 기본 데이터)
      const quotationData = {
        ...baseData,  // 기본 데이터 (최하위 우선순위)
        ...(coupangDocData || {}),  // coupangDoc 데이터 (중간 우선순위)
        ...manualData,  // 수동 입력 데이터 (최우선) - 모든 필드 우선 적용

        // 특수 처리가 필요한 필드들
        barcode: barcode, // 커스텀 바코드 처리
        certificates: certificates.length > 0 ? certificates : (coupangDocData?.certificates || []),

        // 메타 정보는 항상 최신으로
        generatedAt: new Date().toISOString(),
        validUntil: this.getExpiryDate(30)
      };

      // searchTags가 있는지 명시적으로 로깅 (디버깅용)
      console.log(`[processManualQuotation] searchTags 확인: "${manualData.searchTags || '없음'}"`);

      // manualData로부터의 값 유무를 로깅 (디버깅용)
      console.log('[processManualQuotation] 처리된 데이터 필드:', Object.keys(quotationData).join(', '));

      // 상품 업데이트
      const updatedProduct = JSON.parse(JSON.stringify(latestProduct));
      updatedProduct.quotation = quotationData;
      updatedProduct.quotationStatus = 'completed';

      // coupangDoc 업데이트
      console.log(`[processManualQuotation] 모든 옵션에 적용: ${applyToAllOptions ? '예' : '아니오'}`);

      if (applyToAllOptions) {
        // 모든 옵션에 적용하는 모드
        if (updatedProduct.options && updatedProduct.options.length > 0) {
          console.log(`[processManualQuotation] 모든 옵션 모드: ${updatedProduct.options.length}개 옵션에 일괄 적용`);

          // 모든 옵션의 quotationStatus 업데이트
          updatedProduct.options.forEach(opt => {
            // quotationStatus 업데이트
            opt.quotationStatus = 'completed';
          });

          console.log(`[processManualQuotation] 총 ${updatedProduct.options.length}개 옵션의 상태 업데이트 완료`);
        }
      } else {
        // 선택된 옵션에만 적용하는 모드
        // 1. 먼저 서비스 호출 시 전달된 targetOption 확인
        let targetOpt = null;

        // targetOption이 직접 전달된 경우 (타입 체크 필요)
        if (targetOption && typeof targetOption === 'object' && targetOption.optionNumber) {
          console.log(`[processManualQuotation] 전달된 targetOption 사용: ${targetOption.optionNumber}`);
          targetOpt = targetOption;
        }
        // store.state.selectedOption 확인
        else if (store.state.selectedOption) {
          console.log(`[processManualQuotation] store.state.selectedOption 사용: ${store.state.selectedOption.optionNumber}`);
          targetOpt = store.state.selectedOption;
        }
        // 첫 번째 옵션 사용
        else if (updatedProduct.options && updatedProduct.options.length > 0) {
          console.log(`[processManualQuotation] 첫 번째 옵션 사용: ${updatedProduct.options[0].optionNumber}`);
          targetOpt = updatedProduct.options[0];
        }

        // 2. 선택된 옵션 찾기 및 상태 업데이트
        let selectedOptionFound = false;

        if (updatedProduct.options && updatedProduct.options.length > 0 && targetOpt) {
          console.log(`[processManualQuotation] 총 ${updatedProduct.options.length}개 옵션 중 선택된 옵션 검색`);

          // 선택된 옵션의 optionNumber와 일치하는 옵션 찾기
          for (const opt of updatedProduct.options) {
            // 옵션 번호가 일치하면 상태 업데이트
            if (opt.optionNumber === targetOpt.optionNumber) {
              console.log(`[processManualQuotation] 선택된 옵션 ${opt.optionNumber} 상태 업데이트 중`);
              selectedOptionFound = true;
            }

            // 모든 옵션의 quotationStatus 업데이트
            opt.quotationStatus = 'completed';
          }
        }

        // 선택된 옵션이 없으면 경고
        if (!selectedOptionFound) {
          console.warn(`[processManualQuotation] 선택된 옵션을 찾지 못했습니다.`);
        }
      }

      // 서비스에서 직접 부분 업데이트 (coupangDoc 업데이트 포함)
      await this.updateProductWithCoupangDocPartial(updatedProduct, quotationData, applyToAllOptions);
      console.log('견적서 정보로 상품 업데이트 완료');

      return quotationData;
    } catch (error) {
      console.error('수동 견적서 처리 중 오류:', error);
      throw error;
    }
  },

  /**
   * 반복적 접근법으로 추가속성 감지 (최대 5단계)
   * @param {Object} product - 상품 객체
   * @param {Array} duplicatedExposedAttributes - 중복된 노출속성
   * @param {Array} optionAnalysisData - 옵션 분석 데이터
   * @returns {Promise<Object>} 추가속성 감지 결과
   */
  async detectAdditionalAttributesIterative(product, duplicatedExposedAttributes, optionAnalysisData) {
    console.log('🔄 반복적 접근법 시작 (최대 5단계)');

    const maxLevels = 5;
    let currentLevel = 1;
    let currentAttributes = [];
    let remainingDuplicates = [...optionAnalysisData];

    try {
      while (currentLevel <= maxLevels && remainingDuplicates.length > 0) {
        console.log(`\n🔍 ${currentLevel}단계 추가속성 감지 시작`);
        console.log(`처리 대상 옵션 수: ${remainingDuplicates.length}개`);

        // 현재 단계의 추가속성 추출
        const levelResult = await this.extractAttributesForLevel(
          product, duplicatedExposedAttributes, remainingDuplicates, currentLevel, currentAttributes
        );

        if (!levelResult.success) {
          console.warn(`❌ ${currentLevel}단계 추가속성 추출 실패:`, levelResult.error);
          break;
        }

        // 결과를 현재 속성에 병합
        this.mergeAttributesToCurrent(currentAttributes, levelResult.attributes);

        // 노출속성 중복 체크
        const duplicateCheck = this.checkDuplicatesWithCurrentAttributes(
          remainingDuplicates, currentAttributes
        );

        console.log(`✅ ${currentLevel}단계 완료: ${levelResult.attributes.length}개 옵션 처리`);
        console.log(`중복 상태: ${duplicateCheck.duplicateGroups.length}개 그룹, ${duplicateCheck.totalDuplicates}개 중복 옵션`);

        if (duplicateCheck.duplicateGroups.length === 0) {
          console.log('🎉 모든 노출속성 중복이 해결되었습니다!');
          break;
        }

        // 다음 단계를 위해 여전히 중복인 옵션들만 추출
        remainingDuplicates = this.extractRemainingDuplicates(duplicateCheck.duplicateGroups, remainingDuplicates);
        currentLevel++;
      }

      if (currentLevel > maxLevels) {
        console.warn(`⚠️ 최대 단계(${maxLevels})에 도달했지만 일부 중복이 남아있습니다.`);
      }

      // 최종 결과 포맷팅
      const finalResult = this.formatIterativeResult(currentAttributes);

      console.log('🎯 반복적 접근법 완료');
      console.log(`총 ${currentLevel - 1}단계 진행, ${currentAttributes.length}개 옵션 처리`);

      return {
        batchResults: [{
          result: finalResult,
          processedText: JSON.stringify(finalResult)
        }]
      };

    } catch (error) {
      console.error('❌ 반복적 접근법 중 오류:', error);
      return {
        error: error.message,
        batchResults: []
      };
    }
  },

  /**
   * 특정 단계의 추가속성 추출
   * @param {Object} product - 상품 객체
   * @param {Array} duplicatedExposedAttributes - 중복된 노출속성
   * @param {Array} optionData - 옵션 데이터
   * @param {number} level - 현재 단계
   * @param {Array} existingAttributes - 기존 속성들
   * @returns {Promise<Object>} 추출 결과
   */
  async extractAttributesForLevel(product, duplicatedExposedAttributes, optionData, level, existingAttributes) {
    const levelNames = ['', '1차', '2차', '3차', '4차', '5차'];
    const levelCategories = ['', '타입', '등급', '특성', '세부특성', '기타'];

    const prompt = `
# 작업: ${levelNames[level]} 계층 추가속성 추출

## 현재 상황
- 중복된 노출속성: ${JSON.stringify(duplicatedExposedAttributes)}
- 처리 단계: ${level}/${5}
- 이전 단계에서 추출된 속성들이 있지만 여전히 노출속성 중복이 존재

## 분석 대상 옵션들
${JSON.stringify(optionData.slice(0, 20), null, 2)}${optionData.length > 20 ? '\n... (총 ' + optionData.length + '개 옵션)' : ''}

## 기존 추출된 속성들
${JSON.stringify(existingAttributes.slice(0, 10), null, 2)}${existingAttributes.length > 10 ? '\n... (총 ' + existingAttributes.length + '개)' : ''}

## ${levelNames[level]} 계층 요구사항
1. **단계별 집중**: ${levelNames[level]} 계층(${levelCategories[level]})에 해당하는 속성만 추출
2. **중국어 번역**: 중국어 포함 시 한국어로 번역
3. **중복 해결**: 노출속성 + 기존 속성 + 새 속성으로 모든 옵션이 구분되도록
4. **일관성**: 동일한 패턴과 용어 사용

## 응답 형식
{
  "additionalAttributes": [
    {
      "optionNumber": "250622005001",
      "attributes": [
        {
          "level": ${level},
          "value": "추출된 속성값",
          "original": "원본 텍스트",
          "category": "${levelCategories[level]}"
        }
      ]
    }
  ]
}

⚠️ 중요: ${levelNames[level]} 계층에 해당하는 속성만 추출하고, 이전 단계 속성은 포함하지 마세요.
`;

    try {
      const result = await workflowService.requestExtensionWithPromise(
        {
          workType: 'DETECT_ADDITIONAL_ATTRIBUTES',
          productId: product.registrationId,
          requestArr: [{ prompt }]
        },
        {
          timeout: 90000,
          maxRetries: 2
        }
      );

      if (result && result.batchResults && result.batchResults.length > 0) {
        const batchResult = result.batchResults[0];
        let parsedResult = null;

        if (batchResult.result) {
          parsedResult = batchResult.result;
        } else if (batchResult.processedText) {
          let jsonText = batchResult.processedText;
          jsonText = jsonText.replace(/```json\n|\n```/g, '');
          jsonText = jsonText.replace(/```\n|\n```/g, '');
          jsonText = jsonText.trim();
          parsedResult = JSON.parse(jsonText);
        }

        if (parsedResult && parsedResult.additionalAttributes) {
          return {
            success: true,
            attributes: parsedResult.additionalAttributes
          };
        }
      }

      return { success: false, error: '결과 파싱 실패' };

    } catch (error) {
      console.error(`${levelNames[level]} 계층 추출 중 오류:`, error);
      return { success: false, error: error.message };
    }
  },

  /**
   * 현재 속성에 새로운 레벨 속성 병합
   * @param {Array} currentAttributes - 현재 속성 배열
   * @param {Array} newAttributes - 새로운 속성 배열
   */
  mergeAttributesToCurrent(currentAttributes, newAttributes) {
    newAttributes.forEach(newAttr => {
      const existingIndex = currentAttributes.findIndex(
        attr => attr.optionNumber === newAttr.optionNumber
      );

      if (existingIndex >= 0) {
        // 기존 옵션에 새 레벨 속성 추가
        currentAttributes[existingIndex].attributes.push(...newAttr.attributes);
      } else {
        // 새 옵션 추가
        currentAttributes.push(newAttr);
      }
    });
  },

  /**
   * 현재 속성으로 중복 체크
   * @param {Array} optionData - 옵션 데이터
   * @param {Array} currentAttributes - 현재 속성
   * @returns {Object} 중복 체크 결과
   */
  checkDuplicatesWithCurrentAttributes(optionData, currentAttributes) {
    const combinedKeys = new Map();

    optionData.forEach(option => {
      // 노출속성 키 생성
      const exposedKey = JSON.stringify(option.exposedAttributes);

      // 추가속성 키 생성
      const additionalAttr = currentAttributes.find(attr => attr.optionNumber === option.optionNumber);
      const additionalKey = additionalAttr ?
        JSON.stringify(additionalAttr.attributes.sort((a, b) => a.level - b.level)) : '[]';

      // 결합 키
      const combinedKey = `${exposedKey}|${additionalKey}`;

      if (!combinedKeys.has(combinedKey)) {
        combinedKeys.set(combinedKey, []);
      }
      combinedKeys.get(combinedKey).push(option);
    });

    // 중복 그룹 찾기 (2개 이상인 그룹)
    const duplicateGroups = Array.from(combinedKeys.values()).filter(group => group.length > 1);
    const totalDuplicates = duplicateGroups.reduce((sum, group) => sum + group.length, 0);

    return {
      duplicateGroups,
      totalDuplicates,
      totalGroups: combinedKeys.size
    };
  },

  /**
   * 여전히 중복인 옵션들 추출
   * @param {Array} duplicateGroups - 중복 그룹들
   * @param {Array} originalData - 원본 데이터
   * @returns {Array} 중복 옵션들
   */
  extractRemainingDuplicates(duplicateGroups, originalData) {
    const duplicateOptionNumbers = new Set();

    duplicateGroups.forEach(group => {
      group.forEach(option => {
        duplicateOptionNumbers.add(option.optionNumber);
      });
    });

    return originalData.filter(option =>
      duplicateOptionNumbers.has(option.optionNumber)
    );
  },

  /**
   * 반복적 결과를 최종 형식으로 포맷팅
   * @param {Array} currentAttributes - 현재 속성들
   * @returns {Object} 포맷팅된 결과
   */
  formatIterativeResult(currentAttributes) {
    return {
      additionalAttributes: currentAttributes.map(attr => ({
        optionNumber: attr.optionNumber,
        attributes: attr.attributes.sort((a, b) => a.level - b.level) // 레벨 순으로 정렬
      }))
    };
  },

  /**
   * 중복 그룹을 프롬프트용으로 효율적으로 포맷팅
   * @param {Array} optionAnalysisData - 옵션 분석 데이터
   * @returns {string} 포맷팅된 문자열
   */
  formatDuplicateGroupsForPrompt(optionAnalysisData) {
    // 노출속성별로 그룹화
    const groups = new Map();

    optionAnalysisData.forEach(option => {
      const exposedKey = JSON.stringify(option.exposedAttributes);
      if (!groups.has(exposedKey)) {
        groups.set(exposedKey, {
          exposedAttributes: option.exposedAttributes,
          options: []
        });
      }
      groups.get(exposedKey).options.push({
        optionNumber: option.optionNumber,
        korName: option.korName
      });
    });

    // 그룹별로 포맷팅
    const formattedGroups = Array.from(groups.values()).map(group => {
      return `Group: ${JSON.stringify(group.exposedAttributes)}
Options: ${JSON.stringify(group.options, null, 2)}`;
    });

    return formattedGroups.join('\n\n');
  }
};

export default quotationService;
